import { useEffect, useRef, useState, useMemo, lazy, Suspense } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import usePageTitle from '../hooks/usePageTitle'
import { format, addMonths } from 'date-fns'
import { getMatter, createTimeEntry, createEvent,
  updateDiscoverySet, deleteDiscoverySet } from '../services/api'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'
import { pushRecentMatter } from '../lib/recentMatters'
import { displayClientForMatterRow } from '../lib/displayClient'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { parseDateOnly, roundBillable, adjustEndOnStartChange } from '../utils/format'
import { localInputToUtc, utcToLocalInput, toFirmWallClock } from '../utils/eventTime'
import { solDaysLeft, formatDateOnly } from '../utils/solDays'
import { eventsNeedAuthorNames, mergeEventAuthors } from '../utils/eventHistory'
import EventHistory from '../components/EventHistory'
import RecurrenceFields, { applyRecurrenceToPayload, emptyRecurrence } from '../components/RecurrenceFields'
import ReminderEditor from '../components/ReminderEditor'
import { reminderRowsToPayload } from '../utils/reminders'
import PopOutPageButton from '../components/PopOutPageButton'
import CopyLinkButton from '../components/CopyLinkButton'
import MatterRatesCard from '../components/MatterRatesCard'
import MatterPhasesCard from '../components/MatterPhasesCard'
import MatterWitnessesCard from '../components/MatterWitnessesCard'
import MatterQuestionnaireCard from '../components/MatterQuestionnaireCard'
// Ship 7i — Master Witness List (LANDSCAPE, internal work-product) +
// §2034.260 Expert Designation (PORTRAIT, court filing).  logExport fires
// AFTER doc.save() resolves in both handlers (per CLAUDE.md rule).
import { listMatterWitnesses, logExport } from '../services/api'
import { isMatterPinned, toggleMatterPin, PINNED_MATTERS_EVENT } from '../lib/pinnedMatters'
// The printing user's name for the internal print-outs' "Printed by" footer.
import { pdfPrintedByName } from '../lib/pdf'
import usePdfPageSetup from '../hooks/usePdfPageSetup'
import useDraft from '../hooks/useDraft'
import DraftRestoreBanner from '../components/DraftRestoreBanner'
import { ACTIVITY_CODES, getActivityLabel } from '../lib/activityCodes'
// Feedback #837 — one definition of "billed", "charged" and the billing chip
// colours, shared with the Reports page's matter reports.
import {
  billingKey, billingInfo, billingStatusColor, chargedCents, isChargedBilledEntry,
  billedTooltip, hoursTenths, fmtMoney, fmtHours,
  isAmicusHistoryNotInvoiceable, isInvoiceablePostedEntry,
} from '../lib/matterTimeReports'
// The entry's start time ('HH:MM:SS' from this page's te.* rows) as '10:15 AM'.
import { formatStartTime } from '../lib/timeEntryPosting'
import { submitWithContingencyGuard } from '../lib/contingencyFeeOverride'
import { groupLabel, groupColor } from '../lib/contactGroups'
import { renderEventTypeOptions, eventTypeLabel, eventTypeColor } from '../lib/eventTypes'
import { SkeletonCard, SkeletonTable, LoadingRegion } from '../components/Skeleton'
import EmptyState from '../components/EmptyState'
import TypingIndicator, { notifyTyping } from '../components/TypingIndicator'
import useFieldIds from '../hooks/useFieldIds'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'
import { reopenConfirmOptions } from '../lib/matterReopen'

// Lazy: components/Discovery.js is ~125KB and imports jsPDF at module scope,
// so a static import here pinned jsPDF into the main bundle for every user on
// every page load.  All four are conditionally rendered already.  Each wrapper
// carries its own Suspense so call sites are unchanged; defined at MODULE
// scope so the component type is stable across renders.
const __DiscoverySectionLazy = lazy(() => import('../components/Discovery').then((m) => ({ default: m.DiscoverySection })))
const __DiscoverySetFormLazy = lazy(() => import('../components/Discovery').then((m) => ({ default: m.DiscoverySetForm })))
const __DiscoveryExtensionModalLazy = lazy(() => import('../components/Discovery').then((m) => ({ default: m.DiscoveryExtensionModal })))
const __DiscoveryDrillPopupLazy = lazy(() => import('../components/Discovery').then((m) => ({ default: m.DiscoveryDrillPopup })))
const DiscoverySection = (props) => (
  <Suspense fallback={<div className="p-6"><SkeletonCard /></div>}><__DiscoverySectionLazy {...props} /></Suspense>
)
// Modals fall back to null on purpose: a spinner that flashes for one frame
// behind a modal backdrop reads as a glitch, and these only mount on click.
const DiscoverySetForm = (props) => (
  <Suspense fallback={null}><__DiscoverySetFormLazy {...props} /></Suspense>
)
const DiscoveryExtensionModal = (props) => (
  <Suspense fallback={null}><__DiscoveryExtensionModalLazy {...props} /></Suspense>
)
const DiscoveryDrillPopup = (props) => (
  <Suspense fallback={null}><__DiscoveryDrillPopupLazy {...props} /></Suspense>
)

// Lazy: ~171KB of source + jsPDF, and it only renders when the Case
// Analysis accordion is open.  A static import here pinned all of it into
// the main bundle for every user on every page load.
const CaseAnalysis = lazy(() => import('../components/CaseAnalysis'))


// PRACTICE_AREAS moved to firm-configurable /api/practice-areas + useFirmPracticeAreas
// hook (feedback #510, Wave 4 2026-08-07).  Left this comment as breadcrumb so
// future readers know where to look.

const EXPENSE_CODES = [
  'E101 Copying', 'E102 Outside printing', 'E103 Word processing',
  'E104 Facsimile', 'E105 Telephone', 'E106 Online research',
  'E107 Delivery services', 'E108 Postage', 'E109 Local travel',
  'E110 Out-of-town travel', 'E111 Meals', 'E112 Court fees',
  'E113 Subpoena fees', 'E114 Witness fees', 'E115 Deposition transcripts',
  'E116 Trial transcripts', 'E117 Trial exhibits', 'E118 Litigation support',
  'E119 Experts', 'E120 Private investigator', 'E121 Arbitrators/mediation',
  'E122 Local counsel', 'E123 Other professionals', 'E124 Other',
  'E125 Translation', 'E150 Doctor/Medical Records', 'E151 Engineers',
  'E152 Accountants', 'E153 Police Reports', 'Filing Fees',
  'Court Filing Services', 'Service Of Process', 'Hotel', 'Travel',
  'Meals', 'Postage', 'Courier Charges', 'Online Research',
  'Printing', 'Mediator/Arbitration', 'Court Reporter',
  'Certified Transcript', 'Hearing Transcript', 'Parking Fee',
  'Government Services', 'Professional Services', 'Video Taped Deposition',
  'Trial Transcripts'
]

// Activity codes moved to a shared module — see frontend/src/lib/activityCodes.js.

export const UNPOST_NOTHING_MSG = "That entry wasn't unposted — it may be billed, already unposted, or not yours to change."

// The bulk-post toast: PUT /post-many reports `posted` (straight to posted)
// and `sent_to_review` (in the reviewer's queue) separately.  Twin of
// bulkPostToast in pages/TimeEntries.js (lazy-loaded, so not imported here);
// keep the wording identical.
export function bulkPostToast (data) {
  const posted = Number(data?.posted) || 0
  const review = Number(data?.sent_to_review) || 0
  const entries = (n) => `${n} entr${n === 1 ? 'y' : 'ies'}`
  if (posted === 0 && review === 0) return { ok: false, message: 'No entries were posted.' }
  if (review === 0) return { ok: true, message: `Posted ${entries(posted)}.` }
  if (posted === 0) return { ok: true, message: `Submitted ${entries(review)} for review — waiting on your reviewer.` }
  return { ok: true, message: `Posted ${entries(posted)}; ${review} submitted for review — waiting on your reviewer.` }
}

// Classes of the sticky quick-jump chip bar (⏱ Time, 📅 Events, …).
// In the staff shell it is full-bleed: it cancels <main>'s `p-4 md:p-8`
// with the matching negative margin and puts the padding back inside
// (Layout.js; Layout.responsive.test.js pins that padding — change both
// together), and once stuck it sits at top-14, clear of the drawer's fixed
// ☰ button.  This page also renders in a pop-out (/popup/page/matters/:id,
// PopupLayout), which has no padding and no ☰: there the negative margin
// pushed the bar 16-32px past both window edges, so the pop-out scrolled
// sideways at every size, and top-14 left a 56px gap above the stuck bar.
// The pop-out gets no margin, no padding and top-0.
export function matterChipBarClass (pathname) {
  const base = 'sticky z-10 mb-3 bg-gray-50/95 backdrop-blur border-y border-gray-200 py-2 overflow-x-auto'
  return String(pathname || '').startsWith('/popup/')
    ? `${base} top-0`
    : `${base} top-14 -mx-4 md:-mx-8 px-4 md:px-8`
}

export default function MatterDetail() {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const toast = useToast()
  const ask = useAsk()
  const { isEnabled: isFeatureOn } = useFeatureFlags()
  // WCAG 2.1 AA — scoped ids for label htmlFor association across the many
  // forms rendered on this page (add time entry, add event, add cost, edit
  // matter, comm log, SOL entry, link contact, add document, etc.)
  const fid = useFieldIds()
  const canViewAll = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_view_all_time === true
  const canViewCosts = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_view_costs === true
  const canCreateCosts = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_create_costs === true
  const canDeleteCosts = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_delete_costs === true
  const canViewTrust = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.trust === true
  const canViewInvoices = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.invoices === true
  const [showAllTime, setShowAllTime] = useState(false)
  const [showCostForm, setShowCostForm] = useState(false)

  // Date filter for time entries (feedback #71 — Darian).  Persists per
  // user in localStorage so a chosen filter sticks across visits and
  // across matters.  Default = 'all' (preserves the original behavior for
  // anyone who doesn't engage with the filter).
  const [timeDateFilter, setTimeDateFilter] = useState(() => {
    try { return localStorage.getItem('lextrack_matter_time_filter') || 'all' } catch { return 'all' }
  })
  const [timeDateCustom, setTimeDateCustom] = useState(() => {
    try {
      const raw = localStorage.getItem('lextrack_matter_time_filter_custom')
      return raw ? JSON.parse(raw) : { from: '', to: '' }
    } catch { return { from: '', to: '' } }
  })
  useEffect(() => {
    try { localStorage.setItem('lextrack_matter_time_filter', timeDateFilter) } catch {}
  }, [timeDateFilter])
  useEffect(() => {
    try { localStorage.setItem('lextrack_matter_time_filter_custom', JSON.stringify(timeDateCustom)) } catch {}
  }, [timeDateCustom])
  const canSeeBilling = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.billing_tab === true
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'billing' && canSeeBilling ? 'billing' : 'attorney'
  )
  const [showGenerateInvoice, setShowGenerateInvoice] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [editingNote, setEditingNote] = useState(null)
  const [editNoteContent, setEditNoteContent] = useState('')
  const [selectedTimeIds, setSelectedTimeIds] = useState([])
  const [selectedCostIds, setSelectedCostIds] = useState([])
  const [invoiceNotes, setInvoiceNotes] = useState('')
  const [isRetainerReplenishment, setIsRetainerReplenishment] = useState(false)
  const [generatingInvoice, setGeneratingInvoice] = useState(false)
  const [editingCost, setEditingCost] = useState(null)
  const [costForm, setCostForm] = useState({
    date: '',
    quantity: '1',
    unit_rate: '',
    expense_code: '',
    description: '',
    vendor: '',
    reference_number: '',
    hard_cost: true,
    billing_status: 'billable'
  })
  const navigate = useNavigate()
  const [matter, setMatter] = useState(null)
  const [loading, setLoading] = useState(true)
  // Ship 7b F2.3.6 — firm_settings row cached for the 3 drilldown PDF
  // generators (Events / Time / Contacts).  Fetched once on mount; if the
  // /api/settings call is slow or fails, pdfHeader falls back to a generic
  // 'Law Firm' letterhead so print still works.  Kept separate from matter
  // state so the print isn't blocked on data the PDF header degrades on.
  const [firmSettings, setFirmSettings] = useState({})
  // The person's report page setup (Margins / Table size, US Letter; James
  // 2026-10-29) for the Events / Time / Contacts / Witness List print-outs: their own
  // choice from any report, else the firm default.  No controls here.
  const { setup: pdfPageSetup } = usePdfPageSetup(firmSettings)
  const matterPdfPageSetup = () => ({ margins: pdfPageSetup.margins, tableSize: pdfPageSetup.tableSize })
  // Print-in-flight guard — prevents double-click producing two PDFs.
  const [printingPDF, setPrintingPDF] = useState(null) // 'events' | 'time' | 'contacts' | null
  // Dynamic browser-tab title: shows the matter's own title once loaded so
  // Alt+Tab / taskbar / cross-window switching is scannable.  Falls back
  // to plain "Matter" while data is loading so we don't overwrite the
  // Layout's route-fallback title with an empty string.
  usePageTitle(matter?.title ? `Matter — ${matter.title}` : 'Matter')
  const [pinned, setPinned] = useState(false)
  const [showTimeForm, setShowTimeForm] = useState(false)
  const [showEventForm, setShowEventForm] = useState(false)
  const [selectedEntries, setSelectedEntries] = useState([])
  const [error, setError] = useState('')
  const [editingEntry, setEditingEntry] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [communications, setCommunications] = useState([])
  const [showCommForm, setShowCommForm] = useState(false)
  const [editingComm, setEditingComm] = useState(null)
  const [commForm, setCommForm] = useState({
    comm_type: 'phone_call', direction: 'outgoing', subject: '',
    description: '', duration_minutes: '', comm_date: utcToLocalInput(new Date()),
    needs_response: false,
  })

  const [solEntries, setSOLEntries] = useState([])
  const [showSOLForm, setShowSOLForm] = useState(false)
  const [editingSOL, setEditingSOL] = useState(null)
  // Discovery (Phase 1 — set-level tracking)
  const [discoverySets, setDiscoverySets] = useState([])
  const [showDiscoveryForm, setShowDiscoveryForm] = useState(false)
  const [discoveryDrillId, setDiscoveryDrillId] = useState(null)
  const [discoveryExtensionFor, setDiscoveryExtensionFor] = useState(null)
  const [solForm, setSOLForm] = useState({
    cause_of_action: '', incident_date: '', sol_period_months: '',
    sol_date: '', tolling_notes: '', is_tolled: false, tolled_date: '', tolling_reason: '', status: 'active'
  })

  // ── T2-B.next.B draft persistence for the two long-form matter forms ──
  // Both keys carry the matter id.  Without it a communication description or
  // a tolling narrative typed on one client's matter would resurface on
  // another's — a confidentiality problem, not a UX one.  `id` comes from the
  // route so it is always present, but the guard keeps a bad route from
  // collapsing every matter onto `matter-undefined-*`.
  //
  // CREATE ONLY, deliberately.  Both forms are dual-purpose: clicking Edit on
  // an existing row populates them from that record.  Persisting in edit mode
  // would copy an existing record's content into the generic new-record slot
  // and then offer it back the next time someone logged a *different* call.
  // An edit also still has the server row as its source of truth; an unsaved
  // new entry has nothing.
  const commDraftKey = (showCommForm && !editingComm && id) ? `matter-${id}-comm` : null
  const { draft: commDraft, restore: restoreCommDraft, discard: discardCommDraft } =
    useDraft(commDraftKey, commForm, {
      // Subject/description are what a person actually types; the rest of the
      // form is defaulted, so ignoring them keeps a merely-opened form from
      // writing a draft and showing a banner nobody earned.
      isDirty: (s) => !!(String(s?.subject || '').trim() || String(s?.description || '').trim()),
      debounceMs: 800,
    })

  const solDraftKey = (showSOLForm && !editingSOL && id) ? `matter-${id}-sol` : null
  const { draft: solDraft, restore: restoreSolDraft, discard: discardSolDraft } =
    useDraft(solDraftKey, solForm, {
      isDirty: (s) => !!(String(s?.cause_of_action || '').trim() || String(s?.tolling_notes || '').trim() || String(s?.tolling_reason || '').trim()),
      debounceMs: 800,
    })

  const [matterContacts, setMatterContacts] = useState([])
  const [allContacts, setAllContacts] = useState([])
  const [showContactLink, setShowContactLink] = useState(false)
  const [selectedContactId, setSelectedContactId] = useState('')
  const [contactRole, setContactRole] = useState('')

  const [matterDocuments, setMatterDocuments] = useState([])
  // Ship 7g portal-shared-audit — admin/edit_matters can toggle a panel that
  // lists every currently-shared doc + who shared it + client engagement
  // counters (opens, downloads, last-opened).  Fetched on toggle-open.
  const [showPortalAudit, setShowPortalAudit] = useState(false)
  const [portalAuditRows, setPortalAuditRows] = useState(null)
  const [portalAuditLoading, setPortalAuditLoading] = useState(false)
  const [portalAuditError, setPortalAuditError] = useState('')
  const [showBatesConfig, setShowBatesConfig] = useState(false)
  const [batesConfig, setBatesConfig] = useState({ bates_prefix: '', bates_next_number: 1, bates_padding: 6 })
  const [batesConfigBusy, setBatesConfigBusy] = useState(false)
  const [showDocForm, setShowDocForm] = useState(false)
  const [editingDocId, setEditingDocId] = useState(null)
  const [docForm, setDocForm] = useState({ name: '', path: '', link_type: 'network', description: '' })
  const [showStandardLinks, setShowStandardLinks] = useState(false)
  const [selectedStandardLinks, setSelectedStandardLinks] = useState({})

  // Generate-document-links panel state
  //   - matterFolderPath: editable full path to the matter's folder on disk.
  //     Defaults to {cases_base_path}\{matter.title} but the user can override
  //     when the on-disk folder name differs from the matter title (very
  //     common — firm folders are often abbreviated).
  //   - scannedSubfolders: array of {name, fullPath} returned by Electron's
  //     scan-folder IPC.  null = haven't scanned, show the standard 7
  //     fallback instead.
  //   - selectedScanned: name → boolean checkbox state
  //   - scanError / scanning: UI feedback
  const [matterFolderPath, setMatterFolderPath] = useState('')
  const [scannedSubfolders, setScannedSubfolders] = useState(null)
  const [selectedScanned, setSelectedScanned] = useState({})
  const [scanError, setScanError] = useState('')
  const [scanning, setScanning] = useState(false)
  const [casesBasePath, setCasesBasePath] = useState('\\\\192.168.1.5\\Paperportdocs.nxcloud\\_Cases')
  const [copyToast, setCopyToast] = useState('')

  const isUserAdmin = user?.role === 'admin' || user?.permissions?.is_admin
  const canCreateInvoices = isUserAdmin || user?.permissions?.can_create_invoices === true
  // G6 keyboard shortcuts — t = new time entry, e = add event, i = generate invoice.
  // Gated on whether the corresponding button is even reachable on this matter.
  usePageShortcut('t', () => window.dispatchEvent(new CustomEvent('open-time-entry-popup', { detail: { matter_id: id, matter_title: matter?.title } })), { enabled: !!matter })
  usePageShortcut('e', () => setShowEventForm(true), { enabled: !!matter })
  usePageShortcut('i', () => setShowGenerateInvoice(true), { enabled: !!matter && canCreateInvoices })
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [closeChecklist, setCloseChecklist] = useState(null)
  const [closeCheckLoading, setCloseCheckLoading] = useState(false)
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(new Set())
  const [closingNotes, setClosingNotes] = useState('')
  const [closeStep, setCloseStep] = useState(1)

  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = localStorage.getItem(`matter_accordion_${id}`)
      return saved ? JSON.parse(saved) : { time: true }
    } catch { return { time: true } }
  })
  useEffect(() => {
    localStorage.setItem(`matter_accordion_${id}`, JSON.stringify(openSections))
  }, [openSections, id])
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }))

  // Desktop app: subscribe to open-path failures so the user gets a useful
  // toast (with path copied to clipboard as a fallback) instead of silently
  // wondering if their click did anything.  Common cause: network share at
  // \\192.168.1.5 unreachable (VPN dropped, stale SMB credentials).
  useEffect(() => {
    if (!window.electronAPI?.onOpenPathFailed) return
    window.electronAPI.onOpenPathFailed(({ path }) => {
      try { navigator.clipboard.writeText(path) } catch {}
      setCopyToast(`Could not open "${path}" — path copied to clipboard. Check network/VPN and paste into File Explorer (Win+E, Ctrl+V).`)
      setTimeout(() => setCopyToast(''), 8000)
    })
  }, [])

  // Live sync pin state — another window (Electron popout, another browser
  // tab) toggling the same matter fires 'storage' event across the origin +
  // our own module dispatches the custom event on same-tab writes.  Both
  // paths converge on the same re-read.
  useEffect(() => {
    if (!matter?.id) return
    const refresh = () => setPinned(isMatterPinned(matter.id))
    window.addEventListener(PINNED_MATTERS_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(PINNED_MATTERS_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [matter?.id])

  const handleTogglePin = () => {
    if (!matter?.id) return
    const next = toggleMatterPin({ id: matter.id, title: matter.title })
    setPinned(next)
    toast.success(next ? `Pinned "${matter.title}" to sidebar` : 'Unpinned from sidebar')
  }

  // Ship 7b F2.3.6 — Amicus SystemAdministration "Selected File" parity —
  // 3 drilldown PDFs (All Events / All Time / People).  Each handler:
  //   1. Refetches the bundle so the server's current-authorization view
  //      is the source of truth (pre-code panel CRITICAL — a stale in-memory
  //      matter.time_entries could leak entries a since-revoked user
  //      shouldn't see).
  //   2. Builds the PDF client-side via matterPDF.js helpers.
  //   3. Awaits doc.save() success BEFORE firing logExport — the panel
  //      HIGH finding requires: no phantom audit rows on failed generators.
  //   4. Surfaces a toast on failure so the user isn't left wondering.
  const handlePrintEventsPDF = async () => {
    if (!matter?.id || printingPDF) return
    const { buildMatterEventsPDF, matterEventsFilename } = await import('../lib/matterPDF')
    setPrintingPDF('events')
    try {
      // Refetch to pick up any post-load permission change / newly-added
      // events.  Fail-open on network error — the in-memory matter is our
      // fallback so a transient blip doesn't block the print.
      let liveMatter = matter
      try {
        const res = await api.get(`/api/matters/${id}?bundle=true`)
        if (res?.data) { liveMatter = res.data; setMatter(res.data) }
      } catch { /* keep in-memory */ }
      const doc = buildMatterEventsPDF(liveMatter, firmSettings, { printedBy: pdfPrintedByName(user), pageSetup: matterPdfPageSetup() })
      const filename = matterEventsFilename(liveMatter)
      doc.save(filename)
      logExport({
        resource_type: 'matter_events',
        resource_id: liveMatter.id,
        format: 'pdf',
        filename,
        description: `Matter: ${liveMatter.title || liveMatter.id}`,
        count: (liveMatter.events || []).length,
      })
    } catch (err) {
      toast.error(err?.message || 'Failed to build Events PDF')
    } finally {
      setPrintingPDF(null)
    }
  }

  const handlePrintTimePDF = async () => {
    if (!matter?.id || printingPDF) return
    const { buildMatterTimePDF, matterTimeFilename } = await import('../lib/matterPDF')
    setPrintingPDF('time')
    try {
      let liveMatter = matter
      try {
        const res = await api.get(`/api/matters/${id}?bundle=true`)
        if (res?.data) { liveMatter = res.data; setMatter(res.data) }
      } catch { /* keep in-memory */ }
      // Recompute the ON-SCREEN filtered view against the fresh matter
      // payload so the PDF matches what the user is looking at (respects
      // My/All toggle + date-range filter).  Same logic that renders the
      // ATTORNEY tab.
      const all = liveMatter.time_entries || []
      const mine = all.filter(t => t.user_id === user?.id)
      const scope = (canViewAll && showAllTime) ? 'all' : 'own'
      const scoped = scope === 'all' ? all : mine
      const isWithin = (entry) => {
        const d = parseDateOnly(entry.date)
        if (!d) return true
        const t = new Date(); t.setHours(0, 0, 0, 0)
        const sow = new Date(t); sow.setDate(t.getDate() - t.getDay())
        const som = new Date(t.getFullYear(), t.getMonth(), 1)
        if (timeDateFilter === 'today') return d.getTime() === t.getTime()
        if (timeDateFilter === 'week')  return d >= sow
        if (timeDateFilter === 'month') return d >= som
        if (timeDateFilter === 'custom') {
          const from = timeDateCustom.from ? parseDateOnly(timeDateCustom.from) : null
          const to   = timeDateCustom.to   ? parseDateOnly(timeDateCustom.to)   : null
          if (from && d < from) return false
          if (to   && d > to)   return false
          return true
        }
        return true
      }
      const filtered = scoped.filter(isWithin)
      const doc = buildMatterTimePDF(liveMatter, filtered, firmSettings, {
        scope, dateFilter: timeDateFilter, dateCustom: timeDateCustom,
        printedBy: pdfPrintedByName(user),
        pageSetup: matterPdfPageSetup(),
      })
      const filename = matterTimeFilename(liveMatter)
      doc.save(filename)
      // Include scope + date filter in the audit metadata so reviewers can
      // distinguish 'my time' from 'firm time' exports (pre-code panel HIGH).
      logExport({
        resource_type: 'matter_time',
        resource_id: liveMatter.id,
        format: 'pdf',
        filename,
        description: `Matter: ${liveMatter.title || liveMatter.id} · scope=${scope} · filter=${timeDateFilter}`,
        count: filtered.length,
      })
    } catch (err) {
      toast.error(err?.message || 'Failed to build Time PDF')
    } finally {
      setPrintingPDF(null)
    }
  }

  const handlePrintContactsPDF = async () => {
    if (!matter?.id || printingPDF) return
    const { buildMatterContactsPDF, matterContactsFilename } = await import('../lib/matterPDF')
    setPrintingPDF('contacts')
    try {
      let liveMatter = matter
      let liveContacts = matterContacts
      try {
        const res = await api.get(`/api/matters/${id}?bundle=true`)
        if (res?.data) {
          liveMatter = res.data
          setMatter(res.data)
          if (Array.isArray(res.data.matter_contacts)) {
            liveContacts = res.data.matter_contacts
            setMatterContacts(res.data.matter_contacts)
          }
        }
      } catch { /* keep in-memory */ }
      const doc = buildMatterContactsPDF(liveMatter, liveContacts, firmSettings, { printedBy: pdfPrintedByName(user), pageSetup: matterPdfPageSetup() })
      const filename = matterContactsFilename(liveMatter)
      doc.save(filename)
      logExport({
        resource_type: 'matter_contacts',
        resource_id: liveMatter.id,
        format: 'pdf',
        filename,
        description: `Matter: ${liveMatter.title || liveMatter.id}`,
        count: (liveContacts || []).length,
      })
    } catch (err) {
      toast.error(err?.message || 'Failed to build Contacts PDF')
    } finally {
      setPrintingPDF(null)
    }
  }

  // Ship 7i — Fetch fresh witnesses for the two new PDFs.  Bundle endpoint
  // now includes matter.witnesses (matters.js:355 addition) but a dedicated
  // fetch is safer here for the same reason the other 3 print handlers
  // refetch the bundle: a since-load reclassification (fact → retained
  // expert) or a since-load witness add MUST reflect in the printed roster.
  const fetchLiveWitnesses = async () => {
    try {
      const r = await listMatterWitnesses(id)
      return Array.isArray(r?.data) ? r.data : []
    } catch (err) {
      // Fall back to matter.witnesses from the last bundle fetch if the
      // dedicated endpoint fails — better a slightly stale roster than
      // a failed print.  Empty array is legit (no witnesses yet).
      return Array.isArray(matter?.witnesses) ? matter.witnesses : []
    }
  }

  // Ship 7i — Master Witness List PDF (internal trial-prep work-product).
  // No completeness validation; empty roster still produces a 1-page PDF
  // (proof-of-nothing bar-audit request).  logExport carries count=0
  // in the empty case.
  const handlePrintWitnessListPDF = async () => {
    if (!matter?.id || printingPDF) return
    const { buildMasterWitnessListPDF, witnessListFilename } = await import('../lib/witnessListPDF')
    setPrintingPDF('witnesses')
    try {
      const witnesses = await fetchLiveWitnesses()
      const doc = buildMasterWitnessListPDF(matter, witnesses, firmSettings, { printedBy: pdfPrintedByName(user), pageSetup: matterPdfPageSetup() })
      const filename = witnessListFilename(matter)
      doc.save(filename)
      logExport({
        resource_type: 'witness_list',
        resource_id: matter.id,
        format: 'pdf',
        filename,
        description: `Matter: ${matter.title || matter.id} · ${witnesses.length} witness(es)`,
        count: witnesses.length,
      })
    } catch (err) {
      toast.error(err?.message || 'Failed to build Witness List PDF')
    } finally {
      setPrintingPDF(null)
    }
  }

  // Ship 7i — §2034.260 Expert Witness Designation (COURT FILING).
  //
  // Panel-driven flow:
  //   1. Refetch witnesses → count experts.  Refuse if 0 (toast, no PDF).
  //   2. Run validateExpertDesignationCompleteness() → surface missing
  //      §2034.260(c)(1)-(5) fields via useAsk() BEFORE building.
  //   3. useAsk() ceremony gates the build even when validation passes —
  //      "This is a court filing.  Verify all fields before serving.
  //       Continue as a DRAFT (watermark) or STOP to review?"
  //   4. Always build as DRAFT (opts.draft=true) for v1.  A future
  //      filing-ready path can opt into opts.draft=false after a
  //      supervising-attorney signoff surface is built (not v1).
  //   5. logExport fires AFTER doc.save() resolves with the draft
  //      flag captured in the audit metadata so reviewers can grep
  //      draft vs filed exports.
  const handlePrintExpertDesignationPDF = async () => {
    if (!matter?.id || printingPDF) return
    const {
      buildExpertDesignationPDF, expertDesignationFilename,
      validateExpertDesignationCompleteness,
    } = await import('../lib/expertDesignationPDF')

    const witnesses = await fetchLiveWitnesses()
    const validation = validateExpertDesignationCompleteness(witnesses)

    if (validation.retained.length === 0 && validation.nonRetained.length === 0) {
      toast.error('No experts designated for this matter.  Add at least one retained or non-retained expert first.')
      return
    }

    // Surface missing-fields to the attorney BEFORE building.
    let confirmMsg =
      '§2034.260 Expert Designation is a COURT FILING carrying §2034.300 exclusion-sanction risk.\n\n' +
      `This matter has ${validation.retained.length} retained expert(s) and ${validation.nonRetained.length} non-retained expert(s).\n\n`
    if (validation.missing.length > 0) {
      confirmMsg += `⚠ ${validation.missing.length} retained expert row(s) are missing statutory fields:\n`
      for (const m of validation.missing.slice(0, 6)) {
        confirmMsg += `  • ${m.witness_name} — missing: ${m.missing_fields.join(', ')}\n`
      }
      if (validation.missing.length > 6) {
        confirmMsg += `  … and ${validation.missing.length - 6} more.\n`
      }
      confirmMsg += '\nGaps will be surfaced as bracketed placeholders in the DRAFT PDF.\n\n'
    }
    confirmMsg +=
      'This will generate a DRAFT PDF with a "NOT FOR FILING" watermark on every page.  ' +
      'Review carefully against local court rules and §2034.260(c) requirements before removing the watermark and serving.\n\n' +
      'Continue?'

    const ok = await ask({
      type: 'confirm',
      title: '⚖ CCP §2034.260 Expert Designation',
      message: confirmMsg,
      confirmLabel: 'Build DRAFT PDF',
      cancelLabel: 'Cancel',
      danger: false,
    })
    if (!ok) return

    setPrintingPDF('expertDesignation')
    try {
      // Build DRAFT — v1 hard-locks .draft=true.  A supervising-attorney
      // signoff surface + filing-ready pipeline is deferred to Ship 7i.1.
      const result = buildExpertDesignationPDF(matter, witnesses, firmSettings, {
        draft: true,
        mode: 'initial',
        // Ship 7i.1 post-code verify panel HIGH fix — fallback ladder is
        // matter.designating_party → firm_settings.default_designating_party
        // → empty (which triggers the PDF-builder's "designating party not set"
        // refusal-to-render).  Prior version skipped the firm-default step so
        // firms that configured a firm-wide default in Settings still saw the
        // "[DESIGNATING PARTY]" placeholder in the caption/preamble/declaration
        // for every matter that hadn't overridden the default.  This matches
        // the fallback ladder documented at MatterDetail.js:5460-5462 for the
        // on-screen MatterDesignatingPartyCard.
        designating_party: matter?.designating_party || firmSettings?.default_designating_party || '',
        attorney_of_record: firmSettings?.firm_name || '',
        attorney_bar_number: firmSettings?.bar_number || '',
        court_name: '',
        case_caption: matter?.title || '',
        case_number: matter?.file_number || '',
      })

      if (!result.ok) {
        // Should not happen in DRAFT mode (draft path always builds), but
        // handle defensively so a future filing-ready path doesn't silently
        // no-op.
        const reason = result.reason === 'no_experts'
          ? 'No experts on this matter.'
          : `Expert designation refused: ${result.reason}`
        toast.error(reason)
        return
      }

      const filename = expertDesignationFilename(matter, { mode: 'initial' })
      result.doc.save(filename)
      logExport({
        resource_type: 'expert_designation',
        resource_id: matter.id,
        format: 'pdf',
        filename,
        description: `§2034.260 Designation — Matter: ${matter.title || matter.id} · DRAFT · retained=${validation.retained.length} non_retained=${validation.nonRetained.length} · warnings=${(result.warnings || []).length}`,
        count: validation.retained.length + validation.nonRetained.length,
      })
    } catch (err) {
      toast.error(err?.message || 'Failed to build Expert Designation PDF')
    } finally {
      setPrintingPDF(null)
    }
  }

  const [timeForm, setTimeForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    hours: '', rate: '', description: '',
    billing_status: 'billable', activity_code: ''
  })

  const [eventForm, setEventForm] = useState({
    title: '', description: '', notes: '', start_time: '',
    end_time: '', event_type: 'appointment', location: '',
    sync_to_google: false, push_to_user_ids: [],
    // Feedback #828 — reminder rows from the shared ReminderEditor.
    reminders: [],
    ...emptyRecurrence,
  })
  // Feedback #825 — which matter events have their description/notes expanded.
  const [expandedEventIds, setExpandedEventIds] = useState(() => new Set())
  // Feedback #829 follow-up — event history on the matter's event list.
  // matter.events comes from the matter bundle (GET /api/matters/:id), which
  // does not join the author names.  While the Events section is open, fetch
  // them ONCE from GET /api/calendar/matter/:id and merge by id — refetched
  // only when the set of events (or one of their updated_at stamps) changes,
  // not on every unrelated matter reload.  Self-disabling: once the bundle
  // carries the names, eventsNeedAuthorNames() is false and no request goes.
  const [eventAuthorRows, setEventAuthorRows] = useState(null)
  const matterEvents = matter?.events
  const eventsNeedNames = eventsNeedAuthorNames(matterEvents)
  const eventsSig = Array.isArray(matterEvents)
    ? matterEvents.map(e => `${e.id}:${e.updated_at || ''}`).join(',')
    : ''
  useEffect(() => {
    if (activeTab !== 'attorney' || !openSections.events || !eventsNeedNames) return
    let cancelled = false
    api.get(`/api/calendar/matter/${id}`)
      .then(res => { if (!cancelled) setEventAuthorRows(Array.isArray(res.data) ? res.data : []) })
      // Best-effort: without names the rows still get the History expander,
      // just no "Created / Last changed" summary lines.
      .catch(() => { if (!cancelled) setEventAuthorRows([]) })
    return () => { cancelled = true }
  }, [id, activeTab, openSections.events, eventsNeedNames, eventsSig])
  const eventsForList = useMemo(
    () => mergeEventAuthors(matterEvents || [], eventAuthorRows),
    [matterEvents, eventAuthorRows]
  )
  // Google Calendar sync state for the event form
  const [gcalStatus, setGcalStatus] = useState({ configured: false, connected: false })
  const [gcalRecipients, setGcalRecipients] = useState([])
  useEffect(() => {
    // T1-E (2026-08-08) — parallelize the 2 fetches.  The recipients call
    // doesn't consume the status response, so serializing them was a wasted
    // RTT on every MatterDetail cold-load.  Ignore recipients if status
    // resolves to disconnected (frontend components gate on gcalStatus.connected
    // anyway so extra data doesn't leak into UI).
    let cancelled = false
    ;(async () => {
      try {
        const [s, r] = await Promise.all([
          api.get('/api/google-calendar/status'),
          api.get('/api/google-calendar/eligible-recipients').catch(() => ({ data: { users: [] } })),
        ])
        if (cancelled) return
        setGcalStatus(s.data)
        if (s.data?.connected) setGcalRecipients(r.data?.users || [])
      } catch {}
    })()
    return () => { cancelled = true }
  }, [])

  // Ship 7b F2.3.6 — cache firm_settings once for the 3 drilldown PDFs.
  // Silent on failure (pdfHeader defaults handle it).  Same fetch pattern
  // Reports.js:268 uses.
  useEffect(() => {
    let cancelled = false
    api.get('/api/settings')
      .then(res => {
        // This page ALSO renders outside Layout, at /popup/page/matters/:id
        // (App.js), and PopupLayout never populates `__lextrackFirmTz` — a
        // popped-out window is its own renderer with its own `window`, so the
        // global Layout sets does not cross over.  Without this, every
        // toFirmWallClock() call below silently falls back to DEFAULT_FIRM_TZ,
        // which is right for this firm and wrong for any tenant outside
        // Pacific — the exact invisible-fallback trap CLAUDE.md records for
        // the consult-cancel page.  Populate it from the request we already
        // make.  Set before setFirmSettings so the re-render that state
        // update triggers reads the correct zone.
        const tz = res.data?.timezone
        if (tz) window.__lextrackFirmTz = tz
        if (!cancelled) setFirmSettings(res.data || {})
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    // Cold-load via the ?bundle=true server-batched endpoint — 1 round-trip
    // instead of 7.  Falls back to the 7 individual fetchers if the bundle
    // fails (e.g. older API version).  Individual fetchers stay defined
    // below and are called after mutations (add comm, link contact, etc.).
    fetchMatterBundle().catch(() => {
      Promise.all([
        fetchMatter(),
        fetchComms(),
        fetchMatterContacts(),
        fetchSOL(),
        fetchDocuments(),
        fetchCasesBasePath(),
        fetchDiscoverySets()
      ]).catch(() => { /* individual fetchers already setError on failure */ })
    })
    const handleTimerSave = () => fetchMatter()
    window.addEventListener('timerEntrySaved', handleTimerSave)
    // Cross-window save event from a popped-out timer (feedback #67)
    const handleStorageSave = (e) => {
      if (e.key !== 'lextrack_timer_broadcast' || !e.newValue) return
      try {
        const data = JSON.parse(e.newValue)
        if (data?.type === 'saved') handleTimerSave()
      } catch {}
    }
    window.addEventListener('storage', handleStorageSave)

    return () => {
      window.removeEventListener('timerEntrySaved', handleTimerSave)
      window.removeEventListener('storage', handleStorageSave)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const roundToSixMin = (hours) => { // eslint-disable-line no-unused-vars
    const h = parseFloat(hours) || 0
    if (h === 0) return 0
    return Math.max(0.1, Math.ceil(h * 10) / 10)
  }

  const formatTimer = (seconds) => { // eslint-disable-line no-unused-vars
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0')
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0')
    const s = (seconds % 60).toString().padStart(2, '0')
    return `${h}:${m}:${s}`
  }

  // ⏱ Start timer (firm decision 2026-10-13): one click starts timing on THIS
  // matter — no form; the description and the rest are filled in later from
  // ⏱ Timers.  Layout (or, in a popped-out page, useTimerEventBridge) creates
  // the timer through lib/timerStore, which pauses whichever timer was
  // running; without can_post_time it says so and starts nothing.  The id,
  // title and billing type come from the loaded matter together, so the
  // title always belongs to the id (billing type sets the default status).
  // Forms stay forms: "+ Add Time" and the t shortcut still open one.
  const startMatterTimer = () => {
    if (!matter) return
    window.dispatchEvent(new CustomEvent('start-timer', {
      detail: { matter_id: matter.id, matter_title: matter.title || '', billing_type: matter.billing_type || '' }
    }))
  }

  const fetchMatter = async () => {
    try {
      const res = await getMatter(id)
      if (!res.data) {
        setError('Matter not found')
        return
      }
      setMatter(res.data)
      // Track for the sidebar "Recent Matters" muscle-memory shortcut
      pushRecentMatter({ id: res.data.id, title: res.data.title })
      // Refresh pin state whenever the matter loads (a different device may
      // have pinned/unpinned this matter since the last time we opened it —
      // localStorage syncs via 'storage' event but not on cold-fetch).
      setPinned(isMatterPinned(res.data.id))
      if (res.data.rate) {
        setTimeForm(f => ({ ...f, rate: res.data.rate }))
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading matter')
    } finally {
      setLoading(false)
    }
  }

  // Cold-load bundle — pulls matter + comms + matter_contacts + sol +
  // documents + discovery + cases_base_path in ONE HTTP round-trip via
  // the ?bundle=true query param.  Individual fetchers below stay defined
  // for post-mutation refresh (add comm, delete SOL, etc.).
  const fetchMatterBundle = async () => {
    try {
      const res = await api.get(`/api/matters/${id}?bundle=true`)
      if (!res.data) { setError('Matter not found'); return }
      setMatter(res.data)
      pushRecentMatter({ id: res.data.id, title: res.data.title })
      if (res.data.rate) setTimeForm(f => ({ ...f, rate: res.data.rate }))
      if (Array.isArray(res.data.communications)) setCommunications(res.data.communications)
      if (Array.isArray(res.data.matter_contacts)) setMatterContacts(res.data.matter_contacts)
      if (Array.isArray(res.data.sol)) setSOLEntries(res.data.sol)
      if (Array.isArray(res.data.matter_documents)) setMatterDocuments(res.data.matter_documents)
      if (Array.isArray(res.data.discovery_sets)) setDiscoverySets(res.data.discovery_sets)
      if (res.data.cases_base_path) setCasesBasePath(res.data.cases_base_path)
    } catch (err) {
      // Bubble up so the .catch in useEffect can fall back to individual fetchers
      throw err
    } finally {
      setLoading(false)
    }
  }

  const fetchComms = async () => {
    try {
      const res = await api.get(`/api/communications/matter/${id}`)
      setCommunications(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading communications')
    }
  }

  const fetchMatterContacts = async () => {
    try {
      const res = await api.get(`/api/contacts/matter/${id}`)
      setMatterContacts(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading contacts')
    }
  }

  const fetchSOL = async () => {
    try {
      const res = await api.get(`/api/sol/matter/${id}`)
      setSOLEntries(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading SOL entries')
    }
  }

  const fetchDiscoverySets = async () => {
    try {
      const res = await api.get(`/api/discovery?matter_id=${id}`)
      setDiscoverySets(res.data.sets || [])
    } catch (err) {
      // Pre-API-restart graceful degrade: route may not be live yet
      if (err.response?.status === 404) setDiscoverySets([])
      else setError(err.response?.data?.error || 'Error loading discovery')
    }
  }

  const fetchDocuments = async () => {
    try {
      const res = await api.get(`/api/matter-documents/matter/${id}`)
      setMatterDocuments(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading document links')
    }
  }

  const fetchCasesBasePath = async () => {
    try {
      const res = await api.get('/api/settings')
      if (res.data?.cases_base_path) setCasesBasePath(res.data.cases_base_path)
    } catch (err) { /* use default */ }
  }

  const detectLinkType = (path) => {
    if (!path) return 'network'
    const p = path.trim()
    if (/^https?:\/\//i.test(p)) return 'url'
    return 'network'
  }

  const buildMatterPath = (suffix = '') => {
    const base = casesBasePath.replace(/[\\/]+$/, '')
    const title = (matter?.title || '').replace(/[<>:"|?*]/g, '').trim()
    return `${base}\\${title}${suffix ? '\\' + suffix : '\\'}`
  }

  const standardLinkDefs = () => ([
    { key: 'case_files', name: 'Case Files', suffix: '' },
    { key: 'discovery', name: 'Discovery', suffix: 'Discovery' },
    { key: 'pleadings', name: 'Pleadings', suffix: 'Pleadings' },
    { key: 'correspondence', name: 'Correspondence', suffix: 'Correspondence' },
    { key: 'evidence', name: 'Evidence', suffix: 'Evidence' },
    { key: 'depositions', name: 'Deposition Transcripts', suffix: 'Depositions' },
    { key: 'billing', name: 'Billing', suffix: 'Billing' }
  ])

  const handleSaveDocument = async (e) => {
    e.preventDefault()
    if (!docForm.name.trim() || !docForm.path.trim()) {
      setError('Name and path are required')
      return
    }
    try {
      const payload = { ...docForm, link_type: detectLinkType(docForm.path) }
      if (editingDocId) {
        await api.put(`/api/matter-documents/${editingDocId}`, payload)
      } else {
        await api.post(`/api/matter-documents/matter/${id}`, payload)
      }
      setDocForm({ name: '', path: '', link_type: 'network', description: '' })
      setShowDocForm(false)
      setEditingDocId(null)
      fetchDocuments()
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving document link')
    }
  }

  const handleDeleteDocument = async (docId) => {
    if (!await ask({ type: 'confirm', title: 'Delete document link?', message: 'Delete this document link?', confirmLabel: 'Delete', danger: true })) return
    try {
      await api.delete(`/api/matter-documents/${docId}`)
      fetchDocuments()
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting document link')
    }
  }

  // Toggle share_with_client on a document — flips a doc's visibility on
  // the client portal.  Confirms BEFORE turning on (attorneys should be
  // deliberate about what a client sees) but not before turning off
  // (unsharing is always safe).  Sharing a network/UNC path is fine — the
  // client's portal displays the path + name + description; they can copy
  // it and open on their own if they have access to the same file share
  // (rare) or the attorney can use it as a reference to a doc they'll
  // separately email.  Sharing a URL link is the common case.
  const handleToggleShare = async (doc) => {
    const nextShared = !doc.share_with_client
    if (nextShared) {
      // Warn about network paths — clients usually can't open UNC paths from
      // their own machines, so the share is symbolic + reference-only for
      // those.  Bare confirm for URL types since those work everywhere.
      const isNetworkPath = (doc.link_type || 'network') === 'network'
      const msg = isNetworkPath
        ? `Share "${doc.name}" with this client on their portal?\n\nThis is a network path (\\\\...) — most clients can't open it from their own computers.  They'll see the path + description as a reference.\n\nFor documents you want the client to actually download, use a URL link instead.`
        : `Share "${doc.name}" with this client on their portal?\n\nThey'll see it under "Documents" and can click through to open.`
      if (!await ask({ type: 'confirm', title: 'Share with client?', message: msg, confirmLabel: 'Share' })) return
    }
    try {
      await api.put(`/api/matter-documents/${doc.id}/share`, { share: nextShared })
      toast.success(nextShared ? 'Now shared with client' : 'Sharing turned off')
      fetchDocuments()
      // Ship 7g portal-shared-audit: if the audit panel is currently open,
      // refresh its data so the just-shared / just-unshared row appears
      // (or disappears) without a manual refresh click.
      if (showPortalAudit) fetchPortalAudit()
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update share status')
    }
  }

  // Ship 7g portal-shared-audit — pulls per-matter shared-doc + engagement
  // rollup.  Fired on panel-open toggle + after every share/unshare toggle.
  // Silent no-op if the user lacks access (backend returns 404 for blocked
  // matters via assertMatterAccess — matches the sibling docs list gate).
  const fetchPortalAudit = async () => {
    setPortalAuditLoading(true)
    setPortalAuditError('')
    try {
      const res = await api.get(`/api/matter-documents/matter/${id}/portal-shared-audit`)
      setPortalAuditRows(Array.isArray(res.data?.shared_documents) ? res.data.shared_documents : [])
    } catch (err) {
      setPortalAuditError(err.response?.data?.error || 'Could not load portal audit')
      setPortalAuditRows([])
    } finally {
      setPortalAuditLoading(false)
    }
  }

  // Phase 4 #4 — load matter's Bates config when opening the config modal
  useEffect(() => {
    if (!showBatesConfig || !id) return
    api.get(`/api/matter-documents/matter/${id}/bates-config`).then(r => {
      setBatesConfig({
        bates_prefix: r.data?.bates_prefix || '',
        bates_next_number: r.data?.bates_next_number || 1,
        bates_padding: r.data?.bates_padding || 6
      })
    }).catch(() => {})
  }, [showBatesConfig, id])
  const handleSaveBatesConfig = async (e) => {
    e?.preventDefault?.()
    setBatesConfigBusy(true)
    try {
      await api.put(`/api/matter-documents/matter/${id}/bates-config`, batesConfig)
      setShowBatesConfig(false)
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save Bates config')
    } finally { setBatesConfigBusy(false) }
  }

  // Phase 4 #4 — Bates assignment for a single document.  Prompts for count
  // (default 1 for single-page; multi-page docs pass N).  Atomically allocates
  // the next N numbers from the matter's bates_next_number counter.
  const handleAssignBates = async (doc) => {
    if (doc.bates_range && !(await ask({ type: 'confirm', title: 'Re-stamp document?', message: `Re-stamp this document?  Current range: ${doc.bates_range}.  The matter's Bates counter advances regardless — the old range stays "allocated" in the audit trail.`, confirmLabel: 'Re-stamp' }))) return
    const ans = await ask({ title: 'Allocate Bates numbers', message: 'How many Bates numbers to allocate to this document?  (1 for single-page, N for multi-page)', defaultValue: '1', type: 'number', required: true, confirmLabel: 'Allocate' })
    if (ans === null) return
    const count = Math.max(1, parseInt(ans, 10) || 1)
    try {
      const r = await api.post(`/api/matter-documents/${doc.id}/assign-bates`, { count })
      fetchDocuments()
      toast.success(`Assigned: ${r.data.bates_range}`)
    } catch (err) {
      setError(err.response?.data?.error || 'Could not assign Bates numbers')
    }
  }

  const handleEditDocument = (doc) => {
    setEditingDocId(doc.id)
    setDocForm({
      name: doc.name || '',
      path: doc.path || '',
      link_type: doc.link_type || 'network',
      description: doc.description || ''
    })
    setShowDocForm(true)
    setShowStandardLinks(false)
  }

  const handleOpenDocumentPath = (path) => {
    // Always copy to clipboard as a defensive fallback — that way if the
    // desktop app's openPath silently fails (network share unreachable, old
    // build without the explorer.exe spawn fallback, etc.) the user always
    // has the path on their clipboard and can paste into File Explorer.
    navigator.clipboard.writeText(path).catch(() => {})

    if (window.electronAPI?.isElectron) {
      window.electronAPI.openPath(path)
      setCopyToast(`Opening "${path}"… (path also copied to clipboard if it doesn't open)`)
      setTimeout(() => setCopyToast(''), 5000)
    } else {
      setCopyToast('Path copied! Open File Explorer (Win+E) and paste in address bar (Ctrl+V).')
      setTimeout(() => setCopyToast(''), 5000)
    }
  }

  // Build a child path under whatever the user has in matterFolderPath, falling
  // back to the auto-computed matter folder if they haven't touched the field.
  const baseFolder = () => (matterFolderPath || buildMatterPath('').replace(/\\$/, '')).replace(/\\+$/, '')
  const fullPathFor = (subfolder) => subfolder ? `${baseFolder()}\\${subfolder}` : `${baseFolder()}\\`

  const handleScanFolder = async () => {
    setScanError('')
    setScanning(true)
    try {
      const result = await window.electronAPI.scanFolder(baseFolder())
      if (result?.error) {
        setScanError(`Could not scan: ${result.error}`)
        setScannedSubfolders(null)
        return
      }
      const subs = result?.subfolders || []
      setScannedSubfolders(subs)
      // Pre-check all discovered subfolders so the common case (link them all)
      // is a single confirmation click — same UX as the standard 7.
      const all = {}
      subs.forEach(f => { all[f.name] = true })
      setSelectedScanned(all)
    } catch (err) {
      setScanError(err?.message || 'Scan failed')
      setScannedSubfolders(null)
    } finally {
      setScanning(false)
    }
  }

  const handleAddStandardLinks = async () => {
    let payload = []
    if (scannedSubfolders) {
      // Scanned mode — create one link per checked discovered subfolder
      const chosen = scannedSubfolders.filter(f => selectedScanned[f.name])
      if (chosen.length === 0) { setError('Select at least one folder'); return }
      payload = chosen.map(f => ({ name: f.name, path: f.fullPath, link_type: 'network', description: null }))
    } else {
      // Standard 7 mode — one link per checked definition, path computed from
      // the (possibly user-edited) matter folder path
      const chosen = standardLinkDefs().filter(d => selectedStandardLinks[d.key])
      if (chosen.length === 0) { setError('Select at least one standard link'); return }
      payload = chosen.map(d => ({
        name: d.name,
        path: fullPathFor(d.suffix),
        link_type: 'network',
        description: null
      }))
    }
    try {
      await api.post(`/api/matter-documents/matter/${id}`, payload)
      setSelectedStandardLinks({})
      setSelectedScanned({})
      setScannedSubfolders(null)
      setScanError('')
      setShowStandardLinks(false)
      fetchDocuments()
    } catch (err) {
      setError(err.response?.data?.error || 'Error adding links')
    }
  }

  const handleOpenCloseModal = async () => {
    setShowCloseModal(true)
    setCloseStep(1)
    setClosingNotes('')
    setAcknowledgedWarnings(new Set())
    setCloseCheckLoading(true)
    try {
      const res = await api.get(`/api/matters/${id}/close-check`)
      setCloseChecklist(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error running close check')
      setShowCloseModal(false)
    } finally {
      setCloseCheckLoading(false)
    }
  }

  // "Close Matter…" on the Matters edit form (James 2026-10-19, "Send them
  // to Close/Reopen") links here with ?close=1: the edit form can't close a
  // matter, so this page opens Close Matter once the matter has loaded — on
  // an open matter, for someone who sees the Close Matter button (admin or
  // can_create_matters).  The param is scrubbed (history replace) either
  // way, so a reload or Back doesn't open the modal again; the ref makes it
  // once per matter even if the scrub is slow.
  const closeParamRequested = searchParams.get('close') === '1'
  const closeParamHandledFor = useRef(null)
  useEffect(() => {
    if (!closeParamRequested || closeParamHandledFor.current === id) return
    if (!matter || String(matter.id) !== String(id)) return
    closeParamHandledFor.current = id
    const rest = new URLSearchParams(searchParams)
    rest.delete('close')
    setSearchParams(rest, { replace: true })
    if (matter.status !== 'closed' && (isUserAdmin || user?.permissions?.can_create_matters)) {
      handleOpenCloseModal()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeParamRequested, matter?.id, matter?.status, id])

  const handleCloseMatter = async (overrideTrust = false) => {
    try {
      await api.post(`/api/matters/${id}/close`, {
        acknowledged_warnings: [...acknowledgedWarnings],
        closing_notes: closingNotes.trim(),
        override_trust_balance: overrideTrust
      })
      setShowCloseModal(false)
      fetchMatter()
    } catch (err) {
      // Server may reject due to non-zero trust balance.  Offer the user a
      // chance to confirm the override — but force them to document why in
      // their closing notes (which the API already requires non-empty).
      const data = err.response?.data
      if (data?.trust_balance !== undefined && !overrideTrust) {
        if (await ask({ type: 'confirm', title: 'Override trust balance guard?', message: `${data.error}\n\nClick "Override & close" to close anyway (your closing notes will be the record of why).`, confirmLabel: 'Override & close', danger: true })) {
          return handleCloseMatter(true)
        }
      }
      setError(data?.error || 'Error closing matter')
    }
  }

  const handleReopenMatter = async () => {
    // The same words as the edit form's and the list badge's reopen (round-3
    // review): it names what the reopen clears.
    if (!await ask(reopenConfirmOptions('active'))) return
    try {
      await api.post(`/api/matters/${id}/reopen`)
      fetchMatter()
    } catch (err) {
      setError(err.response?.data?.error || 'Error reopening matter')
    }
  }

  const CA_SOL_PERIODS = [
    { label: 'Personal Injury', months: 24 }, { label: 'Wrongful Death', months: 24 },
    { label: 'Medical Malpractice', months: 36 }, { label: 'Property Damage', months: 36 },
    { label: 'Breach of Written Contract', months: 48 }, { label: 'Breach of Oral Contract', months: 24 },
    { label: 'Fraud', months: 36 }, { label: 'Defamation', months: 12 },
    { label: 'False Imprisonment', months: 24 }, { label: 'Professional Negligence', months: 24 },
    { label: 'Products Liability', months: 24 }, { label: 'Construction Defect', months: 36 },
    { label: 'Employment Discrimination', months: 36 }, { label: 'Civil Rights', months: 24 },
    { label: 'Elder Abuse', months: 24 }, { label: 'Sexual Assault', months: 36 },
    { label: 'Government Claim (Tort)', months: 6 }, { label: 'Breach of Fiduciary Duty', months: 36 },
    { label: 'Other', months: null },
  ]

  const handleSOLSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingSOL) {
        await api.put(`/api/sol/${editingSOL}`, solForm)
        setEditingSOL(null)
      } else {
        await api.post('/api/sol', { ...solForm, matter_id: id })
      }
      // Saved — drop the draft BEFORE the state resets below, so useDraft's
      // suppression window swallows the final flush that fires when the key
      // goes null.  No-op when this was an edit (the key was never armed).
      discardSolDraft()
      setSOLForm({ cause_of_action: '', incident_date: '', sol_period_months: '', sol_date: '', tolling_notes: '', is_tolled: false, tolled_date: '', tolling_reason: '', status: 'active' })
      setShowSOLForm(false)
      setError('')
      fetchSOL()
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving SOL entry')
    }
  }

  const handleDeleteSOL = async (solId) => {
    if (!await ask({ type: 'confirm', title: 'Delete SOL entry?', message: 'Delete this SOL entry?', confirmLabel: 'Delete', danger: true })) return
    try {
      await api.delete(`/api/sol/${solId}`)
      setError('')
      fetchSOL()
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting SOL entry')
    }
  }

  const handleAddContactToMatter = async (e) => {
    e.preventDefault()
    if (!selectedContactId) return
    try {
      await api.post(`/api/contacts/matter/${id}`, {
        contact_id: selectedContactId,
        role: contactRole || null
      })
      setShowContactLink(false)
      setSelectedContactId('')
      setContactRole('')
      setError('')
      fetchMatterContacts()
    } catch (err) {
      setError(err.response?.data?.error || 'Error adding contact')
    }
  }

  const handleRemoveContactFromMatter = async (linkId) => {
    if (!await ask({ type: 'confirm', title: 'Remove contact?', message: 'Remove this contact from the matter?', confirmLabel: 'Remove', danger: true })) return
    try {
      await api.delete(`/api/contacts/matter-contact/${linkId}`)
      setError('')
      fetchMatterContacts()
    } catch (err) {
      setError(err.response?.data?.error || 'Error removing contact')
    }
  }

  const handleCommSubmit = async (e) => {
    e.preventDefault()
    // Guard against orphaned matter (no client_id) — communications FK requires one
    if (!editingComm && !matter?.client_id) {
      setError('This matter has no client linked. Assign a client to the matter before logging communications.')
      return
    }
    try {
      if (editingComm) {
        await api.put(`/api/communications/${editingComm}`, { ...commForm, comm_date: localInputToUtc(commForm.comm_date) })
        setEditingComm(null)
      } else {
        await api.post('/api/communications', { ...commForm, comm_date: localInputToUtc(commForm.comm_date), matter_id: id, client_id: matter.client_id })
      }
      // comm_date is firm-TZ end to end as of 2026-09-15.  The seed, the edit
      // seed and the submit are a COUPLED PAIR and are converted together:
      // seeds/edit-seed render the stored UTC into a firm-local input via
      // utcToLocalInput, and the submit converts back with localInputToUtc.
      // Display uses toFirmWallClock, the same helper the events list above
      // uses.  This matches Communications.js and Discovery.js exactly.
      // api/src/routes/communications.js binds the value VERBATIM by design
      // (see the contract comment there) — it must never reinterpret naive
      // input as firm-local, or this conversion would double-shift.
      // Saved — see the SOL handler for why the discard precedes the resets.
      discardCommDraft()
      setCommForm({ comm_type: 'phone_call', direction: 'outgoing', subject: '', description: '', duration_minutes: '', comm_date: utcToLocalInput(new Date()), needs_response: false })
      setShowCommForm(false)
      setError('')
      fetchComms()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error saving communication — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error saving communication (HTTP ${err.response.status})`)
      }
    }
  }

  const handleEditComm = (comm) => {
    setEditingComm(comm.id)
    setCommForm({
      comm_type: comm.comm_type,
      direction: comm.direction || 'outgoing',
      subject: comm.subject || '',
      description: comm.description || '',
      duration_minutes: comm.duration_minutes || '',
      comm_date: comm.comm_date ? utcToLocalInput(comm.comm_date) : '',
      // H-C4: hydrate needs_response from the row so re-saving doesn't
      // silently reset the callback-queue flag.
      needs_response: comm.needs_response ?? false,
    })
    setShowCommForm(true)
  }

  const handleDeleteComm = async (commId) => {
    if (!await ask({ type: 'confirm', title: 'Delete communication?', message: 'Delete this communication log entry?', confirmLabel: 'Delete', danger: true })) return
    try {
      await api.delete(`/api/communications/${commId}`)
      setError('')
      fetchComms()
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting communication')
    }
  }

  const handlePostEntry = async (entryId) => {
    try {
      await api.put(`/api/time-entries/${entryId}/post`)
      fetchMatter()
    } catch (err) {
      setError(err.response?.data?.error || 'Error posting entry')
    }
  }

  // Reopen an entry in the global timer, continuing from its logged hours.
  const handleResumeEntry = (entry) => {
    window.dispatchEvent(new CustomEvent('open-time-entry-popup', { detail: { resume_entry: {
      id: entry.id, matter_id: matter?.id || id, matter_title: matter?.title || '',
      description: entry.description, activity_code: entry.activity_code,
      billing_status: entry.billing_status, rate: entry.rate, hours: entry.hours
    } } }))
  }
  // Unposted-only: once posted, an entry is finalized and can't be reopened with the timer.
  const canResumeEntry = (entry) =>
    entry.status === 'unposted' && !entry.billed &&
    ((user?.role === 'admin' || user?.permissions?.is_admin) || entry.user_id === user?.id)

  // Both bulk-post buttons (selected rows, and "Post All") say how many went
  // straight to posted and how many into the reviewer's queue.
  const postManyAndToast = async (ids) => {
    const res = await api.put('/api/time-entries/post-many', { ids })
    const { message, ok } = bulkPostToast(res?.data)
    if (ok) toast.success(message); else toast.warn(message)
  }

  const handlePostSelected = async () => {
    if (selectedEntries.length === 0) return
    try {
      await postManyAndToast(selectedEntries)
      setSelectedEntries([])
      fetchMatter()
    } catch (err) {
      setError(err.response?.data?.error || 'Error posting entries')
    }
  }

// Unposting goes through PUT /unpost-many (it writes the UNPOST audit row);
// PUT /:id refuses a status change.  A 200 that unposted nothing means the
// entry was billed, already unposted, or not this user's.
const handleUnpostEntry = async (entryId) => {
    try {
      const res = await api.put('/api/time-entries/unpost-many', { ids: [entryId] })
      if (!res?.data?.unposted) setError(UNPOST_NOTHING_MSG)
      fetchMatter()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error unposting entry — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error unposting entry (HTTP ${err.response.status})`)
      }
    }
  }

  const handleDeleteEntry = async (entryId) => {
    if (!await ask({ type: 'confirm', title: 'Delete time entry?', message: 'Delete this time entry?', confirmLabel: 'Delete', danger: true })) return
    try {
      await api.delete(`/api/time-entries/${entryId}`)
      fetchMatter()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error deleting entry — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error deleting entry (HTTP ${err.response.status})`)
      }
    }
  }

  const handleTimeSubmit = async (e) => {
    e.preventDefault()
    if (!timeForm.description || timeForm.description.trim() === '') {
      setError('Please add a description of work performed before saving')
      return
    }
    try {
      await createTimeEntry({ ...timeForm, matter_id: id })
      setShowTimeForm(false)
      setTimeForm({
        date: format(new Date(), 'yyyy-MM-dd'),
        hours: '', rate: matter?.rate || '',
        description: '', billing_status: 'billable', activity_code: ''
      })
      fetchMatter()
    } catch (err) {
      // F2.2 follow-up (feedback #712) — surface the actual server error
      // instead of the historical generic "Error creating time entry".
      // The old generic message hid transient outages (API restart during
      // deploy) + also hid real validation errors (locked period, missing
      // matter, non-visible matter, etc.) — user couldn't tell whether to
      // retry OR whether their input was wrong.  Network failures show as
      // "Server unavailable — please retry" so users know a retry may work.
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error creating time entry — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error creating time entry (HTTP ${err.response.status})`)
      }
    }
  }

  const handleEventSubmit = async (e) => {
    e.preventDefault()
    try {
      // Bug #821 — eventForm.start_time/end_time come from datetime-local
      // inputs, which emit a naive firm-local string.  events.start_time is
      // UTC-in-a-naive-column, so send the converted value or we store
      // local-intent as UTC (a 7-8h error).  This form is create-only, so
      // there is no matching read-side conversion to make.
      // Feedback #819 — the matter form had no repeat control at all, so an
      // attorney working inside a matter could not create a recurring event by
      // any means.  Same translation the calendar does, via the shared helper.
      const res = await createEvent(applyRecurrenceToPayload({
        ...eventForm,
        matter_id: id,
        start_time: localInputToUtc(eventForm.start_time) || '',
        end_time: localInputToUtc(eventForm.end_time) || '',
        // Feedback #828 — this form had no reminder control at all.
        reminders: reminderRowsToPayload(eventForm.reminders),
      }))
      if (res?.data?.series_count > 1) {
        const n = res.data.series_count
        setError(res.data.series_truncated
          ? `Created ${n} occurrences (capped at the 730 safety limit — set an end date to bound the series).`
          : `Created ${n} recurring occurrences.`)
      }
      setShowEventForm(false)
      setEventForm({
        title: '', description: '', notes: '', start_time: '',
        end_time: '', event_type: 'appointment', location: '',
        sync_to_google: false, push_to_user_ids: [],
        reminders: [],
        ...emptyRecurrence,
      })
      fetchMatter()
    } catch (err) {
      // Surface the server's reason (e.g. "Hestia can't see this event, so
      // they can't receive its reminder") instead of a generic failure.
      setError(err?.response?.data?.error || 'Error creating event')
    }
  }

  const handleEditEntry = (entry) => {
  setEditingEntry(entry.id)
  setEditForm({
    date: entry.date.split('T')[0],
    hours: entry.hours,
    rate: entry.rate,
    description: entry.description,
    billing_status: entry.billing_status || 'billable',
    activity_code: entry.activity_code || ''
  })
}

const handleSaveEdit = async (entryId) => {
  if (!editForm.description || editForm.description.trim() === '') {
    setError('Please add a description of work performed')
    return
  }
  try {
    await api.put(`/api/time-entries/${entryId}`, editForm)
    setEditingEntry(null)
    fetchMatter()
  } catch (err) {
    setError('Error updating entry')
  }
}


  const handleCostSubmit = async (e) => {
    e.preventDefault()
    try {
      const payload = {
        ...costForm,
        matter_id: id,
        client_id: matter.client_id
      }
      if (editingCost) {
        await api.put(`/api/costs/${editingCost}`, payload)
        setEditingCost(null)
      } else {
        await api.post('/api/costs', payload)
      }
      setShowCostForm(false)
      setCostForm({
        date: '', quantity: '1', unit_rate: '',
        expense_code: '', description: '', vendor: '',
        reference_number: '', hard_cost: true, billing_status: 'billable'
      })
      fetchMatter()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error saving cost entry — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error saving cost entry (HTTP ${err.response.status})`)
      }
    }
  }

  const handlePostCost = async (costId) => {
    try {
      await api.put(`/api/costs/${costId}/post`)
      fetchMatter()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error posting cost — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error posting cost (HTTP ${err.response.status})`)
      }
    }
  }

  const handleDeleteCost = async (costId) => {
    if (!await ask({ type: 'confirm', title: 'Delete cost entry?', message: 'Delete this cost entry?', confirmLabel: 'Delete', danger: true })) return
    try {
      await api.delete(`/api/costs/${costId}`)
      fetchMatter()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error deleting cost — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error deleting cost (HTTP ${err.response.status})`)
      }
    }
  }

  const handleEditCost = (cost) => {
    setEditingCost(cost.id)
    setCostForm({
      date: (cost.date || '').split('T')[0],
      quantity: cost.quantity,
      unit_rate: cost.unit_rate,
      expense_code: cost.expense_code,
      description: cost.description || '',
      vendor: cost.vendor || '',
      reference_number: cost.reference_number || '',
      hard_cost: cost.hard_cost,
      billing_status: cost.billing_status
    })
    setShowCostForm(true)
  }

  const handleGenerateInvoice = async () => {
    if (selectedTimeIds.length === 0 && selectedCostIds.length === 0) {
      setError('Please select at least one time entry or cost to invoice')
      return
    }
    setGeneratingInvoice(true)
    try {
      const body = {
        matter_id: id,
        time_entry_ids: selectedTimeIds,
        cost_ids: selectedCostIds,
        notes: invoiceNotes,
        is_retainer_replenishment: isRetainerReplenishment
      }
      // Contingency matter + chargeable hourly time: the server answers 409
      // 'contingency_hourly_fee' and we ask for the written reason it will
      // record in the audit log, then resend the SAME request with it.
      // Cancel creates nothing and is not an error.
      const result = await submitWithContingencyGuard(
        ask,
        (extra) => api.post('/api/invoices/generate', { ...body, ...extra }),
        { confirmLabel: 'Create invoice' }
      )
      if (result.cancelled) return
      const res = result.response
      setShowGenerateInvoice(false)
      setSelectedTimeIds([])
      setSelectedCostIds([])
      setInvoiceNotes('')
      setIsRetainerReplenishment(false)
      fetchMatter()
      navigate(`/invoices/${res.data.id}`)
    } catch (err) {
      setError(err.response?.data?.error || 'Error generating invoice')
    } finally {
      setGeneratingInvoice(false)
    }
  }

  const handleAddNote = async (e) => {
    e.preventDefault()
    if (!newNote.trim()) return
    try {
      await api.post('/api/notes', { content: newNote, matter_id: id })
      setNewNote('')
      fetchMatter()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error adding note — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error adding note (HTTP ${err.response.status})`)
      }
    }
  }

  const handleUpdateNote = async (noteId) => {
    try {
      await api.put(`/api/notes/${noteId}`, { content: editNoteContent })
      setEditingNote(null)
      fetchMatter()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error updating note — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error updating note (HTTP ${err.response.status})`)
      }
    }
  }

  const handleDeleteNote = async (noteId) => {
    if (!await ask({ type: 'confirm', title: 'Delete note?', message: 'Delete this note?', confirmLabel: 'Delete', danger: true })) return
    try {
      await api.delete(`/api/notes/${noteId}`)
      fetchMatter()
    } catch (err) {
      if (err?.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err?.response?.status >= 500) {
        setError('Server error deleting note — please retry in a moment.')
      } else if (!err?.response) {
        setError('Cannot reach server — check your connection and retry.')
      } else {
        setError(`Error deleting note (HTTP ${err.response.status})`)
      }
    }
  }


  const toggleSelectEntry = (id) => {
    setSelectedEntries(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const statusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700'
      case 'closed': return 'bg-gray-100 text-gray-700'
      case 'pending': return 'bg-yellow-100 text-yellow-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  // billingStatusColor now lives in lib/matterTimeReports (shared with the
  // Reports page) and is imported above.

  const entryStatusColor = (status) => { // eslint-disable-line no-unused-vars
    switch (status) {
      case 'unposted': return 'bg-yellow-100 text-yellow-700'
      case 'posted': return 'bg-blue-100 text-blue-700'
      case 'billed': return 'bg-green-100 text-green-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  if (loading) return (
    <LoadingRegion label="Loading matter">
      <div className="mb-4"><div className="h-8 w-64 bg-gray-100 rounded animate-pulse" /></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <SkeletonTable rows={6} cols={5} />
      </div>
    </LoadingRegion>
  )

  if (!matter) return (
    <div className="text-center py-12">
      <p className="text-gray-500">Matter not found.</p>
      <button onClick={() => navigate('/matters')} className="mt-4 text-primary-600 hover:underline">
        Back to Matters
      </button>
    </div>
  )

const allEntries = matter.time_entries || []
const myEntries = allEntries.filter(t => t.user_id === user?.id)
const scopedEntries = (canViewAll && showAllTime) ? allEntries : myEntries

// Date filter (feedback #71): narrow scopedEntries by entry.date.
// Uses local-day comparisons (parseDateOnly) so a Pacific-timezone user
// sees "today" correctly without UTC drift.
const today = new Date(); today.setHours(0, 0, 0, 0)
const startOfWeek = new Date(today); startOfWeek.setDate(today.getDate() - today.getDay()) // Sunday
const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
const isWithinFilter = (entry) => {
  const d = parseDateOnly(entry.date)
  if (!d) return true
  if (timeDateFilter === 'today')  return d.getTime() === today.getTime()
  if (timeDateFilter === 'week')   return d >= startOfWeek
  if (timeDateFilter === 'month')  return d >= startOfMonth
  if (timeDateFilter === 'custom') {
    const from = timeDateCustom.from ? parseDateOnly(timeDateCustom.from) : null
    const to   = timeDateCustom.to   ? parseDateOnly(timeDateCustom.to)   : null
    if (from && d < from) return false
    if (to   && d > to)   return false
    return true
  }
  return true  // 'all'
}
const displayEntries = scopedEntries.filter(isWithinFilter)
const unpostedEntries = displayEntries.filter(t => t.status === 'unposted')
const postedEntries = displayEntries.filter(t => t.status === 'posted' && !t.billed)
// Feedback #837 — the Generate Invoice picker offers only what the server
// will put on an invoice (lib/matterTimeReports isInvoiceablePostedEntry, the
// mirror of the server's NOT_AMICUS_HISTORY_SQL).
const invoiceableTimeEntries = postedEntries.filter(isInvoiceablePostedEntry)
const amicusHistoryNotInvoiceableCount = postedEntries.filter(isAmicusHistoryNotInvoiceable).length
const billedEntries = displayEntries.filter(t => t.status === 'billed' || t.billed)
const filteredCount = displayEntries.length
const unfilteredCount = scopedEntries.length

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        {/* min-w-0: this block may shrink to the window, so the title row
            below wraps instead of pushing the page sideways (on one line
            the title and its buttons need at least ~546px; a 420-480px
            pop-out or phone scrolled sideways by up to 140px). */}
        <div className="min-w-0" data-testid="matter-header-main">
          <div className="flex items-center gap-2 mb-1">
            <button onClick={() => navigate('/matters')} className="text-gray-400 hover:text-gray-600 text-sm">
              ← Matters
            </button>
          </div>
          {/* Title + ⤴ 🔗 ☆ and the PDF buttons.  Below 1024px the row
              wraps: the title takes its own line(s) and the buttons flow
              underneath at full size.  From 1024px up it stays one row as
              before (a long title shares the line and the buttons narrow),
              so wide windows look the same. */}
          <div className="flex flex-wrap lg:flex-nowrap items-center gap-2" data-testid="matter-title-row">
            <h2 className="text-2xl font-bold text-gray-800 min-w-0 break-words">{matter.title}</h2>
            <PopOutPageButton title={matter.title} width={1300} height={900} />
            <CopyLinkButton title={`Copy link to matter: ${matter.title}`} />
            <button
              type="button"
              onClick={handleTogglePin}
              title={pinned ? 'Unpin from sidebar Pinned Matters section' : 'Pin to sidebar so this matter sticks around'}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                pinned
                  ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
              }`}>
              {pinned ? '⭐ Pinned' : '☆ Pin'}
            </button>
            {/* Ship 7b F2.3.6 — Amicus SystemAdministration "Selected File"
                parity: 3 drilldown PDFs.  Kept as 3 separate buttons per
                caller's design contract ("attorneys usually want one at a
                time").  Each button disables during in-flight generation to
                prevent double-click producing two PDFs. */}
            <button
              type="button"
              onClick={handlePrintEventsPDF}
              disabled={printingPDF !== null}
              title="Print all events + deadlines for this matter as a PDF (Amicus SystemAdministration parity)"
              className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-wait transition-colors">
              {printingPDF === 'events' ? '⏳ Events…' : '📅 Events PDF'}
            </button>
            <button
              type="button"
              onClick={handlePrintTimePDF}
              disabled={printingPDF !== null}
              title="Print time entries as a PDF.  Matches the on-screen My/All + date-range filter you have set below."
              className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-wait transition-colors">
              {printingPDF === 'time' ? '⏳ Time…' : '⏱ Time PDF'}
            </button>
            <button
              type="button"
              onClick={handlePrintContactsPDF}
              disabled={printingPDF !== null}
              title="Print all people / contacts linked to this matter as a PDF"
              className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-wait transition-colors">
              {printingPDF === 'contacts' ? '⏳ People…' : '📇 People PDF'}
            </button>
            {/* Ship 7i — Master Witness List (internal work-product) +
                §2034.260 Expert Designation (court filing).  Master List is
                always available; Expert Designation prompts a useAsk()
                ceremony because it produces a §2034.300-liability document. */}
            <button
              type="button"
              onClick={handlePrintWitnessListPDF}
              disabled={printingPDF !== null}
              title="Print the Master Witness List for this matter (all types: retained/non-retained experts, percipient, fact).  Landscape.  Internal work-product — NOT a court filing."
              className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-wait transition-colors">
              {printingPDF === 'witnesses' ? '⏳ Witnesses…' : '🖨 Master Witness List'}
            </button>
            <button
              type="button"
              onClick={handlePrintExpertDesignationPDF}
              disabled={printingPDF !== null}
              title="Build the CCP §2034.260 Expert Witness Designation as a DRAFT PDF.  Court filing carrying §2034.300 exclusion-sanction risk — every retained-expert field is checked before build + missing fields surface as bracketed placeholders.  DRAFT watermark on every page until reviewed."
              className="text-xs px-2 py-1 rounded border border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300 disabled:opacity-50 disabled:cursor-wait transition-colors">
              {printingPDF === 'expertDesignation' ? '⏳ Designation…' : '⚖️ §2034.260 Expert Designation'}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
            {matter.file_number && (
              <span className="text-sm text-gray-500 font-mono">#{matter.file_number}</span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(matter.status)}`}>
              {matter.status}
            </span>
            {matter.practice_area && (
              <span className="text-sm text-gray-500">{matter.practice_area}</span>
            )}
            {matter.visibility === 'specific' && (
              <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-medium" title={`Restricted to ${(matter.visible_to_users || []).length} user(s) and ${(matter.visible_to_groups || []).length} group(s) (plus admins and the responsible attorney)`}>
                🔒 Restricted: {(matter.visible_to_users || []).length} user{(matter.visible_to_users || []).length === 1 ? '' : 's'}, {(matter.visible_to_groups || []).length} group{(matter.visible_to_groups || []).length === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap justify-end items-center gap-3">
          {matter.status !== 'closed' && (isUserAdmin || user?.permissions?.can_create_matters) && (
            <button onClick={handleOpenCloseModal}
              className="px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
              Close Matter
            </button>
          )}
          {matter.status === 'closed' && isUserAdmin && (
            <button onClick={handleReopenMatter}
              className="px-3 py-1.5 text-sm font-medium border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
              Reopen Matter
            </button>
          )}
          {matter.client_id ? (
            <Link to={`/clients/${matter.client_id}`} className="text-right hover:opacity-80">
              <p className="text-sm text-gray-500">Client</p>
              {/* displayClientForMatterRow: a company client has no first or
                  last name, so "{first_name} {last_name}" rendered blank. */}
              <p className="font-semibold text-primary-600">{displayClientForMatterRow(matter)}</p>
            </Link>
          ) : (
            <div className="text-right">
              <p className="text-sm text-gray-500">Client</p>
              <p className="font-semibold text-amber-600" title="This matter has no client linked. Edit the matter to assign one.">⚠️ Unassigned</p>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Account Overview — like Amicus billing summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">Unposted</p>
          <p className="text-lg font-bold text-yellow-600">
            {matter.summary?.unposted_hours?.toFixed(1) || '0.0'} hrs
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">Posted Hours</p>
          <p className="text-lg font-bold text-blue-600">
            {postedEntries.reduce((sum, e) => sum + parseFloat(e.hours), 0).toFixed(2)} hrs
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {showAllTime && canViewAll ? 'All attorneys' : 'My time only'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">WIP (unbilled)</p>
          <p className="text-lg font-bold text-blue-600">
            ${displayEntries
                .filter(t => t.status === 'posted' && !t.billed && t.billing_status === 'billable')
                .reduce((sum, t) => sum + (parseFloat(t.hours) * parseFloat(t.rate)), 0)
                .toFixed(2)}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {showAllTime ? 'All attorneys' : 'My time only'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">Total Costs</p>
          <p className="text-lg font-bold text-orange-600">
            ${parseFloat(matter.summary?.total_costs || 0).toFixed(2)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">Total Billed</p>
          <p className="text-lg font-bold text-gray-800">
            ${parseFloat(matter.summary?.total_billed || 0).toFixed(2)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">Total Paid</p>
          <p className="text-lg font-bold text-green-600">
            ${parseFloat(matter.summary?.total_paid || 0).toFixed(2)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">AR Balance</p>
          <p className={`text-lg font-bold ${parseFloat(matter.summary?.ar_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
            ${parseFloat(matter.summary?.ar_balance || 0).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex gap-2 mb-4 items-center">
        <button
          onClick={() => setActiveTab('attorney')}
          className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            activeTab === 'attorney'
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          ATTORNEY
        </button>
        {canSeeBilling && (
          <button
            onClick={() => setActiveTab('billing')}
            className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'billing'
                ? 'bg-primary-600 text-white'
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            BILLING
          </button>
        )}
        {canSeeBilling && (
          <Link to="/billing"
            className="ml-auto text-xs text-primary-600 hover:text-primary-800 hover:underline font-medium">
            View All Billing →
          </Link>
        )}
      </div>

      {/* Sticky mini-nav — quick-jump chips for each accordion on the
          current tab.  Long matter pages have 7+ sections per tab; scrolling
          to the one you want is annoying.  Chips scroll horizontally on
          mobile so they never wrap into multiple rows. */}
      {(() => {
        const attorneyChips = [
          { id: 'time',          label: '⏱ Time' },
          { id: 'events',        label: '📅 Events' },
          { id: 'notes',         label: '📝 Notes' },
          isFeatureOn('communications') && { id: 'communications', label: '📞 Comms' },
          isFeatureOn('discovery')      && { id: 'discovery',      label: '🔍 Discovery' },
          isFeatureOn('case_analysis')  && { id: 'caseAnalysis',   label: '🧩 Case Analysis' },
          isFeatureOn('sol_tracker')    && { id: 'sol',            label: '⏳ SOL' },
          { id: 'contacts',      label: '📇 Contacts' },
          isFeatureOn('witnesses')      && { id: 'witnesses',      label: '🎤 Witnesses' },
          { id: 'documents',     label: '📂 Docs' },
          { id: 'details',       label: '📋 Details' },
        ].filter(Boolean)
        const billingChips = [
          canViewInvoices && { id: 'invoices', label: '💰 Invoices' },
          canViewTrust    && { id: 'trust',    label: '🏦 Trust' },
          canViewCosts    && { id: 'costs',    label: '📎 Costs' },
        ].filter(Boolean)
        const chips = activeTab === 'attorney' ? attorneyChips : billingChips
        if (chips.length <= 1) return null
        return (
          <div className={matterChipBarClass(typeof window !== 'undefined' ? window.location.pathname : '')}
            data-testid="matter-chip-bar">
            <div className="flex flex-nowrap gap-2 min-w-max">
              {chips.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    const el = document.getElementById(c.id)
                    if (!el) return
                    // Open the section if it's collapsed so the user actually
                    // sees the content after the jump.  Discovery has its own
                    // internal state so skip the toggle for it.  Communications
                    // uses a different key ('comms') than its DOM id.
                    const openKey = c.id === 'communications' ? 'comms' : c.id
                    if (c.id !== 'discovery' && openSections[openKey] === false) {
                      toggleSection(openKey)
                    }
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-white border border-gray-300 hover:bg-primary-50 hover:border-primary-300 text-gray-700 whitespace-nowrap transition-colors">
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )
      })()}

      {/* Time Entries Section - ATTORNEY tab */}
      {activeTab === 'attorney' && <div id="time" className="mb-3">
        <button onClick={() => toggleSection('time')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-blue-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.time ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">⏱ Time Entries</span>
            {unpostedEntries.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">{unpostedEntries.length} unposted</span>}
            {postedEntries.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{postedEntries.length} posted</span>}
          </div>
        </button>
        {openSections.time && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Time Entries
            <span className="ml-2 text-sm text-gray-400">
              ({timeDateFilter === 'all' ? unfilteredCount : `${filteredCount} of ${unfilteredCount}`})
            </span>
          </h3>
          <div className="flex gap-2">
  {canViewAll && (
    <button
      onClick={() => setShowAllTime(!showAllTime)}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
        showAllTime
          ? 'bg-gray-700 text-white border-gray-700'
          : 'bg-white text-gray-700 border-gray-300'
      }`}
    >
      {showAllTime ? 'My Time Only' : 'View All Time'}
    </button>
  )}
  {selectedEntries.length > 0 && (
    <button onClick={handlePostSelected}
      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
      Post Selected ({selectedEntries.length})
    </button>
  )}
  <button onClick={() => window.dispatchEvent(new CustomEvent('open-time-entry-popup', { detail: { matter_id: id, rate: matter?.rate || '' } }))}
    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
    + Add Time
  </button>
</div>
        </div>

        {/* Date filter pills (feedback #71 — Darian).  Choice persists per user
            via localStorage so picking "Today" once means future matters open
            filtered to today too. */}
        <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mr-1">Date filter:</span>
          {[
            { id: 'all',    label: 'All time' },
            { id: 'today',  label: 'Today' },
            { id: 'week',   label: 'This week' },
            { id: 'month',  label: 'This month' },
            { id: 'custom', label: 'Custom range' },
          ].map(opt => (
            <button key={opt.id}
              onClick={() => setTimeDateFilter(opt.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors border ${
                timeDateFilter === opt.id
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}>
              {opt.label}
            </button>
          ))}
          {timeDateFilter === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <input type="date" value={timeDateCustom.from}
                onChange={e => setTimeDateCustom({ ...timeDateCustom, from: e.target.value })}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <span className="text-xs text-gray-500">to</span>
              <input type="date" value={timeDateCustom.to}
                onChange={e => setTimeDateCustom({ ...timeDateCustom, to: e.target.value })}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          )}
          {timeDateFilter !== 'all' && filteredCount === 0 && unfilteredCount > 0 && (
            <span className="text-xs text-gray-400 ml-2">No entries match. <button onClick={() => setTimeDateFilter('all')} className="text-primary-600 hover:underline">Clear filter</button></span>
          )}
        </div>

        {/* Timer — one click starts timing on this matter (startMatterTimer). */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={startMatterTimer}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors">
              ⏱ Start timer
            </button>
            <p className="text-xs text-gray-500">Starts timing this matter at once — add the description before you save.</p>
          </div>
        </div>

        {showTimeForm && (
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <form onSubmit={handleTimeSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor={fid('te-date')} className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                <input id={fid('te-date')} type="date" required value={timeForm.date}
                  onChange={e => setTimeForm({...timeForm, date: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('te-hours')} className="block text-xs font-medium text-gray-700 mb-1">Hours</label>
                <input id={fid('te-hours')} type="number" step="0.1" required value={timeForm.hours}
                  onChange={e => setTimeForm({...timeForm, hours: e.target.value})}
                  placeholder="1.5"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('te-rate')} className="block text-xs font-medium text-gray-700 mb-1">Rate ($/hr)</label>
                <input id={fid('te-rate')} type="number" required value={timeForm.rate}
                  onChange={e => setTimeForm({...timeForm, rate: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('te-activity')} className="block text-xs font-medium text-gray-700 mb-1">Activity</label>
                <select id={fid('te-activity')} value={timeForm.activity_code}
                  onChange={e => setTimeForm({...timeForm, activity_code: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select activity</option>
                  {ACTIVITY_CODES.map(ac => (
                    <option key={ac.code} value={ac.code}>{ac.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={fid('te-billing')} className="block text-xs font-medium text-gray-700 mb-1">Billing Status</label>
                <select id={fid('te-billing')} value={timeForm.billing_status}
                  onChange={e => setTimeForm({...timeForm, billing_status: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="billable">Billable</option>
                  <option value="non-billable">Non-Billable</option>
                  <option value="no-charge-show">No Charge - Show on Bill</option>
                  <option value="no-charge-hide">No Charge - Don't Show on Bill</option>
                </select>
              </div>
              <div>
                <label htmlFor={fid('te-description')} className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input id={fid('te-description')} type="text" required value={timeForm.description}
                  onChange={e => setTimeForm({...timeForm, description: e.target.value})}
                  placeholder="Work performed..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="md:col-span-3 flex gap-2">
                <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium">Save Entry</button>
                <button type="button" onClick={() => setShowTimeForm(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="p-6">
          {/* Unposted */}
          {unpostedEntries.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">
                  UNPOSTED — {unpostedEntries.length} entries
                </span>
                <button onClick={async () => {
                  const ids = unpostedEntries
                    .filter(e => e.matter_id && e.description)
                    .map(e => e.id)
                  if (ids.length === 0) {
                    setError('All entries need a matter and description before posting')
                    return
                  }
                  try {
                    await postManyAndToast(ids)
                    fetchMatter()
                  } catch (err) {
                    setError(err.response?.data?.error || 'Error posting entries')
                  }
                }} className="text-xs text-blue-600 hover:underline font-medium">Post All</button>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-100 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-yellow-50 sticky top-0 z-10">
                  <tr>
                    <th className="w-8 px-3 py-2"></th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Time</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Attorney</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Description</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Activity</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Billing</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Hours</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Amount</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {unpostedEntries.map(entry => (
  editingEntry === entry.id ? (
    <tr key={entry.id} className="bg-yellow-50">
      <td className="px-3 py-2"></td>
      <td className="px-3 py-2">
        <input type="date" aria-label="Date" value={editForm.date}
          onChange={e => setEditForm({...editForm, date: e.target.value})}
          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
        />
      </td>
      {/* Start time is shown, not edited, here (edit it on Time Entries or in the timer); the PUT leaves it alone. */}
      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap tabular-nums">{formatStartTime(entry.start_time) || '—'}</td>
      <td className="px-3 py-2 text-xs text-gray-500">{entry.attorney_name || '—'}</td>
      <td className="px-3 py-2 align-top">
        <textarea aria-label="Description" value={editForm.description}
          onChange={e => setEditForm({...editForm, description: e.target.value})}
          rows={3}
          placeholder="Full description — drag corner to resize"
          className="w-full min-w-[260px] border border-gray-300 rounded px-2 py-1 text-xs resize-y leading-snug focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </td>
      <td className="px-3 py-2">
        <select aria-label="Activity code" value={editForm.activity_code}
          onChange={e => setEditForm({...editForm, activity_code: e.target.value})}
          className="w-full min-w-[180px] border border-gray-300 rounded px-2 py-1 text-xs"
        >
          <option value="">—</option>
          {ACTIVITY_CODES.map(ac => (
                    <option key={ac.code} value={ac.code}>{ac.label}</option>
                  ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <select aria-label="Billing status" value={editForm.billing_status}
          onChange={e => setEditForm({...editForm, billing_status: e.target.value})}
          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
        >
          <option value="billable">Billable</option>
          <option value="non-billable">Non-Billable</option>
          <option value="no-charge-show">No Charge - Show on Bill</option>
          <option value="no-charge-hide">No Charge - Don't Show on Bill</option>
        </select>
      </td>
      <td className="px-3 py-2">
        <input type="number" step="0.1" aria-label="Hours" value={editForm.hours}
          onChange={e => setEditForm({...editForm, hours: e.target.value})}
          className="w-full border border-gray-300 rounded px-2 py-1 text-xs text-right"
        />
      </td>
      <td className="px-3 py-2 text-right text-xs font-medium">
        ${(parseFloat(editForm.hours || 0) * parseFloat(editForm.rate || 0)).toFixed(2)}
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <button onClick={() => handleSaveEdit(entry.id)}
            className="text-xs text-green-600 hover:underline">Save</button>
          <button onClick={() => setEditingEntry(null)}
            className="text-xs text-gray-500 hover:underline">Cancel</button>
        </div>
      </td>
    </tr>
  ) : (
    // Row click → reopen the timer popup with this entry pre-loaded
    // (only when the entry can be resumed).  Matches Amicus's "click the
    // entry to keep tracking" workflow.  Action buttons + checkbox stop
    // propagation so they still work.
    <tr key={entry.id}
        onClick={canResumeEntry(entry) ? () => handleResumeEntry(entry) : undefined}
        className={`hover:bg-gray-50 ${canResumeEntry(entry) ? 'cursor-pointer' : ''}`}
        title={canResumeEntry(entry) ? 'Click to reopen this entry in the timer and continue tracking' : undefined}>
      <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
        <input type="checkbox"
          checked={selectedEntries.includes(entry.id)}
          onChange={() => toggleSelectEntry(entry.id)}
        />
      </td>
      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
        {format(parseDateOnly(entry.date), 'MMM d, yyyy')}
      </td>
      <td className="px-3 py-2 text-gray-600 whitespace-nowrap tabular-nums">{formatStartTime(entry.start_time) || <span className="text-gray-300">—</span>}</td>
      <td className="px-3 py-2 text-gray-500 text-xs">{entry.attorney_name || '—'}</td>
      <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{entry.description}</td>
      <td className="px-3 py-2 text-gray-500 text-xs">{getActivityLabel(entry.activity_code)}</td>
      <td className="px-3 py-2">
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${billingStatusColor(entry.billing_status)}`}>
          {entry.billing_status === 'no-charge-show' ? 'No Charge (Show)' :
           entry.billing_status === 'no-charge-hide' ? 'No Charge (Hide)' :
           entry.billing_status === 'non-billable' ? 'Non-Billable' :
           'Billable'}
        </span>
      </td>
      <td className="px-3 py-2 text-right">{roundBillable(entry.hours).toFixed(1)}</td>
      <td className="px-3 py-2 text-right font-medium">
        ${(roundBillable(entry.hours) * parseFloat(entry.rate)).toFixed(2)}
      </td>
      <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          {/* Feedback #833 — Post is a real button (first), matching the Time
              Entries page; it used to be a faint link that read as row text. */}
          {((user?.role === 'admin' || user?.permissions?.is_admin) || entry.user_id === user?.id) && (
            <button type="button" onClick={() => handlePostEntry(entry.id)}
              title="Post this entry (locks it for billing)"
              className="text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 rounded">Post</button>
          )}
          {((user?.role === 'admin' || user?.permissions?.is_admin) || entry.user_id === user?.id) && (
            <button onClick={() => handleEditEntry(entry)}
              className="text-xs text-blue-600 hover:underline">Edit</button>
          )}
          {((user?.role === 'admin' || user?.permissions?.is_admin) || entry.user_id === user?.id) && (
            <button onClick={() => handleDeleteEntry(entry.id)}
              className="text-xs text-red-500 hover:underline">Delete</button>
          )}
        </div>
      </td>
    </tr>
  )
))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Posted */}
          {postedEntries.length > 0 && (
            <div className="mb-6">
              <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                {/* Not "(WIP)": this list also holds posted no-charge and
                    non-billable time, which is not work in progress (#837
                    round 2).  The WIP tile above counts Billable time only. */}
                POSTED — {postedEntries.length} entries
              </span>
              <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-100 rounded-lg">
              <table className="w-full text-sm mt-2">
                <thead className="bg-blue-50 sticky top-0 z-10">
  <tr>
    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Time</th>
    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Attorney</th>
    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Description</th>
    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Activity</th>
    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Billing</th>
    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Hours</th>
    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Amount</th>
    <th className="px-3 py-2"></th>
  </tr>
</thead>
                <tbody className="divide-y divide-gray-100">
                  {postedEntries.map(entry => (
  editingEntry === entry.id ? (
    <tr key={entry.id} className="bg-blue-50">
      <td className="px-3 py-2">
        <input type="date" aria-label="Date" value={editForm.date}
          onChange={e => setEditForm({...editForm, date: e.target.value})}
          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
        />
      </td>
      {/* Start time is shown, not edited, here (edit it on Time Entries or in the timer); the PUT leaves it alone. */}
      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap tabular-nums">{formatStartTime(entry.start_time) || '—'}</td>
      <td className="px-3 py-2 text-xs text-gray-500">{entry.attorney_name || '—'}</td>
      <td className="px-3 py-2 align-top">
        <textarea aria-label="Description" value={editForm.description}
          onChange={e => setEditForm({...editForm, description: e.target.value})}
          rows={3}
          placeholder="Full description — drag corner to resize"
          className="w-full min-w-[260px] border border-gray-300 rounded px-2 py-1 text-xs resize-y leading-snug focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </td>
      <td className="px-3 py-2">
        <select aria-label="Activity code" value={editForm.activity_code}
          onChange={e => setEditForm({...editForm, activity_code: e.target.value})}
          className="w-full min-w-[180px] border border-gray-300 rounded px-2 py-1 text-xs"
        >
          <option value="">—</option>
          {ACTIVITY_CODES.map(ac => (
                    <option key={ac.code} value={ac.code}>{ac.label}</option>
                  ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <select aria-label="Billing status" value={editForm.billing_status}
          onChange={e => setEditForm({...editForm, billing_status: e.target.value})}
          className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
        >
          <option value="billable">Billable</option>
          <option value="non-billable">Non-Billable</option>
          <option value="no-charge-show">No Charge - Show on Bill</option>
          <option value="no-charge-hide">No Charge - Don't Show on Bill</option>
        </select>
      </td>
      <td className="px-3 py-2">
        <input type="number" step="0.1" aria-label="Hours" value={editForm.hours}
          onChange={e => setEditForm({...editForm, hours: e.target.value})}
          className="w-full border border-gray-300 rounded px-2 py-1 text-xs text-right"
        />
      </td>
      <td className="px-3 py-2 text-right text-xs font-medium">
        ${(parseFloat(editForm.hours || 0) * parseFloat(editForm.rate || 0)).toFixed(2)}
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <button onClick={() => handleSaveEdit(entry.id)}
            className="text-xs text-green-600 hover:underline">Save</button>
          <button onClick={() => setEditingEntry(null)}
            className="text-xs text-gray-500 hover:underline">Cancel</button>
        </div>
      </td>
    </tr>
  ) : (
    <tr key={entry.id} className="hover:bg-gray-50">
      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
        {format(parseDateOnly(entry.date), 'MMM d, yyyy')}
      </td>
      <td className="px-3 py-2 text-gray-600 whitespace-nowrap tabular-nums">{formatStartTime(entry.start_time) || <span className="text-gray-300">—</span>}</td>
      <td className="px-3 py-2 text-gray-500 text-xs">{entry.attorney_name || '—'}</td>
      <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{entry.description}</td>
      <td className="px-3 py-2 text-gray-500 text-xs">{getActivityLabel(entry.activity_code)}</td>
      <td className="px-3 py-2">
        <span className={`px-1.5 py-0.5 rounded text-xs ${billingStatusColor(entry.billing_status)}`}>
          {entry.billing_status}
        </span>
      </td>
      <td className="px-3 py-2 text-right">{roundBillable(entry.hours).toFixed(1)}</td>
      <td className="px-3 py-2 text-right font-medium">
        {entry.billing_status === 'billable'
          ? `$${(roundBillable(entry.hours) * parseFloat(entry.rate)).toFixed(2)}`
          : '—'}
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-2">
          <button onClick={() => handleEditEntry(entry)}
            className="text-xs text-blue-600 hover:underline">Edit</button>
          <button onClick={() => handleUnpostEntry(entry.id)}
            className="text-xs text-yellow-600 hover:underline">Unpost</button>
          {((user?.role === 'admin' || user?.permissions?.is_admin) || entry.user_id === user?.id) && (
            <button onClick={() => handleDeleteEntry(entry.id)}
              className="text-xs text-red-500 hover:underline">Delete</button>
          )}
        </div>
      </td>
    </tr>
  )
))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Hours Summary */}
          {displayEntries.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              {/* overflow-x-auto: the time card clips (overflow-hidden, for
                  its rounded corners), so a table wider than a narrow
                  pop-out must scroll here or its last column is cut off
                  with no scrollbar (tableOverflow.test.js). */}
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500"></th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Hours</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Fees</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-3 py-2 text-sm text-gray-600">Billable</td>
                    <td className="px-3 py-2 text-right text-sm font-medium text-gray-800">
                      {displayEntries
                        .filter(e => billingKey(e.billing_status) === 'billable')
                        .reduce((sum, e) => sum + parseFloat(e.hours), 0)
                        .toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-sm font-medium text-gray-800">
                      ${displayEntries
                        .filter(e => billingKey(e.billing_status) === 'billable')
                        .reduce((sum, e) => sum + (parseFloat(e.hours) * parseFloat(e.rate)), 0)
                        .toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-sm text-gray-600">Non-Billable</td>
                    <td className="px-3 py-2 text-right text-sm font-medium text-gray-800">
                      {displayEntries
                        .filter(e => billingKey(e.billing_status) === 'non-billable')
                        .reduce((sum, e) => sum + parseFloat(e.hours), 0)
                        .toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-sm text-gray-400">—</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-sm text-gray-600">No Charge</td>
                    <td className="px-3 py-2 text-right text-sm font-medium text-gray-800">
                      {displayEntries
                        // Both no-charge statuses ('no-charge-show' and
                        // 'no-charge-hide').  The old filter matched the value
                        // 'no-charge', which never exists, so every no-charge
                        // hour was missing from this breakdown.
                        .filter(e => billingKey(e.billing_status).startsWith('no-charge'))
                        .reduce((sum, e) => sum + parseFloat(e.hours), 0)
                        .toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-sm text-gray-400">—</td>
                  </tr>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-3 py-2 text-sm text-gray-800">Total Posted</td>
                    <td className="px-3 py-2 text-right text-sm text-gray-800">
                      {postedEntries
                        .reduce((sum, e) => sum + roundBillable(e.hours), 0)
                        .toFixed(1)}
                    </td>
                    <td className="px-3 py-2 text-right text-sm text-gray-800">
                      ${postedEntries
                        .filter(e => e.billing_status === 'billable')
                        .reduce((sum, e) => sum + (roundBillable(e.hours) * parseFloat(e.rate)), 0)
                        .toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>

              {/* Per-attorney breakdown (feedback #64) */}
              {(() => {
                const byAttorney = {}
                for (const e of displayEntries) {
                  const k = e.attorney_name || '(unassigned)'
                  if (!byAttorney[k]) byAttorney[k] = { billable: 0, nonBillable: 0, noCharge: 0, fees: 0 }
                  const h = roundBillable(e.hours)
                  const bk = billingKey(e.billing_status)   // null/blank = billable (DB default)
                  if (bk === 'billable')          { byAttorney[k].billable    += h; byAttorney[k].fees += h * parseFloat(e.rate) }
                  else if (bk === 'non-billable') byAttorney[k].nonBillable += h
                  else                                          byAttorney[k].noCharge    += h
                }
                const rows = Object.entries(byAttorney).sort((a, b) => a[0].localeCompare(b[0]))
                if (rows.length === 0) return null
                const t = rows.reduce((acc, [, v]) => ({
                  billable: acc.billable + v.billable,
                  nonBillable: acc.nonBillable + v.nonBillable,
                  noCharge: acc.noCharge + v.noCharge,
                  fees: acc.fees + v.fees,
                }), { billable: 0, nonBillable: 0, noCharge: 0, fees: 0 })
                return (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">Hours by Attorney</p>
                    {/* Six columns: scroll inside the clipping time card. */}
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Attorney</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Billable</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Non-Billable</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">No Charge</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Total Hrs</th>
                          <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Billable Fees</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rows.map(([name, v]) => (
                          <tr key={name}>
                            <td className="px-3 py-2 text-sm text-gray-700">{name}</td>
                            <td className="px-3 py-2 text-right text-sm text-gray-800">{v.billable.toFixed(1)}</td>
                            <td className="px-3 py-2 text-right text-sm text-gray-800">{v.nonBillable.toFixed(1)}</td>
                            <td className="px-3 py-2 text-right text-sm text-gray-800">{v.noCharge.toFixed(1)}</td>
                            <td className="px-3 py-2 text-right text-sm font-medium text-gray-800">{(v.billable + v.nonBillable + v.noCharge).toFixed(1)}</td>
                            <td className="px-3 py-2 text-right text-sm text-gray-800">${v.fees.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-3 py-2 text-sm text-gray-800">Total</td>
                          <td className="px-3 py-2 text-right text-sm text-gray-800">{t.billable.toFixed(1)}</td>
                          <td className="px-3 py-2 text-right text-sm text-gray-800">{t.nonBillable.toFixed(1)}</td>
                          <td className="px-3 py-2 text-right text-sm text-gray-800">{t.noCharge.toFixed(1)}</td>
                          <td className="px-3 py-2 text-right text-sm text-gray-800">{(t.billable + t.nonBillable + t.noCharge).toFixed(1)}</td>
                          <td className="px-3 py-2 text-right text-sm text-gray-800">${t.fees.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* Billed Entries */}
          {billedEntries.length > 0 && (
            <div className="mb-6">
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                BILLED — {billedEntries.length} entries
              </span>
              <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-100 rounded-lg">
              <table className="w-full text-sm mt-2">
                <thead className="bg-green-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Time</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Attorney</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Description</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Activity</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Billing</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Hours</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Amount</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 opacity-75">
                  {billedEntries.map(entry => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {format(parseDateOnly(entry.date), 'MMM d, yyyy')}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap tabular-nums">{formatStartTime(entry.start_time) || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{entry.attorney_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{entry.description}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{getActivityLabel(entry.activity_code)}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${billingStatusColor(entry.billing_status)}`}>
                          {billingInfo(entry.billing_status).label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{roundBillable(entry.hours).toFixed(1)}</td>
                      {/* What was CHARGED: a Billable line on an Omnia invoice.
                          Time billed at no charge (incl. Amicus $0 bills)
                          shows N/C — never its face value. */}
                      <td className="px-3 py-2 text-right font-medium" title={billedTooltip(entry)}>
                        {isChargedBilledEntry(entry)
                          ? fmtMoney(chargedCents(entry))
                          : <span className="text-gray-400 text-xs">N/C</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700 cursor-help"
                          title={billedTooltip(entry)}>
                          billed
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan="6" className="px-3 py-2 text-sm font-semibold text-gray-700">Total Billed</td>
                    <td className="px-3 py-2 text-right font-bold">
                      {fmtHours(billedEntries.reduce((sum, e) => sum + hoursTenths(e.hours), 0))}
                    </td>
                    {/* Σ charged — the same rule as each row above. */}
                    <td className="px-3 py-2 text-right font-bold">
                      {fmtMoney(billedEntries.reduce((sum, e) => sum + chargedCents(e), 0))}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              </div>
            </div>
          )}

          {matter.time_entries?.length === 0 && (
            <EmptyState
              className="py-6"
              icon="⏱"
              title="No time entries yet"
              description="Start logging billable work for this matter."
              action={{
                label: '⏱ Start timer',
                onClick: startMatterTimer
              }}
            />
          )}
        </div>
      </div>}
      </div>

      }

      {/* Feedback #503 — per-matter attorney rates card.  Shows matter-wide
          default + per-attorney overrides.  Admin/can_edit_matters can add
          rows; matter's responsible_attorney can too. */}
      {activeTab === 'billing' && (
        <MatterRatesCard
          matterId={matter.id}
          canEdit={user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_edit_matters || user?.permissions?.can_create_matters}
        />
      )}

      {activeTab === 'billing' && canViewInvoices && (
      <div id="invoices" className="mb-3">
        <button onClick={() => toggleSection('invoices')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-yellow-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.invoices ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">💰 Invoices</span>
            {matter.invoices?.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">{matter.invoices.length}</span>}
          </div>
        </button>
        {openSections.invoices && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Invoices
            <span className="ml-2 text-sm text-gray-400">({matter.invoices?.length || 0})</span>
          </h3>
          {((user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_create_invoices) && (
            <button
              onClick={() => setShowGenerateInvoice(!showGenerateInvoice)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              + Generate Invoice
            </button>
          )}
        </div>

        {showGenerateInvoice && (
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Generate Invoice</h4>
            {/* Feedback #837 seam: offer ONLY what the server will invoice.
                The repair posts never-billed no-charge / cancelled Amicus
                history so it stays visible, but /generate leaves it off every
                invoice (Cal. Rule 1.5) — listing it here at face value, under
                "Select All", promised lines the invoice would not carry. */}
            {invoiceableTimeEntries.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Posted Time Entries (unbilled)</p>
                <div className="space-y-1">
                  {invoiceableTimeEntries.map(entry => (
                    <label key={entry.id} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                      <input type="checkbox"
                        checked={selectedTimeIds.includes(entry.id)}
                        onChange={e => {
                          if (e.target.checked) setSelectedTimeIds([...selectedTimeIds, entry.id])
                          else setSelectedTimeIds(selectedTimeIds.filter(i => i !== entry.id))
                        }}
                        className="rounded"
                      />
                      <span className="text-xs text-gray-500 whitespace-nowrap">{format(parseDateOnly(entry.date), 'MMM d')}</span>
                      <span className="text-sm text-gray-700 flex-1">{entry.description}</span>
                      <span className="text-xs text-gray-500">{roundBillable(entry.hours).toFixed(1)} hrs</span>
                      {/* What this line will charge — the same rule as the
                          Total below and the server (Billable only); a
                          no-charge or non-billable line prints N/C, never its
                          face value. */}
                      <span className="text-sm font-medium">
                        {entry.billing_status === 'billable'
                          ? `$${(roundBillable(entry.hours) * parseFloat(entry.rate)).toFixed(2)}`
                          : <span className="text-gray-400 text-xs">N/C</span>}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => setSelectedTimeIds(invoiceableTimeEntries.map(e => e.id))}
                    className="text-xs text-primary-600 hover:underline">Select All</button>
                  <button onClick={() => setSelectedTimeIds([])}
                    className="text-xs text-gray-500 hover:underline">Clear</button>
                </div>
              </div>
            )}
            {matter.costs?.filter(c => c.status === 'posted' && !c.billed).length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Posted Costs (unbilled)</p>
                <div className="space-y-1">
                  {matter.costs.filter(c => c.status === 'posted' && !c.billed).map(cost => (
                    <label key={cost.id} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                      <input type="checkbox"
                        checked={selectedCostIds.includes(cost.id)}
                        onChange={e => {
                          if (e.target.checked) setSelectedCostIds([...selectedCostIds, cost.id])
                          else setSelectedCostIds(selectedCostIds.filter(i => i !== cost.id))
                        }}
                        className="rounded"
                      />
                      <span className="text-xs text-gray-500 whitespace-nowrap">{format(parseDateOnly(cost.date), 'MMM d')}</span>
                      <span className="text-sm text-gray-700 flex-1">{cost.expense_code}</span>
                      <span className="text-sm text-gray-600">{cost.description}</span>
                      <span className="text-sm font-medium">${parseFloat(cost.amount).toFixed(2)}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => setSelectedCostIds(matter.costs.filter(c => c.status === 'posted' && !c.billed).map(c => c.id))}
                    className="text-xs text-primary-600 hover:underline">Select All</button>
                  <button onClick={() => setSelectedCostIds([])}
                    className="text-xs text-gray-500 hover:underline">Clear</button>
                </div>
              </div>
            )}
            {amicusHistoryNotInvoiceableCount > 0 && (
              <p className="text-xs text-gray-500 mb-4">
                {amicusHistoryNotInvoiceableCount} posted {amicusHistoryNotInvoiceableCount === 1 ? 'entry' : 'entries'} imported
                from Amicus ({amicusHistoryNotInvoiceableCount === 1 ? 'it was' : 'they were'} recorded as no charge, cancelled, or already
                billed in Amicus) {amicusHistoryNotInvoiceableCount === 1 ? 'is' : 'are'} kept on record but never invoiced, so {amicusHistoryNotInvoiceableCount === 1 ? 'it is' : 'they are'} not listed here.
              </p>
            )}
            {invoiceableTimeEntries.length === 0 && matter.costs?.filter(c => c.status === 'posted' && !c.billed).length === 0 && (
              <p className="text-sm text-gray-500 mb-4">No posted unbilled time entries or costs to invoice.</p>
            )}
            <div className="mb-3">
              <label htmlFor={fid('invoice-notes')} className="block text-xs font-medium text-gray-700 mb-1">Invoice Notes (optional)</label>
              <textarea id={fid('invoice-notes')} value={invoiceNotes} onChange={e => setInvoiceNotes(e.target.value)}
                rows={2} placeholder="Additional notes..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="mb-3 p-3 border border-amber-200 bg-amber-50 rounded-lg">
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRetainerReplenishment}
                  onChange={e => setIsRetainerReplenishment(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">Retainer replenishment</span> — this invoice replenishes the client's trust retainer.
                  <span className="text-xs text-amber-700 block mt-0.5">⚠ Cal. Rule 1.15: retainer top-ups can NOT be paid via Stripe (must go through a trust-compliant channel). Checking this blocks the Stripe pay link on this invoice.</span>
                </span>
              </label>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                Total: ${(
                  postedEntries.filter(e => selectedTimeIds.includes(e.id) && e.billing_status === 'billable')
                    .reduce((sum, e) => sum + (parseFloat(e.hours) * parseFloat(e.rate)), 0) +
                  (matter.costs?.filter(c => selectedCostIds.includes(c.id) && c.billing_status === 'billable')
                    .reduce((sum, c) => sum + parseFloat(c.amount), 0) || 0)
                ).toFixed(2)}
              </span>
              <button onClick={handleGenerateInvoice} disabled={generatingInvoice}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
              </button>
              <button onClick={() => { setShowGenerateInvoice(false); setSelectedTimeIds([]); setSelectedCostIds([]); setInvoiceNotes(''); setIsRetainerReplenishment(false) }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
                Cancel
              </button>
            </div>
          </div>
        )}
        <div className="p-6">
          {matter.invoices?.length === 0 ? (
            <EmptyState
              className="py-6"
              icon="💰"
              title="No invoices yet"
              description="Generate an invoice from unbilled time entries and costs."
              action={canCreateInvoices ? {
                label: '📄 Generate Invoice',
                onClick: () => setShowGenerateInvoice(true)
              } : undefined}
            />
          ) : (
            // Scrolls inside the invoices card, which clips (overflow-hidden).
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Invoice #</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Date</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Due Date</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Total</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {matter.invoices?.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-primary-600">{invoice.invoice_number}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {format(new Date(invoice.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {invoice.due_date ? format(new Date(invoice.due_date), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-4 py-2 text-right font-bold">${parseFloat(invoice.total).toFixed(2)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                        invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>{invoice.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>}
      </div>

      )}

      {/* Events Section - ATTORNEY tab */}
      {activeTab === 'attorney' && <div id="events" className="mb-3">
        <button onClick={() => toggleSection('events')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-orange-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.events ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">📅 Events & Deadlines</span>
            {matter.events?.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">{matter.events.length}</span>}
          </div>
        </button>
        {openSections.events && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Events & Deadlines
            <span className="ml-2 text-sm text-gray-400">({matter.events?.length || 0})</span>
          </h3>
          <button onClick={() => setShowEventForm(!showEventForm)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            + Add Event
          </button>
        </div>

        {showEventForm && (
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <form onSubmit={handleEventSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor={fid('event-title')} className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                <input id={fid('event-title')} type="text" required value={eventForm.title}
                  onChange={e => setEventForm({...eventForm, title: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('event-type')} className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select id={fid('event-type')} value={eventForm.event_type}
                  onChange={e => setEventForm({...eventForm, event_type: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {renderEventTypeOptions()}
                </select>
              </div>
              <div>
                <label htmlFor={fid('event-start')} className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                <input id={fid('event-start')} type="datetime-local" required value={eventForm.start_time}
                  onChange={e => setEventForm(prev => ({
                    ...prev,
                    start_time: e.target.value,
                    end_time: adjustEndOnStartChange(prev.start_time, e.target.value, prev.end_time)
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('event-end')} className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                <input id={fid('event-end')} type="datetime-local" value={eventForm.end_time}
                  onChange={e => setEventForm({...eventForm, end_time: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor={fid('event-location')} className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <input id={fid('event-location')} type="text" value={eventForm.location}
                  onChange={e => setEventForm({...eventForm, location: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {/* Feedback #825 — the calendar's event form has Description +
                  Notes; the matter page's did not, so an event added from inside
                  a matter could carry neither.  POST /api/calendar already
                  accepts both. */}
              <div className="md:col-span-2">
                <label htmlFor={fid('event-description')} className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea id={fid('event-description')} rows={2} value={eventForm.description}
                  onChange={e => setEventForm({...eventForm, description: e.target.value})}
                  placeholder="What this event is about"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor={fid('event-notes')} className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea id={fid('event-notes')} rows={2} value={eventForm.notes}
                  onChange={e => setEventForm({...eventForm, notes: e.target.value})}
                  placeholder="Internal notes (e.g. what to bring, who is attending)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Google Calendar Sync — only shown if firm has Google configured AND user has connected */}
              {gcalStatus.configured && gcalStatus.connected && (
                <div className="md:col-span-2 border-2 border-blue-200 bg-blue-50 rounded-xl p-3">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={eventForm.sync_to_google}
                      onChange={e => setEventForm({...eventForm, sync_to_google: e.target.checked, push_to_user_ids: e.target.checked ? eventForm.push_to_user_ids : []})}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="text-xs font-medium text-gray-800">📅 Sync to my Google Calendar</div>
                      <p className="text-[11px] text-gray-600 mt-0.5">Push this event to {gcalStatus.google_email || 'your connected Google account'}.</p>
                    </div>
                  </label>
                  {eventForm.sync_to_google && gcalRecipients.filter(r => r.id !== user?.id).length > 0 && (
                    <div className="mt-2 ml-6 pt-2 border-t border-blue-200">
                      <div className="text-xs font-medium text-gray-800 mb-1">Also push to:</div>
                      <div className="space-y-1">
                        {gcalRecipients.filter(r => r.id !== user?.id).map(r => (
                          <label key={r.id} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input type="checkbox"
                              checked={eventForm.push_to_user_ids.includes(r.id)}
                              onChange={e => {
                                if (e.target.checked) setEventForm({...eventForm, push_to_user_ids: [...eventForm.push_to_user_ids, r.id]})
                                else setEventForm({...eventForm, push_to_user_ids: eventForm.push_to_user_ids.filter(id => id !== r.id)})
                              }}
                            />
                            <span>{r.name}</span>
                            <span className="text-[10px] text-gray-500">({r.google_email || r.email})</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">🔔 Reminders</label>
                <ReminderEditor
                  idPrefix="matter-evt-rem"
                  value={eventForm.reminders}
                  onChange={rows => setEventForm(prev => ({ ...prev, reminders: rows }))}
                />
              </div>

              <RecurrenceFields form={eventForm} setForm={setEventForm} />

              <div className="md:col-span-2 flex gap-2">
                <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium">Save Event</button>
                <button type="button" onClick={() => setShowEventForm(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="p-6">
          {matter.events?.length === 0 ? (
            <EmptyState
              className="py-6"
              icon="📅"
              title="No events yet"
              description="Track hearings, deadlines, and appointments for this matter."
              action={{
                label: '+ Add Event',
                onClick: () => setShowEventForm(true)
              }}
            />
          ) : (
            <div className="space-y-2">
              {eventsForList.map(event => (
                <div key={event.id} className="flex items-start gap-4 p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                  <div className="text-center min-w-[48px]">
                    <p className="text-xs text-gray-400">{format(toFirmWallClock(event.start_time), 'MMM')}</p>
                    <p className="text-xl font-bold text-primary-600">{format(toFirmWallClock(event.start_time), 'd')}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800">{event.title}</p>
                    <p className="text-sm text-gray-500">
                      {format(toFirmWallClock(event.start_time), 'h:mm a')}
                      {event.location && ` • ${event.location}`}
                    </p>
                    {/* Feedback #825 — show what was entered.  Collapsed to one
                        line; click to expand (full text also on hover). */}
                    {(event.description || event.notes) && (
                      <div
                        role="button" tabIndex={0}
                        onClick={() => setExpandedEventIds(prev => {
                          const next = new Set(prev)
                          if (next.has(event.id)) next.delete(event.id); else next.add(event.id)
                          return next
                        })}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click() } }}
                        className="mt-1 text-xs text-gray-600 cursor-pointer space-y-0.5"
                        title={[event.description && `Description: ${event.description}`, event.notes && `Notes: ${event.notes}`].filter(Boolean).join('\n\n')}
                        aria-expanded={expandedEventIds.has(event.id)}
                      >
                        {event.description && (
                          <p className={expandedEventIds.has(event.id) ? 'whitespace-pre-wrap' : 'truncate'}>
                            <span className="font-medium text-gray-500">Description: </span>{event.description}
                          </p>
                        )}
                        {event.notes && (
                          <p className={expandedEventIds.has(event.id) ? 'whitespace-pre-wrap' : 'truncate'}>
                            <span className="font-medium text-gray-500">Notes: </span>{event.notes}
                          </p>
                        )}
                      </div>
                    )}
                    {/* Feedback #829 follow-up — Created / Last changed + History,
                        same as the Calendar popup.  Until the author names have
                        loaded, the Created line is withheld rather than shown
                        with no name (which would read as "creator unknown"). */}
                    <EventHistory
                      event={event}
                      compact
                      showCreated={Object.prototype.hasOwnProperty.call(event, 'created_by_name')}
                    />
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${eventTypeColor(event.event_type)}`}>
                    {eventTypeLabel(event.event_type)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>}
      </div>

      }

      {activeTab === 'billing' && canViewTrust && (
      <div id="trust" className="mb-3">
        <button onClick={() => toggleSection('trust')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-indigo-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.trust ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">🏦 Trust Activity</span>
          </div>
        </button>
        {openSections.trust && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-semibold text-gray-800">
            Trust Activity
            <span className="ml-2 text-sm text-gray-400">({matter.trust_deposits?.length || 0})</span>
          </h3>
          {/* D-D.5: retainer target + refill button */}
          {(user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_create_invoices) && matter.retainer_target ? (
            <div className="flex items-center gap-3 text-xs">
              <span className="text-gray-600">
                Retainer target: <span className="font-semibold text-gray-800">${parseFloat(matter.retainer_target).toFixed(2)}</span>
              </span>
              {/* Wave 5 (#509): this matter's trust balance is measured across the
                  client's other pooled matters, but the table below lists only THIS
                  matter's movements.  Amber rather than the card's indigo because
                  this is a caveat about what the numbers beneath mean, not an action. */}
              {matter.shares_client_trust_pool && (
                <span
                  className="px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 border border-amber-300"
                  title="Trust funds for this client are pooled: the retainer balance is measured across every matter of theirs that shares the pool, not just this one."
                >
                  🔗 Trust pooled across this client's matters
                </span>
              )}
              <button
                onClick={async () => {
                  const ok = await ask({
                    title: 'Send retainer replenishment invoice?',
                    message: matter.shares_client_trust_pool
                      ? "This creates an invoice marked as retainer-replenishment for the deficit between the current trust balance and the retainer target.  This matter shares one trust balance with the client's other pooled matters, so the deficit is measured against their COMBINED balance — not the transactions listed below, which are only this matter's.  Rule 1.15: client must pay directly into IOLTA (not through credit-card processor)."
                      : 'This creates an invoice marked as retainer-replenishment for the deficit between the current trust balance and the retainer target.  Rule 1.15: client must pay directly into IOLTA (not through credit-card processor).',
                    // type:'confirm' is REQUIRED, not decoration.  PromptProvider
                    // defaults type to 'text' (PromptProvider.js:31): without this
                    // the dialog renders a text INPUT under the Rule 1.15 notice and
                    // onConfirm resolves String(value) — '' for an untouched box — so
                    // `if (!ok) return` silently swallows the click and NO invoice is
                    // created, with no error shown.  That is the same silent-failure
                    // this codebase banned window.confirm to avoid.
                    type: 'confirm',
                    confirmLabel: 'Create invoice',
                  })
                  if (!ok) return
                  try {
                    const res = await api.post(`/api/matters/${id}/retainer-replenishment-invoice`, {})
                    // Say which balance the amount was measured against.  A toast
                    // that states its own scope is what stops the next person
                    // filing a bug about a number that looks wrong but is not.
                    const pooledSuffix = res.data?.balance_scope === 'pooled'
                      ? ` (deficit vs pooled balance across ${(res.data.sibling_matter_count || 0) + 1} matters)`
                      : ''
                    toast.success(`Replenishment invoice ${res.data.invoice.invoice_number} created for $${parseFloat(res.data.invoiced_amount).toFixed(2)}.${pooledSuffix}`)
                    await fetchMatter()
                  } catch (err) {
                    toast.error(err.response?.data?.error || 'Failed to create replenishment invoice')
                  }
                }}
                className="px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
              >
                💵 Send replenishment invoice
              </button>
            </div>
          ) : null}
        </div>
        <div className="p-6">
          {matter.trust_deposits?.length === 0 ? (
            <EmptyState
              className="py-6"
              icon="🏦"
              title="No trust activity for this matter"
              description="Record retainer deposits and disbursements against this matter."
              action={{
                label: '💵 Record Deposit',
                onClick: () => navigate(`/trust?deposit=1&matter=${id}`)
              }}
            />
          ) : (
            // Scrolls inside the trust card, which clips (overflow-hidden).
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Date</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Account</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Type</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Counterparty</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Description</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {matter.trust_deposits?.map((item, i) => {
                  // D-D.5 trust-first invoicing writes a disbursement whose description
                  // is exactly `Auto-applied earned-fee withdrawal for INV-xxxx`
                  // (trustAutoApply.js).  A MANUAL pay-from-trust writes
                  // `Earned-fee withdrawal for INV-xxxx` — the two rows are otherwise
                  // byte-identical in this payload (same type 'fees', same payee, same
                  // reference_number), so this PREFIX is the only discriminator there is.
                  // Anchored on purpose: a substring match on /earned-fee withdrawal/
                  // would label every manual withdrawal as automatic, which is the
                  // opposite of what a bar auditor needs to see.
                  const isAutoApplied = item.transaction_type === 'disbursement'
                    && /^Auto-applied earned-fee withdrawal for /.test(item.description || '')
                  return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-600">
                      {/* deposit_date / disbursement_date are both DATE columns, which
                          node-pg serializes as `2026-09-15T00:00:00.000Z`.  new Date()
                          on that is UTC midnight and renders a day EARLY in any zone
                          behind UTC — Pacific, i.e. this firm.  parseDateOnly anchors
                          it to LOCAL midnight. */}
                      {format(parseDateOnly(item.transaction_date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{item.account_name}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          item.transaction_type === 'deposit'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>{item.transaction_type}</span>
                        {isAutoApplied && (
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700 border border-indigo-300"
                            title="Withdrawn from trust automatically when this invoice was issued, not by hand. Trust-first invoicing is a firm-wide setting."
                          >
                            ⚡ Auto-applied
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{item.counterparty}</td>
                    <td className="px-4 py-2 text-gray-600">{item.description}</td>
                    <td className={`px-4 py-2 text-right font-bold ${
                      item.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {item.transaction_type === 'deposit' ? '+' : '-'}${parseFloat(item.amount).toFixed(2)}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>}
      </div>

      )}

      {/* Costs Section - BILLING tab */}
      {activeTab === 'billing' && canViewCosts && (
      <div id="costs" className="mb-3">
        <button onClick={() => toggleSection('costs')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-teal-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.costs ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">📊 Case Costs & Expenses</span>
            {matter.costs?.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">{matter.costs.length}</span>}
          </div>
        </button>
        {openSections.costs && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Case Costs & Expenses
            <span className="ml-2 text-sm text-gray-400">({matter.costs?.length || 0})</span>
          </h3>
          {canCreateCosts && (
            <button onClick={() => { setShowCostForm(!showCostForm); setEditingCost(null) }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              + Add Cost
            </button>
          )}
        </div>

        {showCostForm && canCreateCosts && (
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              {editingCost ? 'Edit Cost Entry' : 'New Cost Entry'}
            </h4>
            {/* Feedback #845: the API saves a new cost already posted. */}
            {!editingCost && (
              <p className="text-xs text-gray-500 -mt-2 mb-3">
                New costs are posted as soon as you add them, so they are ready to invoice.
              </p>
            )}
            <form onSubmit={handleCostSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor={fid('cost-date')} className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                <input id={fid('cost-date')} type="date" required value={costForm.date}
                  onChange={e => setCostForm({...costForm, date: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('cost-expense-code')} className="block text-xs font-medium text-gray-700 mb-1">Expense Code</label>
                <select id={fid('cost-expense-code')} required value={costForm.expense_code}
                  onChange={e => setCostForm({...costForm, expense_code: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select expense code</option>
                  {EXPENSE_CODES.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={fid('cost-ref')} className="block text-xs font-medium text-gray-700 mb-1">Reference #</label>
                <input id={fid('cost-ref')} type="text" value={costForm.reference_number}
                  onChange={e => setCostForm({...costForm, reference_number: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('cost-qty')} className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                <input id={fid('cost-qty')} type="number" step="0.01" required value={costForm.quantity}
                  onChange={e => setCostForm({...costForm, quantity: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('cost-unit-rate')} className="block text-xs font-medium text-gray-700 mb-1">Unit Rate ($)</label>
                <input id={fid('cost-unit-rate')} type="number" step="0.01" required value={costForm.unit_rate}
                  onChange={e => setCostForm({...costForm, unit_rate: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('cost-billing')} className="block text-xs font-medium text-gray-700 mb-1">
                  Total: ${(parseFloat(costForm.quantity || 0) * parseFloat(costForm.unit_rate || 0)).toFixed(2)}
                </label>
                <select id={fid('cost-billing')} aria-label="Cost billing status" value={costForm.billing_status}
                  onChange={e => setCostForm({...costForm, billing_status: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="billable">Billable</option>
                  <option value="non-billable">Non-Billable</option>
                  <option value="no-charge-show">No Charge - Show on Bill</option>
                  <option value="no-charge-hide">No Charge - Don't Show on Bill</option>
                </select>
              </div>
              <div>
                <label htmlFor={fid('cost-vendor')} className="block text-xs font-medium text-gray-700 mb-1">Vendor/Payee</label>
                <input id={fid('cost-vendor')} type="text" value={costForm.vendor}
                  onChange={e => setCostForm({...costForm, vendor: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('cost-description')} className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input id={fid('cost-description')} type="text" value={costForm.description}
                  onChange={e => setCostForm({...costForm, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input type="checkbox" id="hard_cost" checked={costForm.hard_cost}
                  onChange={e => setCostForm({...costForm, hard_cost: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="hard_cost" className="text-sm text-gray-700">Hard Cost</label>
              </div>
              <div className="md:col-span-3 flex gap-2">
                <button type="submit"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  {editingCost ? 'Save Changes' : 'Add Cost'}
                </button>
                <button type="button"
                  onClick={() => { setShowCostForm(false); setEditingCost(null) }}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="p-6">
          {!matter.costs || matter.costs.length === 0 ? (
            <EmptyState
              className="py-6"
              icon="💵"
              title="No costs recorded yet"
              description="Log filing fees, expert charges, and other case expenses here."
              action={canCreateCosts ? {
                label: '+ Log Cost',
                onClick: () => { setShowCostForm(true); setEditingCost(null) }
              } : undefined}
            />
          ) : (
            <>
              {matter.costs.filter(c => c.status === 'unposted').length > 0 && (
                <div className="mb-4">
                  <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">
                    UNPOSTED — {matter.costs.filter(c => c.status === 'unposted').length} entries
                  </span>
                  {/* Nine columns: scroll inside the clipping costs card. */}
                  <div className="overflow-x-auto mt-2">
                  <table className="w-full text-sm">
                    <thead className="bg-yellow-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Code</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Description</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Ref #</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Qty</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Rate</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Amount</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Type</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {matter.costs.filter(c => c.status === 'unposted').map(cost => (
                        <tr key={cost.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {format(parseDateOnly(cost.date), 'MMM d, yyyy')}
                          </td>
                          <td className="px-3 py-2 text-gray-600 text-xs">{cost.expense_code}</td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{cost.description}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{cost.reference_number || '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{parseFloat(cost.quantity).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">${parseFloat(cost.unit_rate).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-medium">${parseFloat(cost.amount).toFixed(2)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${cost.hard_cost ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                              {cost.hard_cost ? 'Hard' : 'Soft'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-2">
                              <button onClick={() => handleEditCost(cost)}
                                className="text-xs text-blue-600 hover:underline">Edit</button>
                              <button onClick={() => handlePostCost(cost.id)}
                                className="text-xs text-green-600 hover:underline">Post</button>
                              {canDeleteCosts && (
                                <button onClick={() => handleDeleteCost(cost.id)}
                                  className="text-xs text-red-500 hover:underline">Delete</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {matter.costs.filter(c => c.status === 'posted').length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                    POSTED — {matter.costs.filter(c => c.status === 'posted').length} entries
                  </span>
                  <div className="overflow-x-auto mt-2">
                  <table className="w-full text-sm">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Date</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Code</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Description</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Ref #</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Qty</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Rate</th>
                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Amount</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Billing</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {matter.costs.filter(c => c.status === 'posted').map(cost => (
                        <tr key={cost.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {format(parseDateOnly(cost.date), 'MMM d, yyyy')}
                          </td>
                          <td className="px-3 py-2 text-gray-600 text-xs">{cost.expense_code}</td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{cost.description}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{cost.reference_number || '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{parseFloat(cost.quantity).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">${parseFloat(cost.unit_rate).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-medium">${parseFloat(cost.amount).toFixed(2)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              cost.billing_status === 'billable' ? 'bg-green-100 text-green-700' :
                              cost.billing_status === 'non-billable' ? 'bg-gray-100 text-gray-600' :
                              'bg-blue-100 text-blue-700'
                            }`}>{cost.billing_status}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-2">
                              <button onClick={() => handleEditCost(cost)}
                                className="text-xs text-blue-600 hover:underline">Edit</button>
                              {canDeleteCosts && (
                                <button onClick={() => handleDeleteCost(cost.id)}
                                  className="text-xs text-red-500 hover:underline">Delete</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                      <tr>
                        <td colSpan="6" className="px-3 py-2 text-sm font-semibold text-gray-700">Total Costs</td>
                        <td className="px-3 py-2 text-right font-bold text-gray-800">
                          ${matter.costs.filter(c => c.status === 'posted')
                            .reduce((sum, c) => sum + parseFloat(c.amount), 0).toFixed(2)}
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>}
      </div>
      )}

      {/* Notes Section - ATTORNEY tab */}
      {activeTab === 'attorney' && (
      <div id="notes" className="mb-3">
        <button onClick={() => toggleSection('notes')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-purple-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.notes ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">📝 Matter Notes</span>
            {matter.notes?.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">{matter.notes.length}</span>}
          </div>
        </button>
        {openSections.notes && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden p-6">
        <div className="p-6">
          {/* Add Note Form — with realtime typing indicator */}
          <TypingIndicator matterId={id} kind="note.typing" />
          <form onSubmit={handleAddNote} className="mb-4">
            <textarea
              value={newNote}
              onChange={e => { setNewNote(e.target.value); notifyTyping('note.typing', id) }}
              placeholder="Add a note about this matter..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button type="submit"
              className="mt-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              Add Note
            </button>
          </form>

          {/* Notes List */}
          {!matter.notes || matter.notes.length === 0 ? (
            <EmptyState
              className="py-6"
              icon="📝"
              title="No notes yet"
              description="Use the form above to add your first note about this matter."
            />
          ) : (
            <div className="space-y-3">
              {matter.notes.map(note => (
                <div key={note.id} className="bg-gray-50 rounded-lg p-4">
                  {editingNote === note.id ? (
                    <div>
                      <textarea
                        value={editNoteContent}
                        onChange={e => setEditNoteContent(e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => handleUpdateNote(note.id)}
                          className="text-xs text-green-600 hover:underline">Save</button>
                        <button onClick={() => setEditingNote(null)}
                          className="text-xs text-gray-500 hover:underline">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-400">
                          {note.created_by_name} • {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                        <div className="flex gap-2">
                          <button onClick={() => {
                            setEditingNote(note.id)
                            setEditNoteContent(note.content)
                          }} className="text-xs text-blue-600 hover:underline">Edit</button>
                          <button onClick={() => handleDeleteNote(note.id)}
                            className="text-xs text-red-500 hover:underline">Delete</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>}
      </div>
      )}

      {/* Communications Log - ATTORNEY tab */}
      {activeTab === 'attorney' && isFeatureOn('communications') && <div id="communications" className="mb-3">
        <button onClick={() => toggleSection('comms')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-green-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.comms ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">📞 Communications Log</span>
            {communications.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{communications.length}</span>}
          </div>
        </button>
        {openSections.comms && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Communications Log
            <span className="ml-2 text-sm text-gray-400">({communications.length})</span>
          </h3>
          <button onClick={() => { setShowCommForm(!showCommForm); setEditingComm(null); setCommForm({ comm_type: 'phone_call', direction: 'outgoing', subject: '', description: '', duration_minutes: '', comm_date: utcToLocalInput(new Date()), needs_response: false }) }}
            className="text-sm bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg transition-colors">
            + Log Communication
          </button>
        </div>

        {showCommForm && (
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            {/* Sits inside the form panel but OUTSIDE the <form>, because the
                form is a 3-column grid and a banner placed in it would be laid
                out as one narrow cell. */}
            <DraftRestoreBanner
              draft={commDraft}
              onRestore={() => {
                const s = restoreCommDraft()
                // Spread over the current shape rather than replacing it: a
                // draft from an older build can be missing a key, and an
                // undefined value flips a controlled input to uncontrolled.
                if (s) setCommForm(prev => ({ ...prev, ...s }))
              }}
              onDiscard={discardCommDraft}
            />
            <form onSubmit={handleCommSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor={fid('comm-type')} className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select id={fid('comm-type')} value={commForm.comm_type}
                  onChange={e => setCommForm({...commForm, comm_type: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="phone_call">Phone Call</option>
                  <option value="email">Email</option>
                  <option value="letter">Letter</option>
                  <option value="text">Text Message</option>
                  <option value="in_person">In-Person Meeting</option>
                  <option value="video_call">Video Call</option>
                  <option value="fax">Fax</option>
                </select>
              </div>
              <div>
                <label htmlFor={fid('comm-direction')} className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
                <select id={fid('comm-direction')} value={commForm.direction}
                  onChange={e => setCommForm({
                    ...commForm,
                    direction: e.target.value,
                    // Batch D-B: outgoing implies no callback needed
                    needs_response: e.target.value === 'incoming' ? commForm.needs_response : false,
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="outgoing">Outgoing</option>
                  <option value="incoming">Incoming</option>
                </select>
                {/* Batch D-B: needs-callback checkbox */}
                {commForm.direction === 'incoming' && (
                  <label className="mt-2 flex items-center gap-2 text-xs text-amber-800 cursor-pointer">
                    <input type="checkbox"
                      checked={!!commForm.needs_response}
                      onChange={e => setCommForm({...commForm, needs_response: e.target.checked})} />
                    <span>⚠ Needs callback</span>
                  </label>
                )}
              </div>
              <div>
                <label htmlFor={fid('comm-date')} className="block text-xs font-medium text-gray-600 mb-1">Date & Time</label>
                <input id={fid('comm-date')} type="datetime-local" required value={commForm.comm_date}
                  onChange={e => setCommForm({...commForm, comm_date: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('comm-subject')} className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
                <input id={fid('comm-subject')} type="text" required value={commForm.subject}
                  onChange={e => setCommForm({...commForm, subject: e.target.value})}
                  placeholder="Brief subject line"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor={fid('comm-duration')} className="block text-xs font-medium text-gray-600 mb-1">Duration (minutes)</label>
                <input id={fid('comm-duration')} type="number" min="0" value={commForm.duration_minutes}
                  onChange={e => setCommForm({...commForm, duration_minutes: e.target.value})}
                  placeholder="Optional"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-end gap-2">
                <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                  {editingComm ? 'Save Changes' : 'Log It'}
                </button>
                <button type="button" onClick={() => { discardCommDraft(); setShowCommForm(false); setEditingComm(null) }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                  Cancel
                </button>
              </div>
              <div className="md:col-span-3">
                <label htmlFor={fid('comm-description')} className="block text-xs font-medium text-gray-600 mb-1">Description / Notes</label>
                <TypingIndicator matterId={id} kind="comm.typing" />
                <textarea id={fid('comm-description')} value={commForm.description}
                  onChange={e => { setCommForm({...commForm, description: e.target.value}); notifyTyping('comm.typing', id) }}
                  rows={2} placeholder="Details of the communication..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </form>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {communications.length === 0 ? (
            <EmptyState
              className="py-6"
              icon="📞"
              title="No communications logged yet"
              description="Track phone calls, emails, and other client contact for this matter."
              action={{
                label: '+ Log Communication',
                onClick: () => { setShowCommForm(true); setEditingComm(null); setCommForm({ comm_type: 'phone_call', direction: 'outgoing', subject: '', description: '', duration_minutes: '', comm_date: utcToLocalInput(new Date()), needs_response: false }) }
              }}
            />
          ) : (
            communications.map(comm => (
              <div key={comm.id} className="px-6 py-3 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm">
                        {comm.comm_type === 'phone_call' ? '📞' :
                         comm.comm_type === 'email' ? '📧' :
                         comm.comm_type === 'letter' ? '✉️' :
                         comm.comm_type === 'text' ? '💬' :
                         comm.comm_type === 'in_person' ? '🤝' :
                         comm.comm_type === 'video_call' ? '📹' :
                         comm.comm_type === 'fax' ? '📠' : '📝'}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        comm.direction === 'incoming' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {comm.direction === 'incoming' ? '← In' : '→ Out'}
                      </span>
                      <p className="text-sm font-medium text-gray-800">{comm.subject}</p>
                    </div>
                    {comm.description && (
                      <p className="text-xs text-gray-500 mt-1 ml-7">{comm.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 ml-7 flex-wrap">
                      <p className="text-xs text-gray-400">
                        {comm.comm_date ? format(toFirmWallClock(comm.comm_date), 'MMM d, yyyy h:mm a') : '—'}
                      </p>
                      {comm.duration_minutes && (
                        <p className="text-xs text-gray-400">⏱ {comm.duration_minutes} min</p>
                      )}
                      <p className="text-xs text-gray-400">
                        Logged by {comm.logged_by_name}
                      </p>
                      {/* Batch D-B: needs-callback chips */}
                      {comm.needs_response && !comm.responded_at && (
                        <span className="text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 font-medium">
                          ⚠ Needs callback
                        </span>
                      )}
                      {comm.needs_response && comm.responded_at && (
                        <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5"
                              title={`Returned ${format(toFirmWallClock(comm.responded_at), 'MMM d, yyyy h:mm a')}`}>
                          ✓ Returned {format(toFirmWallClock(comm.responded_at), 'MMM d')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-2">
                    {/* Batch D-B: Mark returned action */}
                    {comm.needs_response && !comm.responded_at && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await api.put(`/api/communications/${comm.id}/mark-returned`)
                            setCommunications(prev => prev.map(x => x.id === comm.id ? { ...x, ...res.data } : x))
                          } catch (err) { setError(err.response?.data?.error || 'Could not mark returned') }
                        }}
                        className="text-xs text-emerald-600 hover:underline"
                        title="Mark this callback as returned"
                      >
                        ✓ Returned
                      </button>
                    )}
                    <button onClick={() => handleEditComm(comm)}
                      className="text-xs text-blue-500 hover:underline">Edit</button>
                    <button onClick={() => handleDeleteComm(comm.id)}
                      className="text-xs text-red-400 hover:underline">Delete</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>}
      </div>
      }

      {/* Discovery Section - ATTORNEY tab (Phase 1: set-level tracking) */}
      {activeTab === 'attorney' && isFeatureOn('discovery') && (
        <div id="discovery">
        <DiscoverySection
          matterId={id}
          discoverySets={discoverySets}
          openSections={openSections}
          toggleSection={toggleSection}
          onCreate={() => setShowDiscoveryForm(true)}
          onImported={fetchDiscoverySets}
          onDrill={(setId) => setDiscoveryDrillId(setId)}
          onExtension={(set) => setDiscoveryExtensionFor(set)}
          onStatusChange={async (set, newStatus) => {
            try {
              await updateDiscoverySet(set.id, { status: newStatus })
              fetchDiscoverySets()
            } catch (err) {
              setError(err.response?.data?.error || 'Could not update status')
            }
          }}
          onDelete={async (set) => {
            if (!await ask({ type: 'confirm', title: 'Delete discovery set?', message: `Delete ${set.type} ${set.set_number ? `Set ${set.set_number} ` : ''}from ${set.propounding_party || 'unknown'}? This will also remove the linked calendar event.`, confirmLabel: 'Delete', danger: true })) return
            try {
              await deleteDiscoverySet(set.id)
              fetchDiscoverySets()
            } catch (err) {
              setError(err.response?.data?.error || 'Could not delete')
            }
          }}
        />
        </div>
      )}

      {/* Case Analysis (CaseMap-style) — ATTORNEY tab */}
      {activeTab === 'attorney' && isFeatureOn('case_analysis') && (
        <div id="caseAnalysis" className="mb-3">
          <button
            onClick={() => toggleSection('caseAnalysis')}
            className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-fuchsia-500 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3">
              <span className={`text-gray-400 text-xs transition-transform ${openSections.caseAnalysis ? 'rotate-90' : ''}`}>&#9654;</span>
              <span className="font-semibold text-gray-800">🧩 Case Analysis</span>
              <span className="text-xs text-gray-500">facts · issues · cast · reports</span>
            </div>
          </button>
          {openSections.caseAnalysis && (
            <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
              <Suspense fallback={<div className="p-6"><SkeletonCard /></div>}>
                <CaseAnalysis
                  matterId={id}
                  matter={matter}
                  matterContacts={matterContacts}
                  documents={matterDocuments}
                />
              </Suspense>
            </div>
          )}
        </div>
      )}

      {/* New Discovery Set modal */}
      {showDiscoveryForm && (
        <DiscoverySetForm
          matterId={id}
          onClose={() => setShowDiscoveryForm(false)}
          onSaved={() => { setShowDiscoveryForm(false); fetchDiscoverySets() }}
        />
      )}

      {/* Extension modal */}
      {discoveryExtensionFor && (
        <DiscoveryExtensionModal
          set={discoveryExtensionFor}
          onClose={() => setDiscoveryExtensionFor(null)}
          onSaved={() => { setDiscoveryExtensionFor(null); fetchDiscoverySets() }}
        />
      )}

      {/* Drill-down popup */}
      {discoveryDrillId && (
        <DiscoveryDrillPopup
          setId={discoveryDrillId}
          onClose={() => setDiscoveryDrillId(null)}
          onChange={fetchDiscoverySets}
        />
      )}

      {/* SOL Tracker - ATTORNEY tab */}
      {activeTab === 'attorney' && isFeatureOn('sol_tracker') && <div id="sol" className="mb-3">
        <button onClick={() => toggleSection('sol')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-red-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.sol ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">⚖️ SOL Tracker</span>
            {solEntries.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{solEntries.length}</span>}
            {/* Firm calendar days, as the SOL Tracker counts them (utils/solDays.js) — not
                24-hour periods from UTC midnight, a day short from 5 PM Pacific. */}
            {solEntries.filter(e => { const d = e.status === 'active' ? solDaysLeft(e) : null; return d !== null && d >= 0 && d <= 90 }).length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white">URGENT</span>}
          </div>
        </button>
        {openSections.sol && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Statute of Limitations
            <span className="ml-2 text-sm text-gray-400">({solEntries.length})</span>
          </h3>
          <button onClick={() => { setShowSOLForm(!showSOLForm); setEditingSOL(null); setSOLForm({ cause_of_action: '', incident_date: '', sol_period_months: '', sol_date: '', tolling_notes: '', is_tolled: false, tolled_date: '', tolling_reason: '', status: 'active' }) }}
            className="text-sm bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg transition-colors">
            + Add SOL
          </button>
        </div>

        {showSOLForm && (
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            {/* Outside the <form> for the same grid-layout reason as the
                communications banner above. */}
            <DraftRestoreBanner
              draft={solDraft}
              onRestore={() => {
                const s = restoreSolDraft()
                if (s) setSOLForm(prev => ({ ...prev, ...s }))
              }}
              onDiscard={discardSolDraft}
            />
            <form onSubmit={handleSOLSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor={fid('sol-cause')} className="block text-xs font-medium text-gray-600 mb-1">Cause of Action</label>
                <select id={fid('sol-cause')} required value={solForm.cause_of_action}
                  onChange={e => {
                    const cause = e.target.value
                    const period = CA_SOL_PERIODS.find(p => p.label === cause)
                    const f = { ...solForm, cause_of_action: cause, sol_period_months: period?.months || '' }
                    if (period?.months && solForm.incident_date) {
                      f.sol_date = format(addMonths(new Date(solForm.incident_date + 'T12:00:00'), period.months), 'yyyy-MM-dd')
                    }
                    setSOLForm(f)
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Select cause</option>
                  {CA_SOL_PERIODS.map(p => (
                    <option key={p.label} value={p.label}>{p.label}{p.months ? ` (${p.months}mo)` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={fid('sol-incident')} className="block text-xs font-medium text-gray-600 mb-1">Incident Date</label>
                <input id={fid('sol-incident')} type="date" value={solForm.incident_date}
                  onChange={e => {
                    const date = e.target.value
                    const f = { ...solForm, incident_date: date }
                    if (date && solForm.sol_period_months) {
                        f.sol_date = format(addMonths(new Date(date + 'T12:00:00'), parseInt(solForm.sol_period_months)), 'yyyy-MM-dd')
                    }
                    setSOLForm(f)
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label htmlFor={fid('sol-date')} className="block text-xs font-medium text-gray-600 mb-1">SOL Date</label>
                <input id={fid('sol-date')} type="date" required value={solForm.sol_date}
                  onChange={e => setSOLForm({...solForm, sol_date: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor={fid('sol-notes')} className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <input id={fid('sol-notes')} type="text" value={solForm.tolling_notes}
                  onChange={e => setSOLForm({...solForm, tolling_notes: e.target.value})}
                  placeholder="Notes..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="flex items-end gap-2">
                <button type="submit" className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                  {editingSOL ? 'Save' : 'Add'}
                </button>
                <button type="button" onClick={() => { discardSolDraft(); setShowSOLForm(false); setEditingSOL(null) }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {solEntries.length === 0 ? (
            <EmptyState
              className="py-6"
              icon="⚖️"
              title="No statute of limitations tracked"
              description="Add SOL entries so filing deadlines surface on the SOL Tracker + Dashboard."
              action={(isUserAdmin || user?.permissions?.can_create_sol) ? {
                label: '+ Add SOL',
                onClick: () => { setShowSOLForm(true); setEditingSOL(null); setSOLForm({ cause_of_action: '', incident_date: '', sol_period_months: '', sol_date: '', tolling_notes: '', is_tolled: false, tolled_date: '', tolling_reason: '', status: 'active' }) }
              } : undefined}
            />
          ) : (
            solEntries.map(entry => {
              // Firm calendar days and the dates as entered — the SOL Tracker's
              // rule (utils/solDays.js), so both pages agree on every entry.
              const days = solDaysLeft(entry)
              const priorityColor = entry.status === 'filed' || entry.status === 'closed' ? 'bg-gray-100 text-gray-500'
                : entry.is_tolled ? 'bg-blue-100 text-blue-700'
                : days === null ? 'bg-gray-100 text-gray-500'
                : days < 0 ? 'bg-gray-900 text-white'
                : days <= 30 ? 'bg-red-600 text-white'
                : days <= 90 ? 'bg-orange-100 text-orange-700'
                : days <= 180 ? 'bg-yellow-100 text-yellow-700'
                : 'bg-green-100 text-green-700'
              const daysLabel = entry.status === 'filed' ? 'Filed' : entry.is_tolled ? 'Tolled' : days === null ? 'No date' : days < 0 ? `Expired ${Math.abs(days)}d ago` : `${days} days`
              return (
                <div key={entry.id} className="px-6 py-3 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-800">{entry.cause_of_action}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColor}`}>
                          {daysLabel}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {formatDateOnly(entry.incident_date, 'MMM d, yyyy') && <span className="text-xs text-gray-400">Incident: {formatDateOnly(entry.incident_date, 'MMM d, yyyy')}</span>}
                        <span className="text-xs text-gray-500 font-medium">SOL: {formatDateOnly(entry.sol_date, 'MMM d, yyyy') || '—'}</span>
                        {entry.tolling_notes && <span className="text-xs text-gray-400">— {entry.tolling_notes}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-2">
                      <button onClick={() => { setEditingSOL(entry.id); setSOLForm({ cause_of_action: entry.cause_of_action, incident_date: entry.incident_date?.slice(0,10) || '', sol_period_months: entry.sol_period_months || '', sol_date: entry.sol_date?.slice(0,10) || '', tolling_notes: entry.tolling_notes || '', is_tolled: entry.is_tolled, tolled_date: entry.tolled_date?.slice(0,10) || '', tolling_reason: entry.tolling_reason || '', status: entry.status || 'active' }); setShowSOLForm(true) }}
                        className="text-xs text-blue-500 hover:underline">Edit</button>
                      <button onClick={() => handleDeleteSOL(entry.id)}
                        className="text-xs text-red-400 hover:underline">Delete</button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>}
      </div>
      }

      {/* Contacts - ATTORNEY tab */}
      {activeTab === 'attorney' && <div id="contacts" className="mb-3">
        <button onClick={() => toggleSection('contacts')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-cyan-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.contacts ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">📇 Contacts</span>
            {matterContacts.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">{matterContacts.length}</span>}
          </div>
        </button>
        {openSections.contacts && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Contacts
            <span className="ml-2 text-sm text-gray-400">({matterContacts.length})</span>
          </h3>
          <button onClick={async () => {
            setShowContactLink(!showContactLink)
            if (!showContactLink && allContacts.length === 0) {
              try { const res = await api.get('/api/contacts'); setAllContacts(res.data) } catch (err) { setError(err.response?.data?.error || 'Error loading contacts') }
            }
          }}
            className="text-sm bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg transition-colors">
            + Add Contact
          </button>
        </div>

        {showContactLink && (
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <form onSubmit={handleAddContactToMatter} className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label htmlFor={fid('contact-link-contact')} className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
                <select id={fid('contact-link-contact')} required value={selectedContactId}
                  onChange={e => setSelectedContactId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Select a contact</option>
                  {allContacts
                    .filter(c => !matterContacts.find(mc => mc.id === c.id))
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}{c.company_name ? ` — ${c.company_name}` : ''}
                      </option>
                    ))}
                </select>
              </div>
              <div className="w-48">
                <label htmlFor={fid('contact-link-role')} className="block text-xs font-medium text-gray-600 mb-1">Role on this matter</label>
                <input id={fid('contact-link-role')} type="text" value={contactRole}
                  onChange={e => setContactRole(e.target.value)}
                  placeholder="e.g. Expert Witness"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <button type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                Link
              </button>
              <button type="button" onClick={() => setShowContactLink(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
            </form>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {matterContacts.length === 0 ? (
            <EmptyState
              className="py-6"
              icon="📇"
              title="No contacts linked to this matter"
              description="Link opposing counsel, expert witnesses, party contacts, and others."
              action={{
                label: '+ Link Contact',
                onClick: async () => {
                  setShowContactLink(true)
                  if (allContacts.length === 0) {
                    try { const res = await api.get('/api/contacts'); setAllContacts(res.data) } catch (err) { setError(err.response?.data?.error || 'Error loading contacts') }
                  }
                }
              }}
            />
          ) : (
            matterContacts.map(c => (
              <div key={c.link_id} className="px-6 py-3 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-800">
                        {c.first_name} {c.middle_name ? c.middle_name + ' ' : ''}{c.last_name}
                      </p>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${groupColor(c.contact_group)}`}>
                        {c.contact_group ? groupLabel(c.contact_group) : ''}
                      </span>
                      {c.matter_role && (
                        <span className="text-xs text-gray-400">({c.matter_role})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      {c.company_name && <span className="text-xs text-gray-500">🏢 {c.company_name}</span>}
                      {c.phone && <span className="text-xs text-gray-500">📞 {c.phone}</span>}
                      {c.email && <span className="text-xs text-gray-500">📧 {c.email}</span>}
                    </div>
                  </div>
                  <button onClick={() => handleRemoveContactFromMatter(c.link_id)}
                    className="text-xs text-red-400 hover:text-red-600 hover:underline ml-2">
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>}
      </div>
      }

      {/* Witnesses (CCP §2034 disclosure roster) — ATTORNEY tab.
          Ship 7i.  Feature-flagged.  Distinct from Case Analysis Cast
          (which is theory-of-case work-product): this is the formal
          statutory disclosure list served on opposing counsel.  Renders
          a purple-accented card between Contacts and Documents so the
          reading order follows "people first, papers second". */}
      {activeTab === 'attorney' && isFeatureOn('witnesses') && <div id="witnesses" className="mb-3">
        <button onClick={() => toggleSection('witnesses')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-purple-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.witnesses ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">🎤 Witnesses</span>
            <span className="text-xs text-gray-500">CCP §2034 disclosure roster</span>
          </div>
        </button>
        {openSections.witnesses && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
          <MatterWitnessesCard
            matterId={parseInt(id, 10)}
            matterContacts={matterContacts}
            canEdit={
              user?.role === 'admin' ||
              user?.permissions?.is_admin ||
              user?.permissions?.can_edit_matters ||
              user?.permissions?.can_create_matters
            }
            onContactCreated={fetchMatterContacts}
          />
        </div>}
      </div>}

      {/* Questionnaire ship (2026-08-29) — read-only Intake Questionnaire card.
          Self-hiding: renders nothing unless a lead questionnaire followed this
          matter through conversion (GET /:id/questionnaire, assertMatterAccess). */}
      {activeTab === 'attorney' && <MatterQuestionnaireCard matterId={matter.id} />}

      {/* Documents - ATTORNEY tab */}
      {activeTab === 'attorney' && <div id="documents" className="mb-3">
        <button onClick={() => toggleSection('documents')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-blue-500 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.documents ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">📂 Documents</span>
            {matterDocuments.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{matterDocuments.length}</span>}
          </div>
        </button>
        {openSections.documents && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">
              Documents
              <span className="ml-2 text-sm text-gray-400">({matterDocuments.length})</span>
            </h3>
            <div className="flex gap-2 flex-wrap">
              {/* Ship 7g portal-shared-audit — admin OR can_edit_matters can
                  view the per-matter shared-doc audit panel.  Toggling on
                  fires fetchPortalAudit (silent no-op if backend 404s). */}
              {(isUserAdmin || user?.permissions?.can_edit_matters) && (
                <button onClick={() => {
                    const next = !showPortalAudit
                    setShowPortalAudit(next)
                    if (next && portalAuditRows === null) fetchPortalAudit()
                  }}
                  className={`text-sm px-3 py-1.5 rounded-lg transition-colors border ${showPortalAudit ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-white hover:bg-emerald-50 text-emerald-600 border-emerald-300'}`}
                  title="Audit which documents are currently shared with the client + when they've opened them">
                  🔒 Portal Audit
                </button>
              )}
              <button onClick={() => setShowBatesConfig(true)}
                className="text-sm bg-white hover:bg-indigo-50 text-indigo-600 border border-indigo-300 px-3 py-1.5 rounded-lg transition-colors"
                title="Configure Bates stamping for this matter (prefix, starting number, zero-padding)">
                📑 Bates Config
              </button>
              <button onClick={() => {
                  const willShow = !showStandardLinks
                  setShowStandardLinks(willShow)
                  setShowDocForm(false)
                  setEditingDocId(null)
                  // Pre-check every standard folder when opening the panel.
                  // Common case is "create all 7", so a single click on
                  // Create finishes the job — no need to tick each box.
                  // User can uncheck any they don't want before committing.
                  if (willShow) {
                    const all = {}
                    standardLinkDefs().forEach(d => { all[d.key] = true })
                    setSelectedStandardLinks(all)
                    // Seed the matter folder path with the auto-computed default;
                    // user can override before scanning or generating links.
                    setMatterFolderPath(buildMatterPath('').replace(/\\$/, ''))
                    setScannedSubfolders(null)
                    setSelectedScanned({})
                    setScanError('')
                  }
                }}
                className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                Generate Document Links
              </button>
              <button onClick={() => {
                const willShow = !showDocForm
                setShowDocForm(willShow)
                setShowStandardLinks(false)
                setEditingDocId(null)
                if (willShow) {
                  setDocForm({ name: '', path: buildMatterPath(''), link_type: 'network', description: '' })
                }
              }}
                className="text-sm bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                + Add Document Link
              </button>
            </div>
          </div>

          {copyToast && (
            <div className="mx-6 mt-4 px-4 py-2 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
              {copyToast}
            </div>
          )}

          {/* Ship 7g portal-shared-audit panel — collapsible, shows every
              currently-shared doc + who shared it + client engagement
              counters.  Only rendered when toggled on (button above).
              Note about the audit limitation: shared_at/shared_by are
              cleared on untoggle by PUT /:id/share so this shows only
              CURRENTLY SHARED docs, not full history — the audit_log
              table has that append-only trail if needed. */}
          {showPortalAudit && (
            <div className="mx-6 mt-4 mb-2 px-4 py-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-sm font-semibold text-emerald-900">🔒 Portal Shared Records — audit</h4>
                  <p className="text-xs text-emerald-800/80 mt-0.5">
                    Documents currently visible to this matter's client on their portal.
                    Engagement counters reflect the client's opens + downloads for each doc.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={fetchPortalAudit}
                    disabled={portalAuditLoading}
                    className="text-xs bg-white hover:bg-emerald-100 text-emerald-700 border border-emerald-300 px-2 py-1 rounded transition-colors disabled:opacity-50">
                    {portalAuditLoading ? 'Loading…' : '↻ Refresh'}
                  </button>
                  <button onClick={() => setShowPortalAudit(false)}
                    className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline">
                    Close
                  </button>
                </div>
              </div>
              {portalAuditError && (
                <p className="text-xs text-red-700 mb-2">{portalAuditError}</p>
              )}
              {portalAuditLoading && !portalAuditRows && (
                <p className="text-xs text-emerald-700 italic">Loading shared-doc audit…</p>
              )}
              {!portalAuditLoading && Array.isArray(portalAuditRows) && portalAuditRows.length === 0 && (
                <p className="text-xs text-emerald-800/80 italic">No documents are currently shared with this client on the portal.</p>
              )}
              {Array.isArray(portalAuditRows) && portalAuditRows.length > 0 && (
                <div className="overflow-x-auto -mx-1 mt-2">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-emerald-900 border-b border-emerald-200">
                        <th className="text-left px-2 py-1 font-semibold">Document</th>
                        <th className="text-left px-2 py-1 font-semibold">Shared On</th>
                        <th className="text-left px-2 py-1 font-semibold">Shared By</th>
                        <th className="text-right px-2 py-1 font-semibold">Opens</th>
                        <th className="text-right px-2 py-1 font-semibold">Downloads</th>
                        <th className="text-left px-2 py-1 font-semibold">Last Opened</th>
                      </tr>
                    </thead>
                    <tbody>
                      {portalAuditRows.map(r => (
                        <tr key={r.doc_id} className="border-b border-emerald-100 last:border-b-0">
                          <td className="px-2 py-1.5">
                            <div className="font-medium text-emerald-900 truncate max-w-[24rem]" title={r.path}>{r.name}</div>
                            {r.document_category && <div className="text-emerald-700/80 text-[10px]">{r.document_category}</div>}
                          </td>
                          <td className="px-2 py-1.5 text-emerald-800">
                            {r.shared_at ? format(new Date(r.shared_at), 'MMM d, yyyy') : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-emerald-800">
                            {r.shared_by_name || <span className="text-gray-400" title="Actor unknown (shared before audit tracking, or user deleted)">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-emerald-900">{r.opened_count}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-emerald-900">{r.download_count}</td>
                          <td className="px-2 py-1.5 text-emerald-800">
                            {r.last_opened_at ? format(new Date(r.last_opened_at), 'MMM d, yyyy h:mm a') : <span className="text-gray-400">Never</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {showStandardLinks && (
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              {/* Editable matter folder path — defaults to {cases_base_path}\{matter.title}
                  but the user can override when the on-disk folder name differs from the
                  matter title (very common — firm folders are often abbreviated). */}
              <label htmlFor={fid('matter-folder-path')} className="block text-xs font-medium text-gray-600 mb-1">Matter folder path</label>
              <div className="flex gap-2 mb-3">
                <input id={fid('matter-folder-path')} type="text" value={matterFolderPath}
                  onChange={e => { setMatterFolderPath(e.target.value); setScannedSubfolders(null); setScanError('') }}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
                {window.electronAPI?.scanFolder && (
                  <button onClick={handleScanFolder} disabled={scanning || !matterFolderPath.trim()}
                    title="Read this folder and list whatever subfolders actually exist on disk"
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap">
                    {scanning ? 'Scanning…' : '🔍 Scan Folder'}
                  </button>
                )}
              </div>
              {scanError && (
                <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">⚠️ {scanError}</p>
              )}

              {scannedSubfolders ? (
                /* Discovered-folders mode — scan returned actual subdirectories on disk */
                scannedSubfolders.length === 0 ? (
                  <p className="text-sm text-gray-600 mb-3 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                    📂 Folder exists but has no subfolders. You can still create the standard 7 below if you want to pre-create links for folders you plan to add later.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-3">
                      Found <b>{scannedSubfolders.length}</b> subfolder{scannedSubfolders.length === 1 ? '' : 's'} — uncheck any you don't want linked:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 max-h-80 overflow-y-auto">
                      {scannedSubfolders.map(f => (
                        <label key={f.name} className="flex items-start gap-2 p-2 rounded hover:bg-white cursor-pointer">
                          <input type="checkbox" checked={!!selectedScanned[f.name]}
                            onChange={e => setSelectedScanned({...selectedScanned, [f.name]: e.target.checked})}
                            className="mt-1" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800">{f.name}</p>
                            <p className="text-xs text-gray-400 truncate font-mono" title={f.fullPath}>{f.fullPath}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )
              ) : (
                /* Fallback: standard 7 — used when scan unavailable (browser),
                   scan hasn't been clicked yet, or scan errored out */
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    {window.electronAPI?.scanFolder
                      ? 'Or pick from the standard 7 folder names (paths will be generated under the matter folder above):'
                      : 'Select which standard folder links to create for this matter:'}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                    {standardLinkDefs().map(d => (
                      <label key={d.key} className="flex items-start gap-2 p-2 rounded hover:bg-white cursor-pointer">
                        <input type="checkbox" checked={!!selectedStandardLinks[d.key]}
                          onChange={e => setSelectedStandardLinks({...selectedStandardLinks, [d.key]: e.target.checked})}
                          className="mt-1" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">{d.name}</p>
                          <p className="text-xs text-gray-400 truncate font-mono" title={fullPathFor(d.suffix)}>{fullPathFor(d.suffix)}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}

              <div className="flex gap-2">
                {(() => {
                  const count = scannedSubfolders
                    ? Object.values(selectedScanned).filter(Boolean).length
                    : Object.values(selectedStandardLinks).filter(Boolean).length
                  return (
                    <button onClick={handleAddStandardLinks}
                      disabled={count === 0}
                      className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                      Create {count > 0 ? `${count} ` : ''}Link{count === 1 ? '' : 's'}
                    </button>
                  )
                })()}
                <button onClick={() => { setShowStandardLinks(false); setSelectedStandardLinks({}); setScannedSubfolders(null); setSelectedScanned({}); setScanError('') }}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {showDocForm && (
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <form onSubmit={handleSaveDocument} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={fid('doc-name')} className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                    <input id={fid('doc-name')} type="text" required value={docForm.name}
                      onChange={e => setDocForm({...docForm, name: e.target.value})}
                      placeholder="e.g. Case Files, Discovery, Pleadings"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label htmlFor={fid('doc-type')} className="block text-xs font-medium text-gray-600 mb-1">
                      Type <span className="text-gray-400">(auto-detected: {detectLinkType(docForm.path) === 'url' ? 'URL' : 'Network Drive'})</span>
                    </label>
                    <input id={fid('doc-type')} type="text" readOnly value={detectLinkType(docForm.path) === 'url' ? 'URL' : 'Network Drive'}
                      className="w-full border border-gray-200 bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-500" />
                  </div>
                </div>
                <div>
                  <label htmlFor={fid('doc-path')} className="block text-xs font-medium text-gray-600 mb-1">Path</label>
                  <input id={fid('doc-path')} type="text" required value={docForm.path}
                    onChange={e => setDocForm({...docForm, path: e.target.value})}
                    placeholder="\\192.168.1.5\nxcloud1.2\data\E\PaperPort Docs\_Cases\[Matter Title]\"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div>
                  <label htmlFor={fid('doc-description')} className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
                  <input id={fid('doc-description')} type="text" value={docForm.description}
                    onChange={e => setDocForm({...docForm, description: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="flex gap-2">
                  <button type="submit"
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    {editingDocId ? 'Save Changes' : 'Save'}
                  </button>
                  <button type="button" onClick={() => { setShowDocForm(false); setEditingDocId(null); setDocForm({ name: '', path: '', link_type: 'network', description: '' }); }}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="divide-y divide-gray-100">
            {matterDocuments.length === 0 ? (
              <EmptyState
                className="py-6"
                icon="📂"
                title="No document links for this matter"
                description="Add the default folder set with one click, or add a custom link."
                action={{
                  label: '📁 Generate Standard Links',
                  onClick: () => {
                    setShowStandardLinks(true)
                    setShowDocForm(false)
                    setEditingDocId(null)
                    const all = {}
                    standardLinkDefs().forEach(d => { all[d.key] = true })
                    setSelectedStandardLinks(all)
                    setMatterFolderPath(buildMatterPath('').replace(/\\$/, ''))
                    setScannedSubfolders(null)
                    setSelectedScanned({})
                    setScanError('')
                  }
                }}
                secondary={{
                  label: '+ Add Document Link',
                  onClick: () => {
                    setShowDocForm(true)
                    setShowStandardLinks(false)
                    setEditingDocId(null)
                    setDocForm({ name: '', path: buildMatterPath(''), link_type: 'network', description: '' })
                  }
                }}
              />
            ) : (
              matterDocuments.map(d => (
                <div key={d.id} className="px-6 py-3 hover:bg-gray-50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg">{d.link_type === 'url' ? '🔗' : '📁'}</span>
                        <p className="text-sm font-bold text-gray-800 truncate">{d.name}</p>
                        {d.bates_range && (
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-mono"
                                title="Bates range assigned">📑 {d.bates_range}</span>
                        )}
                        {d.share_with_client && (
                          <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded"
                                title={d.shared_at ? `Shared with client on ${format(new Date(d.shared_at), 'MMM d, yyyy')}` : 'Shared with client'}>
                            👥 Shared
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 font-mono truncate mt-0.5" title={d.path}>{d.path}</p>
                      {d.description && <p className="text-xs text-gray-400 mt-0.5">{d.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleToggleShare(d)}
                        className={`text-xs px-2 py-1 rounded transition-colors ${d.share_with_client ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : 'text-emerald-600 hover:text-emerald-800 hover:underline'}`}
                        title={d.share_with_client ? 'Currently visible on client portal — click to stop sharing' : 'Share this document with the client on their portal'}>
                        {d.share_with_client ? '👥 Sharing' : '👥 Share'}
                      </button>
                      <button onClick={() => handleAssignBates(d)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                        title="Allocate next Bates number(s) for this document">
                        {d.bates_range ? '📑 Re-stamp' : '📑 Stamp'}
                      </button>
                      <button onClick={() => handleOpenDocumentPath(d.path)}
                        className="text-xs bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                        {window.electronAPI?.isElectron ? 'Open' : 'Copy Path'}
                      </button>
                      <button onClick={() => handleEditDocument(d)}
                        className="text-xs text-gray-500 hover:text-gray-700 hover:underline">Edit</button>
                      <button onClick={() => handleDeleteDocument(d.id)}
                        className="text-xs text-red-400 hover:text-red-600 hover:underline">Delete</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>}
      </div>
      }

      {activeTab === 'attorney' && <div id="details" className="mb-3">
        <button onClick={() => toggleSection('details')}
          className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-gray-400 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className={`text-gray-400 text-xs transition-transform ${openSections.details ? 'rotate-90' : ''}`}>&#9654;</span>
            <span className="font-semibold text-gray-800">📋 Matter Details</span>
          </div>
        </button>
        {openSections.details && <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden">
        {/* Matters depth #1 — budget + burn indicator.  Empty when no
            budget set; admins/editors get an "Edit budget" inline form. */}
        <MatterBudgetCard matterId={matter.id} canEdit={user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_edit_matters || user?.permissions?.can_create_matters} />
        {/* Matters depth #3 UI — related matters (parent/child/sibling/consolidated/appeal_of/related_to) */}
        <MatterRelationshipsCard matterId={matter.id} canEdit={user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_edit_matters || user?.permissions?.can_create_matters} />
        {/* Matters depth #4 UI — stage picker + history for the client-facing timeline */}
        <MatterStageCard matterId={matter.id} matterStatus={matter.status} canEdit={user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_edit_matters || user?.permissions?.can_create_matters} />
        {/* D-D.6 — firm-side billing phases + per-phase burn.  Sits beside
            MatterStageCard deliberately: stages are the CLIENT-facing
            timeline, phases are internal billing structure the client
            never sees. */}
        <MatterPhasesCard matterId={matter.id} canEdit={user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_edit_matters || user?.permissions?.can_create_matters} />
        {/* D-D.6.c — contingency settlement + Settlement Statement PDF card.
            Renders ONLY for contingency-billed matters.  Two states:
            unsettled → Settle Contingency modal (POSTs settle-contingency);
            settled → Download Settlement Statement PDF button (GETs
            /settlement-statement + builds via buildSettlementStatementPDF). */}
        {matter.billing_type === 'contingency' && (
          <ContingencySettlementCard
            matter={matter}
            canSettle={user?.role === 'admin' || user?.permissions?.is_admin || user?.permissions?.can_create_invoices}
            onRefresh={fetchMatter}
            toast={toast}
            ask={ask}
          />
        )}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Matter Information</h4>
            <dl className="space-y-2">
              {[
                ['File Number', matter.file_number],
                ['Practice Area', matter.practice_area],
                ['Status', matter.status],
                ['Billing Type', matter.billing_type?.replace('_', ' ')],
                ['Hourly Rate', matter.rate ? `$${matter.rate}/hr` : null],
                ['Retainer', matter.retainer ? `$${matter.retainer}` : null],
                ['Open Date', matter.open_date ? format(new Date(matter.open_date), 'MMM d, yyyy') : null],
                ['Responsible Attorney', matter.responsible_attorney_name],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between py-1 border-b border-gray-100">
                  <dt className="text-sm text-gray-500">{label}</dt>
                  <dd className="text-sm font-medium text-gray-800">{value || '—'}</dd>
                </div>
              ))}
            </dl>
            {/* Ship 7i.1 — Designating party picker.  Pre-code panel HIGH #3
                (3/3 convergence): constrained-vocabulary role dropdown +
                optional party name, NOT a free-text field.  Composed
                on save into matter.designating_party (VARCHAR(200)).
                Parsed back on read via best-effort role-prefix match.
                Firm default lives at firm_settings.default_designating_party
                — surfaced as the placeholder + "Use firm default" chip. */}
            <MatterDesignatingPartyCard
              matterId={matter.id}
              matter={matter}
              firmSettings={firmSettings}
              canEdit={
                user?.role === 'admin' ||
                user?.permissions?.is_admin ||
                user?.permissions?.can_edit_matters ||
                user?.permissions?.can_create_matters
              }
              onSaved={fetchMatter}
            />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Client Information</h4>
            <dl className="space-y-2">
              {[
                ['Name', matter.client_id ? displayClientForMatterRow(matter) : null],
                ['Email', matter.client_email],
                ['Phone', matter.client_phone],
                ['Address', matter.client_address],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between py-1 border-b border-gray-100">
                  <dt className="text-sm text-gray-500">{label}</dt>
                  <dd className="text-sm font-medium text-gray-800">{value || '—'}</dd>
                </div>
              ))}
            </dl>
            {matter.description && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                <p className="text-sm text-gray-600">{matter.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>}
      </div>
      }
      {/* Close Matter Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Close Matter</h3>

            {closeCheckLoading ? (
              <p className="text-gray-500 text-center py-8">Running pre-close checklist...</p>
            ) : closeChecklist && (
              <>
                {/* Step 1 — Checklist */}
                {closeStep === 1 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-4">Review the following items before closing this matter:</p>
                    <div className="space-y-3">
                      {Object.entries(closeChecklist.checklist).map(([key, item]) => (
                        <div key={key} className={`flex items-start gap-3 p-3 rounded-lg border ${
                          item.status === 'ok' ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'
                        }`}>
                          <span className="text-lg">{item.status === 'ok' ? '✅' : '⚠️'}</span>
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${item.status === 'ok' ? 'text-green-700' : 'text-yellow-700'}`}>
                              {item.message}
                            </p>
                            {item.status === 'warning' && (
                              <label className="flex items-center gap-2 mt-2 text-xs text-yellow-600 cursor-pointer">
                                <input type="checkbox" checked={acknowledgedWarnings.has(key)}
                                  onChange={() => {
                                    const next = new Set(acknowledgedWarnings)
                                    if (next.has(key)) next.delete(key); else next.add(key)
                                    setAcknowledgedWarnings(next)
                                  }}
                                  className="rounded border-yellow-400 text-yellow-600 focus:ring-yellow-500" />
                                Acknowledge and proceed
                              </label>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-3 mt-6">
                      <button onClick={() => setCloseStep(2)}
                        disabled={Object.entries(closeChecklist.checklist)
                          .filter(([, item]) => item.status === 'warning')
                          .some(([key]) => !acknowledgedWarnings.has(key))}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                        Next
                      </button>
                      <button onClick={() => setShowCloseModal(false)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2 — Closing Notes */}
                {closeStep === 2 && (
                  <div>
                    <label htmlFor={fid('closing-notes')} className="text-sm text-gray-600 mb-3 block">Provide a reason for closing this matter:</label>
                    <textarea id={fid('closing-notes')} value={closingNotes} onChange={e => setClosingNotes(e.target.value)}
                      rows={4} placeholder="e.g. Matter resolved. Settlement paid. File archived."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2" />
                    <p className="text-xs text-gray-400 mb-4">This will mark the matter as Closed. This action can be reversed by an admin.</p>
                    <div className="flex gap-3">
                      <button onClick={handleCloseMatter} disabled={!closingNotes.trim()}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
                        Close Matter
                      </button>
                      <button onClick={() => setCloseStep(1)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Phase 4 #4 — Bates configuration modal */}
      {showBatesConfig && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowBatesConfig(false)}>
          <form onSubmit={handleSaveBatesConfig} className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">📑 Bates Configuration</h3>
              <button type="button" onClick={() => setShowBatesConfig(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">Per-matter Bates stamping config.  Each document on this matter can be assigned the next number(s) via the 📑 Stamp button on its row.</p>
              <label className="text-sm block">Prefix *
                <input type="text" required value={batesConfig.bates_prefix}
                  onChange={e => setBatesConfig({ ...batesConfig, bates_prefix: e.target.value.toUpperCase() })}
                  placeholder="e.g., DEF, PLAINTIFF, PROD" autoFocus
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1 font-mono"/>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">Next number
                  <input type="number" min="1" value={batesConfig.bates_next_number}
                    onChange={e => setBatesConfig({ ...batesConfig, bates_next_number: parseInt(e.target.value, 10) || 1 })}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1"/>
                </label>
                <label className="text-sm">Zero padding
                  <input type="number" min="1" max="10" value={batesConfig.bates_padding}
                    onChange={e => setBatesConfig({ ...batesConfig, bates_padding: parseInt(e.target.value, 10) || 6 })}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1"/>
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Preview: <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{batesConfig.bates_prefix || 'PREFIX'}-{String(batesConfig.bates_next_number || 1).padStart(batesConfig.bates_padding || 6, '0')}</span>
              </p>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button type="button" onClick={() => setShowBatesConfig(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
              <button type="submit" disabled={batesConfigBusy} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50">
                {batesConfigBusy ? 'Saving…' : 'Save Config'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

// D-D.6.c — Contingency Settlement card.  Two states:
//   (a) unsettled  → renders a "Settle Contingency" button that opens an
//                    inline form; POSTs /:id/settle-contingency; refetches
//                    parent matter on success.  Gated on canSettle.
//   (b) settled    → renders a "Download Settlement Statement" button that
//                    GETs /settlement-statement + hands the payload to
//                    buildSettlementStatementPDF for client-side rendering.
//                    Always visible (regenerable per bar-audit requirement).
//
// Renders only when matter.billing_type='contingency' (parent gates).
function ContingencySettlementCard ({ matter, canSettle, onRefresh, toast, ask }) {
  const [settling, setSettling] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ recovery_amount: '', settle_notes: '' })
  const [err, setErr] = useState('')

  // T2-B.next.B draft persistence.  Settlement is one-shot and irreversible,
  // and settle_notes carries the narrative (insurer, case number, terms) that
  // is worst to retype from memory.
  //
  // The matter id is REQUIRED in the key: without it a settlement narrative
  // typed on one client's matter would resurface on another's, which is a
  // confidentiality bug rather than a UX one.  Null when the id is somehow
  // absent, so a missing id disables persistence instead of collapsing every
  // matter onto `matter-undefined-settle`.  Also null while the form is
  // closed, so nothing is written for a form the user never opened.
  const settleDraftKey = (showForm && matter?.id) ? `matter-${matter.id}-settle` : null
  const { draft, restore, discard } = useDraft(settleDraftKey, form, {
    isDirty: (s) => !!(String(s.recovery_amount || '').trim() || String(s.settle_notes || '').trim())
  })

  const isSettled = !!matter.contingency_settled_at
  const pct = matter.contingency_pct != null ? parseFloat(matter.contingency_pct) : null
  const costsDeductedFirst = !!matter.contingency_costs_deducted_first

  // Client-side live preview.  Same formula the server applies at settle
  // time (matters.js:1857) so the user sees the exact fee/net before hitting
  // Confirm.  Costs figure is best-effort; the real amount is computed
  // server-side from case_costs at settle time (this is a UX preview only).
  const previewFor = (recoveryStr) => {
    const rec = parseFloat(recoveryStr)
    if (!Number.isFinite(rec) || rec <= 0 || !pct) return null
    // Preview assumes zero costs — we can't fetch case_costs from here
    // without a round-trip; server does the authoritative math.
    const feeBase = costsDeductedFirst ? rec : rec
    const feeAmount = feeBase * (pct / 100)
    return {
      gross: rec,
      fee: feeAmount,
      net_approx: rec - feeAmount,
    }
  }
  const preview = previewFor(form.recovery_amount)

  const submitSettle = async (e) => {
    e.preventDefault()
    const rec = parseFloat(form.recovery_amount)
    if (!Number.isFinite(rec) || rec <= 0) {
      setErr('Recovery amount must be a positive number.')
      return
    }
    const confirmed = await ask({
      message: `Settle this matter for ${money(rec)}? This is IRREVERSIBLE — the fee invoice will be generated and the settlement amount will be locked on the matter.`,
      variant: 'danger',
    })
    if (!confirmed) return
    setSettling(true); setErr('')
    try {
      await api.post(`/api/matters/${matter.id}/settle-contingency`, {
        recovery_amount: rec,
        settle_notes: form.settle_notes || undefined,
      })
      // Settled — the draft has served its purpose.  Discarding BEFORE the
      // state changes below lets useDraft's suppression window swallow the
      // final flush that fires when showForm flips and the key goes null.
      discard()
      setShowForm(false)
      toast?.showToast?.(`Settlement recorded for ${money(rec)}. Invoice generated.`, 'success')
      onRefresh?.()
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2?.response?.data?.message || 'Settle failed')
    } finally {
      setSettling(false)
    }
  }

  const downloadStatement = async () => {
    setDownloading(true)
    const { buildSettlementStatementPDF, settlementStatementFilename } = await import('../lib/settlementStatementPDF')
    try {
      const r = await api.get(`/api/matters/${matter.id}/settlement-statement`)
      const payload = r.data || {}
      const doc = buildSettlementStatementPDF(payload)
      doc.save(settlementStatementFilename(payload))
      // Best-effort audit log entry — logExport already covers other PDF paths.
      try {
        const { logExport } = await import('../services/api')
        if (logExport) {
          await logExport({
            resource_type: 'settlement_statement',
            resource_id: matter.id,
            format: 'pdf',
            filename: settlementStatementFilename(payload),
            description: `Settlement Statement for ${payload.matter_title || `Matter ${matter.id}`}`,
          }).catch(() => {})
        }
      } catch { /* audit is best-effort */ }
      toast?.showToast?.('Settlement Statement downloaded.', 'success')
    } catch (e) {
      toast?.showToast?.(e?.response?.data?.error || 'Download failed', 'error')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-emerald-50/60 to-white">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-emerald-900">💼 Contingency Settlement</h4>
          <p className="text-xs text-emerald-700/80">
            {pct != null ? `${pct.toFixed(2)}% fee — costs deducted ${costsDeductedFirst ? 'FIRST' : 'AFTER fee'}` : 'Contingency configuration missing'}
          </p>
        </div>
        {isSettled && (
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
            SETTLED
          </span>
        )}
      </div>

      {isSettled ? (
        <div className="space-y-3">
          <dl className="grid grid-cols-2 gap-y-1 text-xs">
            <dt className="text-gray-500">Recovery Amount</dt>
            <dd className="text-right font-semibold text-gray-800">{money(matter.contingency_recovery_amount)}</dd>
            <dt className="text-gray-500">Settled At</dt>
            <dd className="text-right text-gray-800">{matter.contingency_settled_at ? format(new Date(matter.contingency_settled_at), 'MMM d, yyyy') : '—'}</dd>
          </dl>
          <button
            type="button"
            onClick={downloadStatement}
            disabled={downloading}
            className="w-full text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-2 rounded font-medium"
          >
            {downloading ? 'Preparing…' : '📄 Download Settlement Statement'}
          </button>
          <p className="text-[10px] text-gray-500 italic">
            Regenerable indefinitely for bar-audit compliance (Cal. B&amp;P § 6147).
          </p>
        </div>
      ) : canSettle ? (
        showForm ? (
          <form onSubmit={submitSettle} className="space-y-3">
            <DraftRestoreBanner
              draft={draft}
              onRestore={() => {
                const s = restore()
                // Spread over the pristine shape, never assign the stored object
                // wholesale: a draft written by an older build can be missing a
                // key, and an undefined value flips a controlled input to
                // uncontrolled.
                if (s) setForm({ recovery_amount: '', settle_notes: '', ...s })
              }}
              onDiscard={discard}
            />
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Gross Recovery Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.recovery_amount}
                onChange={(e) => setForm({ ...form, recovery_amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder="100000.00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Settlement Notes (optional)</label>
              <textarea
                rows={2}
                value={form.settle_notes}
                onChange={(e) => setForm({ ...form, settle_notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder="Case #, insurer, settlement terms…"
              />
            </div>
            {preview && (
              <div className="text-xs bg-white border border-emerald-200 rounded p-2 space-y-1">
                <div className="flex justify-between"><span>Gross Recovery</span><span className="font-semibold">{money(preview.gross)}</span></div>
                <div className="flex justify-between"><span>Attorney Fee ({pct?.toFixed(2)}%)</span><span className="text-red-600">({money(preview.fee)})</span></div>
                <div className="flex justify-between border-t pt-1 mt-1"><span className="font-semibold">Net (approx, before costs)</span><span className="font-semibold text-emerald-700">{money(preview.net_approx)}</span></div>
                <p className="text-[10px] text-gray-500 italic pt-1">Server subtracts advanced costs at settle time — actual net may differ.</p>
              </div>
            )}
            {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
            <div className="flex gap-2">
              <button type="submit" disabled={settling} className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm px-3 py-2 rounded font-medium">
                {settling ? 'Settling…' : 'Confirm Settlement'}
              </button>
              {/* Cancel deliberately discards the STORED draft but leaves the
                  in-memory form alone — reopening in the same session still
                  shows what was typed (unchanged behaviour), while the next
                  session gets no stale banner.  Not resetting the form here is
                  also what keeps the discard safe: mutating to a dirty state
                  inside useDraft's 500ms suppression window would re-persist
                  what was just cleared. */}
              <button type="button" onClick={() => { discard(); setShowForm(false); setErr('') }} className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            disabled={!pct}
            title={!pct ? 'Set contingency_pct on the matter edit form first.' : 'Record settlement + generate fee invoice'}
            className="w-full text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-2 rounded font-medium"
          >
            💼 Settle Contingency
          </button>
        )
      ) : (
        <p className="text-xs text-gray-500 italic">Requires the "can_create_invoices" permission to settle.</p>
      )}
    </div>
  )
}

// Small helper reused by ContingencySettlementCard.
function money (n) { return `$${Number(n || 0).toFixed(2)}` }

// Matters depth #1 — budget + burn tracker card.  Loads /budget-burn
// endpoint on mount; renders empty state with "Set budget" when no
// budget; renders spent-vs-budget with red/yellow/green bar when set.
function MatterBudgetCard ({ matterId, canEdit }) {
  const [burn, setBurn] = useState(null)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ budget_amount: '', budget_type: 'fees_and_costs', budget_notes: '', budget_warn_pct: 80 })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [hoveredBar, setHoveredBar] = useState(null)
  const load = async () => {
    try {
      // Ask for 12 weeks of burn history + projection.  Endpoint extension
      // lands with A11; if the backend hasn't shipped the weeks= param yet
      // it will simply ignore it and return the existing shape.
      const r = await api.get(`/api/matters/${matterId}/budget-burn`, { params: { weeks: 12 } })
      setBurn(r.data)
      setForm({
        budget_amount: r.data.budget_amount ?? '',
        budget_type: r.data.budget_type || 'fees_and_costs',
        budget_notes: r.data.budget_notes || '',
        budget_warn_pct: r.data.budget_warn_pct || 80,
      })
    } catch { /* fallback: no card */ }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [matterId])
  if (!burn) return null

  const save = async () => {
    setSaving(true); setErr('')
    try {
      await api.put(`/api/matters/${matterId}`, {
        budget_amount: form.budget_amount === '' ? null : Number(form.budget_amount),
        budget_type: form.budget_type,
        budget_notes: form.budget_notes || null,
        budget_warn_pct: parseInt(form.budget_warn_pct, 10) || 80,
      })
      setEditing(false); load()
    } catch (e) { setErr(e.response?.data?.error || 'Save failed') } finally { setSaving(false) }
  }

  const clearBudget = async () => {
    setSaving(true); setErr('')
    try {
      await api.put(`/api/matters/${matterId}`, {
        budget_amount: null, budget_type: null, budget_notes: null, budget_warn_pct: null,
      })
      setEditing(false); load()
    } catch (e) { setErr(e.response?.data?.error || 'Clear failed') } finally { setSaving(false) }
  }

  const barColor = burn.indicator === 'over' ? 'bg-red-500'
    : burn.indicator === 'warn' ? 'bg-amber-500'
    : burn.indicator === 'ok' ? 'bg-emerald-500' : 'bg-gray-300'
  const chipCls = burn.indicator === 'over' ? 'bg-red-100 text-red-700'
    : burn.indicator === 'warn' ? 'bg-amber-100 text-amber-700'
    : burn.indicator === 'ok' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'

  return (
    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-800">💰 Budget &amp; Burn</h4>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} className="text-xs text-primary-700 hover:underline">
            {burn.budget_amount === null ? '+ Set budget' : 'Edit'}
          </button>
        )}
      </div>
      {err && <div className="text-xs text-red-700 mb-2">{err}</div>}
      {editing ? (
        <div className="space-y-2 text-xs bg-white border border-gray-200 rounded-lg p-3">
          <div className="grid grid-cols-2 gap-2">
            <label>Budget ($) <input type="number" step="0.01" min="0" value={form.budget_amount} onChange={e => setForm({...form, budget_amount: e.target.value})} className="w-full border border-gray-300 rounded px-2 py-1" /></label>
            <label>Type
              <select value={form.budget_type} onChange={e => setForm({...form, budget_type: e.target.value})} className="w-full border border-gray-300 rounded px-2 py-1">
                <option value="fees_only">Fees only</option>
                <option value="fees_and_costs">Fees + costs</option>
                <option value="fees_and_costs_cap">Fees + costs (hard cap)</option>
              </select>
            </label>
            <label>Warn at (%) <input type="number" min="1" max="200" value={form.budget_warn_pct} onChange={e => setForm({...form, budget_warn_pct: e.target.value})} className="w-full border border-gray-300 rounded px-2 py-1" /></label>
            <label className="col-span-2">Notes <input type="text" value={form.budget_notes} onChange={e => setForm({...form, budget_notes: e.target.value})} placeholder="e.g. Phase 1 discovery cap" className="w-full border border-gray-300 rounded px-2 py-1" /></label>
          </div>
          <div className="flex gap-2 justify-end">
            {burn.budget_amount !== null && <button onClick={clearBudget} disabled={saving} className="text-xs text-red-700 hover:underline">Clear budget</button>}
            <button onClick={() => { setEditing(false); setErr('') }} className="text-xs text-gray-600 hover:underline">Cancel</button>
            <button onClick={save} disabled={saving} className="text-xs bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      ) : burn.budget_amount === null ? (
        <EmptyState
          className="py-4"
          icon="💰"
          title="No budget set for this matter"
          description="Set a fee/cost budget to track spend against a target and project burn."
          action={canEdit ? {
            label: 'Set Budget',
            onClick: () => setEditing(true)
          } : undefined}
        />
      ) : (
        <>
          <div className="flex items-center gap-3 text-xs mb-2">
            <span className="tabular-nums">
              <strong>${Number(burn.spent_total).toFixed(2)}</strong> spent /
              <strong className="ml-1">${Number(burn.budget_amount).toFixed(2)}</strong> budget
            </span>
            <span className={`px-2 py-0.5 rounded-full font-semibold ${chipCls}`}>{burn.percent_used}% used</span>
            {burn.remaining < 0 && <span className="text-red-700 font-semibold">Over by ${Math.abs(burn.remaining).toFixed(2)}</span>}
            {burn.remaining >= 0 && <span className="text-gray-500">${Number(burn.remaining).toFixed(2)} remaining</span>}
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, Math.max(0, burn.percent_used || 0))}%` }} />
          </div>
          <div className="text-[11px] text-gray-500">
            Billed ${Number(burn.billed_fees).toFixed(2)} · WIP ${Number(burn.wip_fees).toFixed(2)}
            {burn.budget_type !== 'fees_only' && <> · Costs ${Number(burn.posted_costs).toFixed(2)}</>}
            {burn.budget_notes && <> · <span className="italic">{burn.budget_notes}</span></>}
          </div>
          {/* 12-week sparkline + projection.  Backwards-compat: only renders when
              backend returned weekly_series (extended payload from A11).  Older
              backends fall through to the progress bar only. */}
          {burn.weekly_series && burn.weekly_series.length > 0 && (
            <BudgetSparkline burn={burn} hoveredBar={hoveredBar} setHoveredBar={setHoveredBar} />
          )}
        </>
      )}
    </div>
  )
}

// 12-week historical burn + 8-week dashed projection.  Pure inline SVG, no
// chart library dep — mirrors the CaseWideTimeline / MatterStagePicker pattern.
// Colors: gray for weeks below firm avg, primary tint above; dashed 0.4 opacity
// for projection bars; red vertical line where cumulative burn crosses budget.
function BudgetSparkline ({ burn, hoveredBar, setHoveredBar }) {
  const historical = burn.weekly_series || []
  const avgWeekly4 = Number(burn.projection?.avg_weekly_burn_last_4) || 0
  const exhaustDate = burn.projection?.exhaust_date_iso

  // Build 8 forward-projection weeks locally from avg_weekly_burn_last_4 —
  // backend returns only the projection summary, not the per-week series.
  // Each projected bar carries the same incremental_spend (the 4-week avg)
  // and a synthetic week_start_iso stepped +7 days from the last historical.
  const projection = []
  if (avgWeekly4 > 0 && historical.length > 0) {
    const lastIso = historical[historical.length - 1].week_start_iso
    const baseMs = new Date(lastIso + 'T00:00:00Z').getTime()
    if (!isNaN(baseMs)) {
      for (let i = 1; i <= 8; i++) {
        const d = new Date(baseMs + i * 7 * 24 * 60 * 60 * 1000)
        projection.push({
          week_start_iso: d.toISOString().slice(0, 10),
          incremental_spend: avgWeekly4,
        })
      }
    }
  }

  const bars = [
    ...historical.map(b => ({ ...b, kind: 'historical' })),
    ...projection.map(b => ({ ...b, kind: 'projection' })),
  ]
  if (bars.length === 0) return null

  const maxAmount = Math.max(1, ...bars.map(b => Number(b.incremental_spend) || 0))
  const avgPerWeek = historical.length > 0
    ? historical.reduce((s, b) => s + (Number(b.incremental_spend) || 0), 0) / historical.length
    : 0
  const budget = Number(burn.budget_amount) || 0
  const alreadySpent = Number(burn.spent_total) || 0

  // Layout math (viewBox coordinates).
  const barW = 10
  const gap = 2
  const width = bars.length * (barW + gap)
  const height = 32
  const scale = (v) => Math.max(1, Math.round((Number(v) || 0) / maxAmount * (height - 4)))

  // Find x where cumulative running total hits budget.
  let cumulative = alreadySpent
  let crossX = null
  for (let i = 0; i < bars.length; i++) {
    const amt = Number(bars[i].incremental_spend) || 0
    const prev = cumulative
    cumulative += amt
    if (crossX === null && prev < budget && cumulative >= budget) {
      // Interpolate within this bar for a more accurate line position.
      const frac = amt === 0 ? 0 : (budget - prev) / amt
      crossX = i * (barW + gap) + frac * barW
    }
  }

  const projectionText = (() => {
    if (avgWeekly4 <= 0) return 'No recent burn — projection unavailable'
    if (exhaustDate) {
      const d = new Date(exhaustDate)
      const dStr = isNaN(d.getTime()) ? exhaustDate : format(d, 'MMM d, yyyy')
      return `At current burn (~$${avgWeekly4.toFixed(0)}/wk over last 4 weeks), budget exhausted ~${dStr}`
    }
    return `Under budget through +52 weeks at current pace (~$${avgWeekly4.toFixed(0)}/wk)`
  })()

  const formatWeekLabel = (isoStr) => {
    if (!isoStr) return ''
    const d = new Date(isoStr)
    return isNaN(d.getTime()) ? isoStr : format(d, 'MMM d, yyyy')
  }

  return (
    <div className="mt-3">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 flex items-center justify-between">
        <span>12-Week Burn + Projection</span>
        {hoveredBar && (
          <span className="text-gray-700 font-normal normal-case tracking-normal">
            Week of {formatWeekLabel(hoveredBar.week_start_iso)} · ${Number(hoveredBar.incremental_spend || 0).toFixed(2)}
            {hoveredBar.kind === 'projection' && ' (projected)'}
          </span>
        )}
      </div>
      <div className="relative w-full">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none"
             className="w-full" style={{ height: `${height}px` }}>
          {/* Baseline */}
          <line x1="0" y1={height - 1} x2={width} y2={height - 1} stroke="#e5e7eb" strokeWidth="0.5" />
          {/* Bars */}
          {bars.map((b, i) => {
            const amt = Number(b.incremental_spend) || 0
            const h = amt > 0 ? scale(amt) : 1
            const x = i * (barW + gap)
            const y = height - 1 - h
            const isProjection = b.kind === 'projection'
            const aboveAvg = amt > avgPerWeek
            const fill = isProjection
              ? (aboveAvg ? 'var(--color-primary-500, #6366f1)' : '#9ca3af')
              : (aboveAvg ? 'var(--color-primary-600, #4f46e5)' : '#9ca3af')
            return (
              <rect
                key={i}
                x={x} y={y} width={barW} height={h}
                fill={fill}
                fillOpacity={isProjection ? 0.4 : 1}
                stroke={isProjection ? fill : 'none'}
                strokeDasharray={isProjection ? '2,1' : undefined}
                strokeWidth={isProjection ? 0.5 : 0}
                onMouseEnter={() => setHoveredBar(b)}
                onMouseLeave={() => setHoveredBar(null)}
                style={{ cursor: 'pointer' }}
              >
                <title>Week of {formatWeekLabel(b.week_start_iso)} · ${amt.toFixed(2)}{isProjection ? ' (projected)' : ''}</title>
              </rect>
            )
          })}
          {/* Budget-cross vertical line */}
          {crossX !== null && (
            <line x1={crossX} y1="0" x2={crossX} y2={height}
                  stroke="#dc2626" strokeWidth="0.75" strokeDasharray="1,1" />
          )}
        </svg>
      </div>
      <div className="text-[11px] text-gray-600 mt-1.5 italic">{projectionText}</div>
    </div>
  )
}

// Matters depth #3 UI — related matters.  Renders forward + reverse
// links; admins/editors get add + remove.  Uses a small matter-picker
// combobox with filter-as-you-type + a relationship-type dropdown.
function MatterRelationshipsCard ({ matterId, canEdit }) {
  const ask = useAsk()
  const [data, setData] = useState({ forward: [], reverse: [] })
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [allMatters, setAllMatters] = useState([])
  const [pickerFilter, setPickerFilter] = useState('')
  const [pickedMatter, setPickedMatter] = useState(null)
  const [relType, setRelType] = useState('related_to')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const REL_LABEL = {
    parent: 'Parent of', child: 'Child of', sibling: 'Sibling of',
    consolidated: 'Consolidated with', appeal_of: 'Appeal of', related_to: 'Related to',
  }

  const load = async () => {
    setLoading(true)
    try {
      const r = await api.get(`/api/matters/${matterId}/relationships`)
      setData({ forward: r.data.forward || [], reverse: r.data.reverse || [] })
    } catch { /* silent */ } finally { setLoading(false) }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [matterId])

  const openAdd = async () => {
    setAdding(true); setErr('')
    if (allMatters.length === 0) {
      try {
        const r = await api.get('/api/matters', { params: { paginate: 'true', limit: 200, slim: 'true' } })
        const items = Array.isArray(r.data) ? r.data : (r.data.items || [])
        setAllMatters(items.filter(m => m.id !== matterId))
      } catch { setAllMatters([]) }
    }
  }
  const submitAdd = async () => {
    if (!pickedMatter) { setErr('Pick a matter'); return }
    setBusy(true); setErr('')
    try {
      await api.post(`/api/matters/${matterId}/relationships`, {
        related_matter_id: pickedMatter.id,
        relationship_type: relType,
        notes: notes || null,
      })
      setAdding(false); setPickedMatter(null); setNotes(''); setPickerFilter('')
      load()
    } catch (e) { setErr(e.response?.data?.error || 'Failed') } finally { setBusy(false) }
  }
  const removeLink = async (linkId) => {
    const ok = await ask({ type: 'confirm', title: 'Remove relationship?', message: 'This unlinks the two matters but leaves both matters intact.', confirmLabel: 'Remove' })
    if (!ok) return
    try { await api.delete(`/api/matters/${matterId}/relationships/${linkId}`); load() }
    catch (e) { setErr(e.response?.data?.error || 'Failed') }
  }

  const filteredPickerMatters = pickerFilter.trim()
    ? allMatters.filter(m => {
        const q = pickerFilter.toLowerCase()
        return (m.title || '').toLowerCase().includes(q)
          || (m.file_number || '').toLowerCase().includes(q)
          || (m.client_name || '').toLowerCase().includes(q)
      }).slice(0, 20)
    : allMatters.slice(0, 20)

  const forwardCount = data.forward.length, reverseCount = data.reverse.length
  const totalCount = forwardCount + reverseCount

  return (
    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-800">🔗 Related Matters {totalCount > 0 && <span className="text-xs font-normal text-gray-500">({totalCount})</span>}</h4>
        {canEdit && !adding && (
          <button onClick={openAdd} className="text-xs text-primary-700 hover:underline">+ Link a matter</button>
        )}
      </div>
      {err && <div className="text-xs text-red-700 mb-2">{err}</div>}
      {loading ? (
        <p className="text-xs text-gray-400 italic">Loading…</p>
      ) : totalCount === 0 && !adding ? (
        <EmptyState
          className="py-4"
          icon="🔗"
          title="No related matters linked"
          description="Link consolidated cases, appeals, siblings, or parent/child matters."
          action={canEdit ? {
            label: '+ Add relationship',
            onClick: openAdd
          } : undefined}
        />
      ) : !adding && (
        <div className="space-y-2 text-xs">
          {data.forward.map(r => (
            <div key={`f-${r.id}`} className="bg-white border border-gray-200 rounded p-2 flex items-center gap-2">
              <span className="bg-primary-100 text-primary-800 px-1.5 py-0.5 rounded text-[10px] font-medium">{REL_LABEL[r.relationship_type] || r.relationship_type}</span>
              <Link to={`/matters/${r.matter_id}`} className="flex-1 text-primary-700 hover:underline truncate">
                {r.title || `matter #${r.matter_id}`}{r.file_number && <span className="text-gray-400 font-mono text-[10px] ml-1">{r.file_number}</span>}
              </Link>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{r.status}</span>
              {r.notes && <span className="text-gray-400 italic truncate max-w-xs" title={r.notes}>{r.notes}</span>}
              {canEdit && <button onClick={() => removeLink(r.id)} className="text-[10px] text-red-600 hover:underline">remove</button>}
            </div>
          ))}
          {data.reverse.length > 0 && (
            <div className="pt-1 border-t border-gray-200">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Referenced by</div>
              {data.reverse.map(r => (
                <div key={`r-${r.id}`} className="bg-white border border-gray-100 rounded p-2 flex items-center gap-2 mb-1">
                  <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-[10px] font-medium">← {REL_LABEL[r.relationship_type] || r.relationship_type}</span>
                  <Link to={`/matters/${r.matter_id}`} className="flex-1 text-primary-700 hover:underline truncate">
                    {r.title || `matter #${r.matter_id}`}{r.file_number && <span className="text-gray-400 font-mono text-[10px] ml-1">{r.file_number}</span>}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {adding && (
        <div className="bg-white border border-gray-200 rounded p-3 text-xs space-y-2">
          <div>
            <label className="block text-gray-600 mb-1">Search for a matter to link</label>
            <input value={pickerFilter} onChange={e => { setPickerFilter(e.target.value); setPickedMatter(null) }}
              placeholder="Title, file number, or client name"
              className="w-full border border-gray-300 rounded px-2 py-1" />
            {!pickedMatter && (
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded mt-1 bg-white">
                {filteredPickerMatters.length === 0 ? (
                  <p className="p-2 text-gray-400 italic">No matches</p>
                ) : filteredPickerMatters.map(m => (
                  <button key={m.id} type="button"
                    onClick={() => setPickedMatter(m)}
                    className="w-full text-left p-1.5 hover:bg-primary-50 border-b border-gray-100 last:border-b-0">
                    <span className="text-gray-800">{m.title}</span>
                    {m.file_number && <span className="text-gray-400 font-mono ml-2">{m.file_number}</span>}
                    {m.client_name && <span className="text-gray-500 ml-2">· {m.client_name}</span>}
                  </button>
                ))}
              </div>
            )}
            {pickedMatter && (
              <div className="mt-1 p-2 bg-primary-50 rounded flex items-center gap-2">
                <span className="flex-1">✓ {pickedMatter.title}</span>
                <button type="button" onClick={() => setPickedMatter(null)} className="text-primary-700 hover:underline">change</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label>Relationship type
              <select value={relType} onChange={e => setRelType(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1">
                {Object.entries(REL_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label>Notes (optional)
              <input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Consolidated 2026-03-15 per court order"
                className="w-full border border-gray-300 rounded px-2 py-1" />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => { setAdding(false); setErr(''); setPickedMatter(null) }}
              className="text-gray-600">Cancel</button>
            <button type="button" onClick={submitAdd} disabled={busy || !pickedMatter}
              className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded disabled:opacity-50">
              {busy ? 'Linking…' : 'Add link'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Matters depth #4 UI — stage picker for the client-facing timeline.
// Admins + editors set the current stage; the change writes to
// matter_stage_history + audit_log.  Every stage change is visible to
// the client on the portal timeline.
function MatterStageCard ({ matterId, matterStatus, canEdit }) {
  const ask = useAsk()
  const { user } = useAuth()
  const isAdminUser = user?.role === 'admin' || user?.permissions?.is_admin
  const [stages, setStages] = useState([])
  const [current, setCurrent] = useState('')
  const [history, setHistory] = useState([])
  const [notes, setNotes] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const load = async () => {
    try {
      const [stagesRes, historyRes] = await Promise.all([
        api.get('/api/matters/stage-definitions'),
        api.get(`/api/matters/${matterId}/stage-history`),
      ])
      setStages(stagesRes.data.stages || [])
      setHistory(historyRes.data.history || [])
      const latest = (historyRes.data.history || []).slice(-1)[0]
      setCurrent(latest?.stage_key || '')
    } catch { /* silent */ }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [matterId])

  const setStage = async (stageKey) => {
    if (stageKey === current) return
    if (!stageKey) return
    setBusy(true); setErr('')
    try {
      await api.put(`/api/matters/${matterId}/stage`, { stage_key: stageKey, notes: notes || null })
      setNotes('')
      load()
    } catch (e) { setErr(e.response?.data?.error || 'Failed to change stage') } finally { setBusy(false) }
  }
  const clearStage = async () => {
    const ok = await ask({ type: 'confirm', title: 'Clear stage assignment?', message: 'The client-facing timeline will hide until a new stage is picked.', confirmLabel: 'Clear' })
    if (!ok) return
    setBusy(true); setErr('')
    try { await api.put(`/api/matters/${matterId}/stage`, { stage_key: null }); load() }
    catch (e) { setErr(e.response?.data?.error || 'Failed') } finally { setBusy(false) }
  }

  const currentDef = stages.find(s => s.key === current)
  const currentIdx = stages.findIndex(s => s.key === current)

  return (
    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-800">
          🎯 Client Timeline Stage
          {current && <span className="ml-2 text-xs font-normal text-gray-500">visible to client on the portal</span>}
        </h4>
        {history.length > 0 && (
          <button onClick={() => setShowHistory(s => !s)} className="text-xs text-primary-700 hover:underline">
            {showHistory ? 'Hide' : 'Show'} history ({history.length})
          </button>
        )}
      </div>
      {err && <div className="text-xs text-red-700 mb-2">{err}</div>}
      {stages.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          No firm stages defined yet. {isAdminUser
            ? <>Configure in <Link to="/settings#matter-stages" className="text-primary-700 hover:underline">Settings → Matter Stages</Link>.</>
            : <>Ask an admin to configure stages in Settings → Matter Stages.</>}
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-3 text-xs flex-wrap">
            <span className="text-gray-600">Current stage:</span>
            {canEdit ? (
              <select value={current} onChange={e => setStage(e.target.value)} disabled={busy}
                className="border border-gray-300 rounded px-2 py-1 text-sm">
                <option value="">— none —</option>
                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            ) : (
              <span className="font-medium text-primary-800">{currentDef?.label || '—'}</span>
            )}
            {current && canEdit && (
              <button onClick={clearStage} disabled={busy} className="text-[10px] text-red-600 hover:underline">Clear</button>
            )}
            {currentDef?.description && (
              <span className="text-gray-500 italic text-[11px]">{currentDef.description}</span>
            )}
          </div>
          {canEdit && current && (
            <div className="flex items-center gap-2">
              <input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Notes on next stage change (optional, e.g. 'discovery cut-off passed')"
                className="flex-1 text-xs border border-gray-300 rounded px-2 py-1" />
              <span className="text-[10px] text-gray-400">applied on next selection</span>
            </div>
          )}
          {/* Compact horizontal progress indicator */}
          {stages.length > 0 && current && (
            <div className="flex items-center gap-0.5 pt-1">
              {stages.map((s, i) => {
                const isPast = i < currentIdx
                const isNow = s.key === current
                const cls = isNow ? 'bg-primary-600' : isPast ? 'bg-emerald-500' : 'bg-gray-300'
                return (
                  <div key={s.key} className={`flex-1 h-1.5 first:rounded-l last:rounded-r ${cls}`}
                    title={`${s.label}${isNow ? ' (current)' : isPast ? ' (past)' : ''}`} />
                )
              })}
            </div>
          )}
        </div>
      )}
      {showHistory && history.length > 0 && (
        <ol className="mt-3 space-y-1 border-l-2 border-gray-200 ml-2 pl-3 text-xs">
          {history.map(h => (
            <li key={h.id} className="relative">
              <span className="absolute -left-[15px] top-1.5 w-2 h-2 rounded-full bg-gray-400" />
              <span className="font-medium text-gray-800">{h.stage_label || h.stage_key}</span>
              <span className="text-gray-400 ml-2">{h.entered_at ? new Date(h.entered_at).toLocaleDateString() : ''}</span>
              {h.entered_by_name && <span className="text-gray-500 ml-1">· {h.entered_by_name}</span>}
              {h.notes && <div className="text-gray-500 italic text-[11px]">{h.notes}</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// MatterDesignatingPartyCard — pre-code panel HIGH #3 (3/3 convergence).
// Constrained-vocabulary role dropdown + optional party name; composed
// on save into matter.designating_party (single VARCHAR(200) column).
// Falls back to firm_settings.default_designating_party when the matter's
// value is blank — the Expert Designation PDF reads matter.designating_party
// only, so composed value is authoritative.
// ═══════════════════════════════════════════════════════════════════════
const DESIGNATING_PARTY_ROLES = [
  { value: '',                    label: '— pick a role —' },
  { value: 'Plaintiff',           label: 'Plaintiff' },
  { value: 'Plaintiffs',          label: 'Plaintiffs' },
  { value: 'Defendant',           label: 'Defendant' },
  { value: 'Defendants',          label: 'Defendants' },
  { value: 'Cross-Complainant',   label: 'Cross-Complainant' },
  { value: 'Cross-Complainants',  label: 'Cross-Complainants' },
  { value: 'Cross-Defendant',     label: 'Cross-Defendant' },
  { value: 'Cross-Defendants',    label: 'Cross-Defendants' },
  { value: 'Petitioner',          label: 'Petitioner' },
  { value: 'Petitioners',         label: 'Petitioners' },
  { value: 'Respondent',          label: 'Respondent' },
  { value: 'Respondents',         label: 'Respondents' },
  { value: 'Intervenor',          label: 'Intervenor' },
  { value: 'Intervenors',         label: 'Intervenors' },
  { value: 'Other',               label: 'Other (specify in party name)' },
]

// Best-effort parse of a persisted composed string back into role + name.
// Sort roles by length DESC so 'Cross-Complainants' matches before
// 'Cross-Complainant' etc.  Returns {role, name} where role is '' when
// no known role matches.
function parseDesignatingParty (val) {
  if (val == null) return { role: '', name: '' }
  const s = String(val).trim()
  if (!s) return { role: '', name: '' }
  const roles = DESIGNATING_PARTY_ROLES
    .filter(r => r.value)
    .map(r => r.value)
    .sort((a, b) => b.length - a.length)
  for (const r of roles) {
    if (s === r) return { role: r, name: '' }
    if (s.toLowerCase().startsWith(r.toLowerCase() + ' ')) {
      return { role: r, name: s.slice(r.length + 1).trim() }
    }
  }
  // No known role prefix — treat as free-form Other with name = whole string.
  return { role: 'Other', name: s }
}
function composeDesignatingParty (role, name) {
  const r = (role || '').trim()
  const n = (name || '').trim()
  if (!r) return null
  if (!n) return r
  return `${r} ${n}`
}

function MatterDesignatingPartyCard ({ matterId, matter, firmSettings, canEdit, onSaved }) {
  const initialParsed = parseDesignatingParty(matter?.designating_party)
  const [editing, setEditing] = useState(false)
  const [role, setRole] = useState(initialParsed.role)
  const [name, setName] = useState(initialParsed.name)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  // Reset on matter change (matter refetch after another save).
  useEffect(() => {
    const p = parseDesignatingParty(matter?.designating_party)
    setRole(p.role); setName(p.name)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matter?.designating_party])

  const displayed = matter?.designating_party
  const firmDefault = firmSettings?.default_designating_party || null

  const save = async () => {
    setSaving(true); setErr('')
    try {
      const composed = composeDesignatingParty(role, name)
      // Enforce panel HIGH #3 cap on the free-text piece — server also
      // caps at 200 total via VARCHAR(200), but a friendly client-side
      // cap keeps the field from truncating silently.
      if (composed && composed.length > 200) {
        setErr('Designating-party string exceeds 200 characters.  Shorten the party name.')
        setSaving(false); return
      }
      await api.put(`/api/matters/${matterId}`, {
        designating_party: composed,
      })
      setEditing(false)
      if (typeof onSaved === 'function') await onSaved()
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not save designating party')
    } finally {
      setSaving(false)
    }
  }

  const clear = async () => {
    setSaving(true); setErr('')
    try {
      await api.put(`/api/matters/${matterId}`, { designating_party: null })
      setEditing(false); setRole(''); setName('')
      if (typeof onSaved === 'function') await onSaved()
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not clear designating party')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-4 p-3 border border-purple-200 bg-purple-50/40 rounded-lg">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-purple-800">
          ⚖ Designating Party <span className="text-gray-500 font-normal">(§2034.260 PDF caption)</span>
        </div>
        {canEdit && !editing && (
          <button type="button" onClick={() => setEditing(true)}
            className="text-xs text-purple-700 hover:underline">
            {displayed ? 'Edit' : 'Set'}
          </button>
        )}
      </div>
      {err && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">
          {err}
        </div>
      )}
      {!editing ? (
        <div className="text-sm text-gray-800">
          {displayed
            ? <span className="font-medium">{displayed}</span>
            : (firmDefault
                ? <span className="text-gray-500 italic">
                    Using firm default: <span className="font-medium text-gray-700">{firmDefault}</span>
                  </span>
                : <span className="text-gray-400 italic">Not set — the Expert Designation PDF will render a placeholder.</span>
              )
          }
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Party role</label>
            <select value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
              {DESIGNATING_PARTY_ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Party name <span className="text-gray-400">(optional)</span>
            </label>
            <input type="text" value={name}
              onChange={e => setName(e.target.value)}
              maxLength={150}
              placeholder={firmDefault
                ? `Firm default: ${firmDefault}`
                : 'e.g. ACME Inc., XYZ Corp'}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
            <p className="text-[11px] text-gray-500 mt-1">
              Appended to the role in the PDF caption.  Leave blank for a bare role like "Defendants".
            </p>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" onClick={save} disabled={saving || !role}
              className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => {
              const p = parseDesignatingParty(matter?.designating_party)
              setRole(p.role); setName(p.name); setEditing(false); setErr('')
            }} className="px-3 py-1 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">
              Cancel
            </button>
            {displayed && (
              <button type="button" onClick={clear} disabled={saving}
                className="px-3 py-1 text-xs border border-red-300 text-red-700 bg-white rounded hover:bg-red-50 disabled:opacity-50 ml-auto">
                Clear (use firm default)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}